// @ts-strict-ignore
import React, {createRef, PureComponent} from 'react';
import copy from 'clipboard-copy';
import omit from 'lodash/omit';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import pick from 'lodash/pick';
import isEmpty from 'lodash/isEmpty';
import queryString from 'query-string';
import debounce from 'lodash/debounce';
import {Location, NavigateFunction, useLocation} from 'react-router-dom';
import cloneDeep from 'lodash/cloneDeep';
import produce from 'immer';
import moment from 'moment';

import {spans} from 'common/util/tracing';
import {generateShortcutLabel, getCommandKey} from 'common/util/keyboard';
import {BROWSE_URL_PARAM} from 'common/report/util/search';
import {dashboardsClient, DashboardsClient} from 'common/report/queries/app-api/dashboards-client';
import {BoardMoveSetting} from 'common/report/queries/app-api/project-metadata-client';
import {USE_THIS_BOARD_IDENTIFIER} from 'common/constants';
import {GridLayout} from 'apps/boards/src/dashboards-app/components/grid-layout';
import {ERROR_MESSAGES, TV_MODE_RELOAD_TIME_IN_MS} from 'apps/boards/src/models/constants';
import {AppMode, NetworkError} from 'apps/boards/src/models/enums';
import {StagedDashboard} from 'common/report/dashboards/staged-dashboard';
import {
  BadLayoutRequestErrors,
  BadLayoutRequestMessages,
  BenchmarkCard,
  BlueprintConfig,
  Bookmark,
  CardContent,
  CardOnDashboard,
  ContentAction,
  ContentActionType,
  ContentType,
  Dashboard,
  DashboardCard,
  DashboardQueryOrigin,
  DashboardType,
  DisplayBookmark,
  MediaCard,
  MediaContent,
  NestedDashboardCard,
  ReportContent,
  ReportLinkContent,
  SeenCards,
  TemplateSpecsTranslationKey,
  TemplateType,
  TextCard,
  TimeFilter,
} from 'common/report/dashboards/types';
import {getBookmarkParamsWithOverrides} from 'common/report/dashboards/bookmark-overrides';
import {
  getBoardFilterGroupPropTrackingProps,
  getBookmarkTrackingParams,
  getBookmarkUrlPath,
  getCardTrackingParams,
  getChartOptions,
} from 'apps/boards/src/report-util';
import {
  createCardResults,
  DashboardCardResult,
  getCardResultIfInvalid,
  queryCanonicalBookmark,
} from 'apps/boards/src/report-util/query';
import {
  CONTENT_ACTION_TRACKING_NAME_MAP,
  DashboardCRUDSource,
  DashboardMoveSource,
  getCardTypes,
  getCommonDashboardTrackingProps,
  getTextCardStyles,
  GridCalcTrackingPropsParams,
  GridLayoutTrackEventDetail,
  TrackingEvent,
} from 'common/report/dashboards/tracking';
import {DashboardsContext} from 'apps/boards/src/util/context';
import {
  BrowseUrlData,
  DashboardUrlData,
  generateStagedCardId,
  isBrowseUrlData,
  isStagedCardId,
  parseDashboardUrlHashParams,
  serializeDashboardUrlHashParams,
  STAGED_CARD_PREFIX,
} from 'common/report/dashboards/url';
import {ViewportSeenWatcher} from 'apps/boards/src/util/viewport-seen-watcher';
import {confirm} from 'common/components/confirm';
import {THEME as ConfirmTheme} from 'common/components/confirm/types';
import {
  DashboardModelController,
  setCardsFromDashboardLayout,
} from 'common/report/controllers/dashboard-model-controller';
import {DashboardSelectorController} from 'common/report/controllers/dashboard-selector-controller';
import {reportManagerFromType} from 'common/report/report-manager/util';
import {EntityType} from 'common/report/util/saved-entities/types';
import {
  buildQueryReportHeaderParams,
  getEditCapability,
  isAllowedSuperadminOverride,
  maybeAddGlobalAccessTypeParam,
  normalizeDashboardEntity,
} from 'common/report/util/saved-entities';
import {userDetailsStore} from 'common/report/stores/user-details';
import {AsyncCountingSemaphore} from 'common/util/async-counting-semaphore';
import {AsyncCountingSemaphorePriorityQueue} from 'common/util/async-counting-semaphore-priority-queue';
import {
  RecentSearchItemType,
  SearchDisplayContext,
  SearchDisplayContextIcons,
  SearchSortOption,
} from 'common/report/util/search/types';
import {searchUtils, transformBookmarkIntoRecentItem} from 'common/report/util/search/index';
import {isDataClassificationError} from 'common/report/queries/utils';
import {ToastType} from 'common/components/toast/types';
import {getProjectId, getReportUrl, isEmbedMode} from 'common/report/util';
import {DashboardCardId} from 'common/report/util/saved-entities/types';
import {
  FeatureGate,
  LocalStorageKey,
  Permission,
  ProjectFeatureFlag,
  ReportName,
  RestrictedReport,
  UserFlag,
} from 'common/report/constants';
import {formatDate, getCalendarDate} from 'common/util/date';
import {reporter} from 'common/report/error-reporting';
import {GlobalAccessType} from 'common/widgets/create-entity-modal/types';
import {promptManageDashboardModal} from 'common/widgets/create-entity-modal';
import {dashboardUtils, getCardIdFromContent, isNestedDashboardsEnabled} from 'common/report/util/dashboards/utils';
import {
  getLayoutParamWithContentRemoved,
  getLayoutUpdateParamsForHistoryAction,
  GridLayoutUpdateParams,
} from 'common/report/util/dashboards/grid-layout';
import {
  HeaderAction,
  QueryReportHeaderParams,
  QueryReportHeaderSave,
  UntitledReportNameKey,
} from 'common/query-builder/query-report-header/types';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {ApiResponseError, StatusCode} from 'common/util/shared-client';
import {diffFromNow, getNow, PERFORMANCE_EVENT_PREFIX, WEB_DASH_PREFIX} from 'common/util/timing';
import {openDemoProjectSelectorModal} from 'common/widgets/demo-selector-modal';
import {cancelWorkflow, captureWorkflowTiming} from 'common/util/workflow-timing';
import {emitInfraTrackEvent, sleep} from 'common/util/dom';
import {initialCardsLoaded} from 'apps/boards/src/util/workflow';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {DashboardBookmarkOverrides, DashboardOrigin, getDashboardParams} from 'common/report/util/bookmark-overrides';
import {DeleteEvent, SaveEvent, SaveEventDetails, UpdateEvent} from 'common/report-editor';
import {BlueprintsRequest} from 'app_api/projects/dashboards/types';
import {ReportManager} from 'common/report/report-manager';
import {addToast} from 'common/components/toast-area/toast-manager';
import {BaseQueryErrorType, BaseQueryResultsType} from 'common/report/types';
import {BookmarkType, bulkUpdateBookmarks} from 'common/report/queries/app-api/bookmark-client';
import {parseUri} from 'common/util';
import {CurrentProject, CurrentProjectResource} from 'common/report/model-resources/projects';
import {getSpaNavCount} from 'common/report/util/spa-nav-manager';
import {findKeyByValue} from 'common/util/typescript';
import {Segmentation} from 'common/types/reports/query';
import {BoardFilter} from 'apps/boards/src/dashboards-app/components/global-filter-bar/types';
import {
  Events as BoardFilterEvents,
  track as trackBoardFilters,
} from 'apps/boards/src/dashboards-app/components/global-filter-bar/tracking';
import {CardBackgroundRepollState} from 'apps/boards/src/dashboards-app/types';
import {
  confirmDeleteCard,
  createStagedBookmark,
  createStagedCard,
  getActiveBoardFilters,
  getBenchmarkCardParams,
  getBoardFiltersFromPersisted,
  getCardQuerySpanId,
  getDashboardCardParams,
  GetDashboardCardParamsOptions,
  getMaxCardsToLoadImmediately,
  getMaxConcurrentBackgroundPoolQueries,
  getMaxConcurrentQueries,
  getMediaCardParams,
  getNestedDashboardCardParams,
  getTextCardParams,
  GetTextCardParamsOptions,
  isBenchmarkCard,
  isEqualToSavedBreakdowns,
  isEqualToSavedFilters,
  isTextOrMediaCard,
  NULL_BOOKMARK,
  waitForNecessaryResources,
} from 'apps/boards/src/dashboards-app/util';
import {SparkCardProps} from 'apps/boards/src/dashboards-app/spark-card';
import {CardJsxProps} from 'apps/boards/src/dashboards-app/card';
import {UserMediaToDashboardMediaMap} from 'apps/boards/src/dashboards-app/card/media/types';
import {Directions} from 'common/components/drop-menu/types';
import {getBoardsDashboardSectionItem, getProjectSelectorSectionItem} from 'common/report/util/boards/presets';
import {OpenInNewTab, useOpenInNewTab} from 'common/contexts/react/open-in-new-tab-context';
import type {ChromeHeader} from 'common/widgets/chrome/chrome-header';
import {beforeSavingBookmark} from 'common/report/report-manager/query-lifecycles';
import {incrementUserFlag} from 'common/report/queries/app-api/user-flags';
import {trackHotjarEvent} from 'common/util/hotjar';
import {RHSInputType} from 'common/widgets/rhs-panel/types';
import {exportDashboardModalPromise} from 'common/query-builder/query-report-header/export-dashboard-modal';
import {ExportDashboardType} from 'common/query-builder/query-report-header/export-dashboard-modal/types';
import {DigestTrackingEvent, track} from 'common/query-builder/query-report-header/subscribe-to-boards-menu/tracking';
import {DisplayOptions, isInsightsBookmarkParams} from 'common/report/insights/util/types';
import {InsightsBookmarkParams} from 'common/types/reports/bookmark';
import {includesSavedBehaviorId} from 'common/report/insights/models/utils';
import {getRouteToCurrentDashboard} from 'common/report/util/boards';
import {
  QueryReportHeaderActionItems,
  QueryReportHeaderTitle,
} from 'apps/boards/src/dashboards-app/standard-mode/query-report-header-mixins';
import {getDashboardDescendants, getDashboardHierarchyTreeHeight} from 'common/widgets/dashboard-sidenav/util';
import {SidenavSectionIndex, SidenavSectionTrackingName} from 'common/widgets/dashboard-sidenav/constants';
import {DashboardItemInfo, DashboardSidenavContextValue} from 'common/widgets/dashboard-sidenav/types';
import {useDashboardSidenavContext} from 'common/widgets/dashboard-sidenav/context';
import {BROWSE_MODE_KEY} from 'common/widgets/browse/types';
import type {LocalizationStore} from 'common/i18n/store';
import {useTranslation} from 'common/i18n/hooks';
import {useReportEditorActions} from 'common/contexts/react/report-editor-actions-context';
import {useBookmark} from 'common/contexts/react/bookmark-context';
import {useLocalStorageManager} from 'common/contexts/react/storage-manager-context';
import {useHistoryManager} from 'common/contexts/react/history-manager-context';
import {useGlobalSearchController} from 'common/contexts/react/global-search-controller-context';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {useBehaviors} from 'common/contexts/react/behaviors-context';
import {useMetrics} from 'common/contexts/react/metrics-resource-context';
import {useNavigateWithSearchParams} from 'common/contexts/react/react-router';
import {IronContextRegistry} from 'common/report/context-registry';
import {useDashboardSelectorController} from 'common/contexts/react/dashboard-selector-controller-context';
import {isDashboardCard} from 'common/report/dashboards/util';
import {BackToBoardEvent, BackToBoardSource} from 'common/report-editor/types';
import {getLocation} from 'common/util/url';
import {useReportLayoutManager} from 'common/contexts/react/report-layout-manager-context';
import {useThemes} from 'common/contexts/react/themes-context';
import {mergeLegendOverrides} from 'common/charts/contexts/utils';
import {useQueryContextManager} from 'common/contexts/react/query-context-manager';
import {fetchDisplayNameData} from 'common/report/display-name-registry';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {syncReportPropertyValueMappings} from 'common/report/util/mappings';
import {isScdPropertyName} from 'common/report/util/properties';
import {getWorkspacesForProject, isGlobalSidenavEnabled} from 'iron/routes/global-sidenav/utils';
import {useProjects} from 'common/contexts/react/projects-context';
import {useSpaNavManager} from 'common/contexts/react/spa-nav-manager';

import {CustomHeaderAction, DefaultRoutePath} from './types';
import './index.styl';
import {template} from './template';
import {swapBlueprintVariable, swapFeatureLaunchDate, updateBlueprintCohorts} from './blueprint-boards';

// The following background polling scheme works out to 8s, 16s, 32s, 64s for a total of ~120s
const BASE_RETRY_BACKOFF_MS = 8 * 1000;
const EXPONENTIAL_BACKOFF_FACTOR = 2;
const MAX_BACKGROUND_POLL_RETRIES = 4;

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 60 * SECONDS_PER_MINUTE;

const B2B_DEMO_PROJECT_ID = 685944;
const MOVED_BOARD_STORAGE_KEY = `last_move`;

type FocusedCard =
  | {
      bookmarkId: number;
      isNew?: boolean;
    }
  | {
      cardId: DashboardCardId;
      isNew?: boolean;
    };

interface FoundationalTemplatesStoredRecord {
  viewedCount: number; // Number of times the user viewed a template generated board
  lastInteraction: string; // Date string from the last time the user interacted with the foundational tempaltes feedback toast
  feedbackRequestCount: number; // Number of times we've requested the user for feedback of foundational templates
}

interface MovedBoardInfo {
  sourceProjectId: number;
  sourceDashboardId: number;
  undoable: boolean;
}

interface State {
  $fragment?: string;
  dashboard: null | Dashboard;
  dashboardFailed: boolean;
  dashboardLoaded: boolean;
  dashboardModified: boolean;
  hasNoDashboards: boolean;
  cards: Map<DashboardCardId, CardOnDashboard>;
  isSavingEditorBookmark: boolean;
  seenCards: SeenCards;
  cardBackgroundRepollState: Map<DashboardCardId, CardBackgroundRepollState>;
  foregroundCardResults: Map<DashboardCardId, DashboardCardResult>;
  // QueryResults are used for fast-edit as the ResultsPromise in the ReportEditor.
  foregroundQueryResults: Map<DashboardCardId, Promise<BaseQueryResultsType | BaseQueryErrorType>>;
  filters: BoardFilter[] | null;
  breakdowns: Segmentation[] | null;
  timeFilter: TimeFilter | null;
  cardOrder: DashboardCardId[];
  globalInfoLoaded: boolean;
  savedReportsLoaded: boolean;
  tvModeEnabled: boolean;
  lastRefreshed: Date;
  routedDashboardId: number;
  isFavoriteRequestLoading: boolean;
  searchSortOption?: SearchSortOption;
  focusedCard: FocusedCard;
  wasCardFocused: boolean;
  openCreateFoundationalTemplateModal: boolean;
  isShowingFoundationalTemplateToast: boolean;
  templateModalLoading: boolean;
  isEmbedMode: boolean;

  isHistoryActionInProgress?: boolean;
  reportEditorSession: number;
  editorCardId?: DashboardCardId;
  editorCardEditedBookmark?: DisplayBookmark;
  editorCardBookmarkURLSlug?: string;
  editorCardAiCellId?: string;
  editedBookmarkLongUrlCount: number;
  revealedBannerIdentifiers: Set<string>;
  isFasteditPageload?: boolean;
  gridLayoutUpdateParams: GridLayoutUpdateParams;
  browseParams?: BrowseUrlData;
  showTemplateRHS?: boolean;

  blueprintConfig?: BlueprintConfig;
  blueprintDashboard?: Dashboard;
  isCommittingBlueprint?: boolean;
  wasBlueprintUpdated?: boolean;
  isSeeAllTemplatesLoading?: boolean;
  showRHSReminderTooltip?: boolean;
}

type Props = {
  elref: string;
  context: DashboardsContext;
  onToast: (ev: CustomEvent) => void;
};

class StandardModeClass extends PureComponent<
  Props &
    Pick<
      IronContextRegistry,
      | `themes`
      | `currentProject`
      | `globalSearchController`
      | `historyManager`
      | `localStorageManager`
      | `reportEditorActions`
      | `bookmarkStore`
      | `metrics`
      | `behaviors`
      | `i18n`
      | `reportLayoutManager`
      | `queryContextManager`
      | `projects`
      | `spaNavManager`
    > & {
      selectorController: DashboardSelectorController;
      location: Location;
      openInNewTab: OpenInNewTab;
      navigate: NavigateFunction;
      sidenavContext: DashboardSidenavContextValue;
    },
  State
> {
  containerRef = createRef<HTMLDivElement>();
  declare cardSeenWatcher: ViewportSeenWatcher<DashboardCardId>;

  private disconnectQueue: Array<() => void> = [];
  private declare selectorListener;
  private declare dashboardListener;
  // make it public for testcases
  public declare dashboardController: DashboardModelController<DashboardsClient>;
  private declare queryApiForegroundPool: AsyncCountingSemaphorePriorityQueue;
  private declare queryApiBackgroundPool: AsyncCountingSemaphorePriorityQueue;
  private declare foregroundQueryAbortControllers: Map<DashboardCardId, AbortController>;
  private declare backgroundQueryAbortControllers: Map<DashboardCardId, AbortController>; // Used to abort an ongoing background query.
  private declare backgroundRepollAbortControllers: Map<DashboardCardId, AbortController>; // Used to abort an active repoll loop.
  private declare backgroundCardResults: Map<DashboardCardId, DashboardCardResult>;
  private declare backgroundQueryResults: Map<DashboardCardId, Promise<BaseQueryResultsType | BaseQueryErrorType>>;
  private declare cardsAutoLoadedCounter: AsyncCountingSemaphore;
  private declare foregroundLoadCardCounter: number;

  private declare gridEl: GridLayout;
  private declare globalInfoLoadPromise: null | Promise<any>;
  private declare savedReportsLoadPromise: null | Promise<any>;

  private declare recentlyViewedDashboardsStorage: UserLocalStorageHelper<DashboardItemInfo[]>;
  private declare seenPinnedDashboardIdsStorage: UserLocalStorageHelper<number[]>;
  private declare movedBoardInfoStorage: UserLocalStorageHelper<MovedBoardInfo>;

  private reloadTimer = null;

  private invokedFirstRoute = false;
  private mounted = false;
  private perfTracking = new Map<
    number, // Dashboard ID
    {
      firstLoad: boolean;
      firstLoadTime: number;
      allLoaded: boolean;
      fetchedCards: Set<DashboardCardId>;
    }
  >();

  state: State = {
    reportEditorSession: 1,
    dashboard: null,
    dashboardFailed: false,
    dashboardLoaded: false,
    dashboardModified: false,
    hasNoDashboards: false,
    cards: new Map<number, CardOnDashboard>(),
    isSavingEditorBookmark: false,
    seenCards: {},
    cardBackgroundRepollState: new Map<DashboardCardId, CardBackgroundRepollState>(),
    foregroundCardResults: new Map<DashboardCardId, DashboardCardResult>(),
    foregroundQueryResults: new Map<DashboardCardId, Promise<BaseQueryResultsType | BaseQueryErrorType>>(),
    filters: [],
    breakdowns: [],
    timeFilter: null,
    cardOrder: [],
    globalInfoLoaded: false,
    savedReportsLoaded: false,
    tvModeEnabled: false,
    lastRefreshed: new Date(),
    routedDashboardId: null,
    isFavoriteRequestLoading: false,
    searchSortOption: null,
    focusedCard: null,
    wasCardFocused: false,
    openCreateFoundationalTemplateModal: false,
    isShowingFoundationalTemplateToast: false,
    templateModalLoading: false,
    gridLayoutUpdateParams: null,
    revealedBannerIdentifiers: new Set(),
    isHistoryActionInProgress: false,
    isEmbedMode: false,
    editedBookmarkLongUrlCount: 0,
    showTemplateRHS: false,
    blueprintConfig: null,
    blueprintDashboard: null,
    isCommittingBlueprint: false,
    wasBlueprintUpdated: true,
    isSeeAllTemplatesLoading: false,
    showRHSReminderTooltip: false,
  };

  componentDidMount() {
    this.mounted = true;
    // initialize anything used in route handlers before super.connectedCallback()
    // because route handlers are called during super.connectedCallback()
    if (!this.props.context) {
      throw new Error(`"context" is a required component prop`);
    }
    if (!this.props.selectorController) {
      throw new Error(`"selectorController" is a required component prop`);
    }
    this.props.selectorController.resetActiveDashboard();

    this.props.reportLayoutManager.updateWhiteBackBackground(true);

    this.props.context.initializeTracking();

    const {queryKey} = parseUri(getLocation().href);

    const shouldShowFoundationalTemplateModal = queryKey[`show-template-selector`] === `true`;
    const shouldShowDemoDatasetSelectModal = queryKey[`show-demo-dataset-modal`] === `true`;

    if (shouldShowFoundationalTemplateModal === true) {
      const queryParams = queryString.parse(this.props.location.search);
      this.handleOpenFoundationalTemplateModalClicked({entryPoint: queryParams[`source`]}); // Entry point may be different from onboarding checklist
    }

    if (shouldShowDemoDatasetSelectModal === true) {
      // forces it to default to B2B demo instead of first available demo project
      this.props.navigate(`#id=${B2B_DEMO_PROJECT_ID}`);
      openDemoProjectSelectorModal((ev) => {
        addToast({message: ev.detail.message, type: ToastType.ERROR});
      });
    }

    this.selectorListener = ({activeDashboardController = null} = {}) => {
      if (activeDashboardController) {
        this.handleDashboardControllerChanged(activeDashboardController);
      }
    };
    this.props.selectorController.subscribeUpdates(this.selectorListener);
    this.props.selectorController.loadDashboards();

    // this is to handle component being mount and un-mount in strict-mode
    const urlHashParams = this.props.location.hash.slice(1);
    const {parsedData} = parseDashboardUrlHashParams(urlHashParams);
    if (!isBrowseUrlData(parsedData) && Number.isInteger(parsedData.dashboardId)) {
      this.setState(
        {
          dashboardFailed: false,
          dashboardLoaded: false,
          routedDashboardId: parsedData.dashboardId,
        },
        () => {
          this.props.selectorController.changeActiveDashboard(parsedData.dashboardId); // async, no need to wait
        },
      );
    }

    this.queryApiForegroundPool =
      this.queryApiForegroundPool ?? new AsyncCountingSemaphorePriorityQueue(getMaxConcurrentQueries(getProjectId()));
    this.queryApiBackgroundPool =
      this.queryApiBackgroundPool ??
      new AsyncCountingSemaphorePriorityQueue(getMaxConcurrentBackgroundPoolQueries(getProjectId()));
    this.cardsAutoLoadedCounter =
      this.cardsAutoLoadedCounter ?? new AsyncCountingSemaphore(getMaxCardsToLoadImmediately(getProjectId()));
    this.foregroundQueryAbortControllers = new Map<DashboardCardId, AbortController>();
    this.backgroundQueryAbortControllers = new Map<DashboardCardId, AbortController>();
    this.backgroundRepollAbortControllers = new Map<DashboardCardId, AbortController>();
    this.backgroundCardResults = this.backgroundCardResults ?? new Map<DashboardCardId, DashboardCardResult>();
    this.backgroundQueryResults =
      this.backgroundQueryResults ?? new Map<DashboardCardId, Promise<BaseQueryResultsType | BaseQueryErrorType>>();
    this.cardSeenWatcher = this.cardSeenWatcher ?? new ViewportSeenWatcher(this.handleCardSeen);
    this.loadGlobalInfoOnce(); // async, no need to wait
    this.trackViewedReport();

    this.recentlyViewedDashboardsStorage = new UserLocalStorageHelper({
      namespace: LocalStorageKey.RecentlyViewedDashboards,
      context: this.props.localStorageManager,
    });

    this.seenPinnedDashboardIdsStorage = new UserLocalStorageHelper({
      namespace: LocalStorageKey.SeenPinnedDashboardIds,
      context: this.props.localStorageManager,
    });

    this.movedBoardInfoStorage = new UserLocalStorageHelper({
      namespace: LocalStorageKey.CrossProject,
      context: this.props.localStorageManager,
    });

    if (this.state.tvModeEnabled) {
      this.reloadTimer = setTimeout(
        function () {
          window.location.reload();
        },
        24 * 60 * 60 * 1000,
      ); // 1 day or 86,400,000 ms
    }

    this.state.focusedCard && this.highlightAndScrollToFocusedCard();

    if (!this.state.editorCardBookmarkURLSlug && !this.state.editorCardId) {
      this.setupUndoRedo();
    }

    this.props.currentProject.subscribe(this.resourceCustomSubscriptions.get(CurrentProjectResource));

    document.addEventListener(`copyDemoToProject`, this.handleCopyDemoToProject);
    this.foregroundLoadCardCounter = 0;
  }

  componentWillUnmount() {
    this.mounted = false;
    this.disconnectQueue.forEach((disconnectAction) => disconnectAction());

    // Abort all pending background repoll loops.
    for (const abortController of this.backgroundRepollAbortControllers.values()) {
      abortController.abort(new DOMException(`unmounting`, `AbortError`));
    }

    // @ts-ignore
    this.dashboardController?.unsubscribeUpdates(this.dashboardListener);
    window.onbeforeunload = null;
    if (this.reloadTimer) {
      clearTimeout(this.reloadTimer);
    }

    this.historyManager.teardown();
    this.props.currentProject.unsubscribe(this.resourceCustomSubscriptions.get(CurrentProjectResource));

    document.removeEventListener(`copyDemoToProject`, this.handleCopyDemoToProject);
  }

  onDisconnected = (disconnectAction: () => void) => {
    this.disconnectQueue.push(disconnectAction);
  };

  hasImplementedData = () => this.getCurrentProject()?.hasIntegratedArb;

  handleCardSeen = (cardId: DashboardCardId) => {
    this.setState(
      produce((draft) => {
        draft.seenCards[cardId] = true;
      }),
    );
  };

  resourceCustomSubscriptions = new Map([
    [
      CurrentProjectResource,
      () => {
        this.loadSavedReportsOnce(); // async, no need to wait

        if (
          !this.state.browseParams?.isBrowseMode &&
          this.getCurrentProject().isDemo &&
          Number.isInteger(this.state.routedDashboardId)
        ) {
          this.setState({revealedBannerIdentifiers: new Set([USE_THIS_BOARD_IDENTIFIER])});
        }
      },
    ],
  ]);

  handleIndexRoute = () => {
    // delay route change by a frame to allow this route handler to complete before changing route
    window.requestAnimationFrame(() => {
      this.replaceAndInvokeDefaultRoute(); /* async, no need to wait */
    });
  };

  handleHashRoutes = (hashParams: string) => {
    const requiredStateUpdates = {...this.state};
    const {parsedData, correctedHashParams} = parseDashboardUrlHashParams(hashParams);

    requiredStateUpdates.revealedBannerIdentifiers = new Set();
    if (isBrowseUrlData(parsedData)) {
      requiredStateUpdates.browseParams = parsedData;
      requiredStateUpdates[`$fragment`] = correctedHashParams;

      this.showSidenav({selectedNavItem: BROWSE_MODE_KEY});
      this.setState(requiredStateUpdates, () => {
        this.props.navigate(`#${correctedHashParams}`, {replace: true});
      });

      if (!isNil(requiredStateUpdates.browseParams?.searchText)) {
        this.props.globalSearchController.setSearchedText(requiredStateUpdates.browseParams.searchText);
      }

      return;
    }
    requiredStateUpdates.browseParams = null;

    if (Number.isInteger(parsedData.dashboardId)) {
      // dashboard ID
      if (parsedData.dashboardId !== this.props.selectorController.getActiveDashboardController()?.getModel()?.id) {
        this.props.selectorController.changeActiveDashboard(parsedData.dashboardId); // async, no need to wait
        requiredStateUpdates.dashboardFailed = false;
        requiredStateUpdates.dashboardLoaded = false;
        requiredStateUpdates.routedDashboardId = parsedData.dashboardId;
      }

      if (this.getCurrentProject()?.isDemo) {
        requiredStateUpdates.revealedBannerIdentifiers = new Set([USE_THIS_BOARD_IDENTIFIER]);
      }
    } else if (Number.isInteger(parsedData.rcaSourceId)) {
      this.createRCADashboard(parsedData.rcaSourceId, parsedData.rcaSourceData);
    } else {
      this.props.onToast(
        new CustomEvent(`toast`, {
          detail: {
            text: `URL's board ID was invalid, you were redirected to Discover.`,
          },
        }),
      );
      // delay route change by a frame to allow this route handler to complete before changing route
      window.requestAnimationFrame(() => {
        this.replaceAndInvokeDefaultRoute(); /* async, no need to wait */
      });
    }

    requiredStateUpdates.tvModeEnabled = parsedData.tvModeEnabled || false;
    requiredStateUpdates.isEmbedMode = isEmbedMode();
    // TODO modified this to include focusedcardid when we are able
    // to copy link of a text card
    if (parsedData.focusedBookmarkId) {
      requiredStateUpdates.focusedCard = {
        bookmarkId: parsedData.focusedBookmarkId,
        isNew: false,
      };
    }

    if (parsedData.focusedCardId) {
      requiredStateUpdates.focusedCard = {
        cardId: parsedData.focusedCardId,
        isNew: this.state.focusedCard?.isNew || false,
      };
    }

    const chromeHeaderEl = document.querySelector<ChromeHeader>(`mp-chrome-header`);
    if (chromeHeaderEl) {
      if (requiredStateUpdates.tvModeEnabled) {
        chromeHeaderEl.setParams({...chromeHeaderEl.params, hidden: true});
      } else {
        chromeHeaderEl.setParams({...chromeHeaderEl.params, hidden: false});
      }
    }

    const {editorCardId, editorCardEditedBookmark, editorCardBookmarkURLSlug} = parsedData;
    requiredStateUpdates.editorCardId = editorCardId;
    requiredStateUpdates.editorCardEditedBookmark = editorCardEditedBookmark;
    requiredStateUpdates.editorCardBookmarkURLSlug = editorCardBookmarkURLSlug;
    if (editorCardEditedBookmark) {
      this.setState({editedBookmarkLongUrlCount: this.state.editedBookmarkLongUrlCount + 1});
    }
    if (!this.invokedFirstRoute && !!(editorCardId || editorCardEditedBookmark || editorCardBookmarkURLSlug)) {
      const spaNavCount = isGlobalSidenavEnabled(this.getCurrentProject())
        ? this.props.spaNavManager.getSpaNavCount()
        : getSpaNavCount();
      if (spaNavCount === 0) {
        this.setState({isFasteditPageload: true});
      }
    }

    // apply any URL reparations
    if (correctedHashParams !== hashParams) {
      this.props.navigate(`#${correctedHashParams}`, {replace: true});
      requiredStateUpdates[`$fragment`] = correctedHashParams;
    }

    const isMinimalMode = requiredStateUpdates.isEmbedMode || requiredStateUpdates.tvModeEnabled;
    if (isMinimalMode) {
      this.props.sidenavContext.setSidenavState({showSidenav: false});
    } else {
      this.showSidenav({selectedNavItem: requiredStateUpdates.routedDashboardId?.toString()});
    }

    this.invokedFirstRoute = true;
    this.setState(requiredStateUpdates);
  };

  showSidenav({selectedNavItem}: {selectedNavItem: string}) {
    this.props.sidenavContext?.setSidenavState({
      showSidenav: true,
      selectedNavItem,
      preventDefaultNavigate: this.preventDefaultNavigate,
      confirmDashboardNavigate: this.leaveRHSModeConfirmation,
      confirmHeaderAction: this.leaveRHSModeConfirmation,
    });
  }

  isMinimalMode() {
    return this.state.isEmbedMode || this.state.tvModeEnabled;
  }

  async saveDashboardReport({
    bookmarkId,
    originalBookmarkData,
    bookmarkData,
    bookmarkType,
    cardId,
    saveAsNew,
    disableHistory,
  }) {
    let savedReportCardId = cardId;
    let updatedDashboard;
    try {
      if (!saveAsNew && bookmarkId) {
        const contentAction = {
          contentId: bookmarkId,
          contentType: ContentType.Report,
          contentParams: {bookmark: {...bookmarkData}},
          actionType: ContentActionType.Update,
        };

        if (!this.state.isHistoryActionInProgress && !disableHistory && originalBookmarkData) {
          this.historyManager.applyUpdates<TODO>({
            source: {fields: {...contentAction, contentParams: {bookmark: originalBookmarkData}}},
            updates: {fields: contentAction},
            metadata: {
              isContentUpdate: true,
            },
          });
        }

        this.props.selectorController.debouncedStopSavingChanges();

        ({updatedDashboard} = await this.saveChangesOnDashboard({contentAction, updateModel: false}));
      } else {
        const contentAction = {
          contentType: ContentType.Report,
          contentParams: {bookmark: {...bookmarkData, type: bookmarkType}},
          actionType: ContentActionType.Create,
        };
        ({updatedDashboard} = await this.saveChangesOnDashboard({contentAction, updateModel: false}));
        const {new_content: newContent} = updatedDashboard;
        savedReportCardId = getCardIdFromContent(newContent.id, ContentType.Report);
      }
    } catch (error) {
      this.props.context.trackError(error as Error);
      this.showToast({
        text: `Error saving report`,
        type: ToastType.ERROR,
      });
    }
    return {savedReportCardId, updatedDashboard};
  }

  addReportToRecentsSearchList(dashboard) {
    const {projectId, workspaceId} = this.props.context;
    const item = {
      description: dashboard.description,
      icon: SearchDisplayContextIcons.Dashboard,
      id: dashboard.id,
      isOwner: dashboard.creator_id === this.props.context.userId,
      key: `entity-${dashboard.id}`,
      label: dashboard.title,
      link: getReportUrl(`boards/#id=${dashboard.id}`, {projectId, workspaceId}),
      report: SearchDisplayContext.Dashboard,
      subLabel: `${dashboard.creator_name} : ${formatDate(new Date(dashboard.modified))}`,
      type: RecentSearchItemType.Entity,
      user: dashboard.creator_name,
    };
    searchUtils.addRecentItem(item);
  }

  private addCardToRecentsSearchList(cardId: DashboardCardId) {
    const card = this.state.cards.get(cardId);
    if (card && isDashboardCard(card)) {
      searchUtils.addRecentItem(
        transformBookmarkIntoRecentItem(card.bookmark, card.bookmark.type, {
          projectId: this.props.context.projectId,
          workspaceId: this.props.context.workspaceId,
          userId: this.props.context.userId,
        }),
      );
    }
  }

  private getCardDistanceFromViewport(cardId) {
    if (this.gridEl && cardId) {
      const card = this.state.cards.get(cardId);
      if (card) {
        const {content_id: contentId, type: contentType} = this.state.cards.get(cardId);

        const containerElement = this.gridEl.getItemContainerElement(contentId, contentType);
        if (containerElement) {
          const rect = containerElement.getBoundingClientRect();

          // We're only concerned with vertical closeness here, calculate the
          // vertical distance between the card midpoint and viewport midpoint.
          const cardMidpointDistance = Math.abs(rect.top + rect.height / 2);
          const windowMidpoint = window.innerHeight / 2;
          return Math.abs(cardMidpointDistance - windowMidpoint);
        }
      }
    }
    return Number.MAX_VALUE;
  }

  getActiveStagedDashboard(extraFields: Partial<Dashboard>) {
    const stagedDashboard = new StagedDashboard(
      {
        ...this.state.dashboard,
        title: this.state.dashboard.title,
        cards: [],
        card_order: this.state.cardOrder,
        filters: this.state.filters.map(
          (filter) =>
            pick(
              filter,
              `filterOperator`,
              `filterValue`,
              `propertyDefaultType`,
              `propertyName`,
              `propertyObjectKey`,
              `propertyType`,
              `resourceType`,
            ) as Segmentation,
        ),
        time_filter: this.state.timeFilter,
        breakdowns: this.state.breakdowns,
        ...extraFields,
      },
      {
        isSaveAsNew: true,
        crudSource: DashboardCRUDSource.Header,
      },
    );
    // we don't set the cards immediately on purpose. When we create the dashboard, we compare the original cards with the current cards.
    stagedDashboard.dashboard.cards = this.getAvailableCards();
    return stagedDashboard;
  }

  async deleteDashboard(dashboard: DashboardItemInfo, crudSource: DashboardCRUDSource, bypassConfirm?: boolean) {
    const {t} = this.getLocalizationStore();

    this.props.context.track(
      TrackingEvent.ClickedDeleteDashboard,
      getCommonDashboardTrackingProps({
        dashboard,
        cards: this.getAvailableCards(),
        sourceTrackingProps: {crudSource},
      }),
    );
    const isActive = this.isActiveDashboard(dashboard);
    const confirmationMessage = `${t(`boards.confirmation.delete.msg-header`, {boardTitle: dashboard.title})} ${dashboard.child_dashboards?.length > 0 ? t(`boards.confirmation.delete.msg-has-child`) : ``}\n\n${t(`boards.confirmation.delete.msg-footer`)}`;

    if (
      bypassConfirm ||
      (await confirm({
        title: t(`boards.confirmation.delete.title`),
        description: isAllowedSuperadminOverride(dashboard)
          ? `${dashboard.allow_staff_override ? t(`boards.confirmation.delete.permission-staff`) : t(`boards.confirmation.delete.permission-owner`)}\n\n${confirmationMessage}`
          : confirmationMessage,
        theme: ConfirmTheme.DELETE,
        'confirm-button-label': t(`boards.confirmation.delete.confirm-btn-label`),
        'description-allow-newline': true,
      }))
    ) {
      try {
        await this.props.selectorController.deleteDashboard(dashboard.id);
        this.deleteSidenavItem(dashboard.id.toString());

        if (isActive) {
          await this.replaceAndInvokeDefaultRoute(`discover`);
        }

        this.props.context.track(TrackingEvent.DeleteDashboard, {
          ...getCommonDashboardTrackingProps({
            dashboard,
            cards: this.getAvailableCards(),
            sourceTrackingProps: {crudSource},
          }),
          ...this.myDashboardTrackingProps(dashboard.id),
        });
      } catch (err) {
        reporter.error(err as Error, `Error deleting board.`);
        this.props.onToast(
          new CustomEvent(`toast`, {
            detail: {
              text: `Error deleting board`,
              type: ToastType.ERROR,
              timeoutMS: 20000,
            },
          }),
        );
      }
      this.props.selectorController.debouncedStopSavingChanges();
    }
  }

  onTVModeLinkClick() {
    this.props.navigate(`#${serializeDashboardUrlHashParams({...this.getUrlDataFromState(), tvModeEnabled: true})}`);
    this.props.context.track(
      TrackingEvent.ClickedTVModeLink,
      getCommonDashboardTrackingProps({
        dashboard: this.state.dashboard,
        cards: this.getAvailableCards(),
      }),
    );
  }

  onPinDashboardClick() {
    this.pinDashboard(this.state.dashboard, DashboardCRUDSource.Header);
  }

  onRefresh(shouldTrack: boolean = true, useQueryApiCache: boolean = false) {
    this.clearCards();
    const cards = this.state.dashboard.card_order.map((id) => this.state.cards.get(id));
    for (const card of cards) {
      this.loadCard(card, {useQueryApiCache});
    }
    this.setState({
      lastRefreshed: new Date(),
    });
    if (shouldTrack) {
      this.props.context.track(TrackingEvent.ReloadReport, {
        ...getCommonDashboardTrackingProps({
          dashboard: this.state.dashboard,
          cards: this.getAvailableCards(),
        }),
        manual: true,
      });
    }
  }
  private loadGlobalInfoOnce(): Promise<void> {
    if (!this.globalInfoLoadPromise?.then) {
      this.globalInfoLoadPromise = (async () => {
        try {
          await this.props.context.loadUserInfo();
          // Re-render view with permissions.
          this.setState({globalInfoLoaded: true});

          const {isStandalone, projectId, workspaceId} = this.props.context;

          // Kick off fetches to various stores, but do not block.
          dataGroupStore.fetch({projectId, workspaceId});
          syncReportPropertyValueMappings(projectId);
          if (isStandalone) {
            fetchDisplayNameData(projectId, workspaceId);
          }
        } catch (err) {
          this.props.onToast(
            new CustomEvent(`toast`, {
              detail: {
                text: `Failed to load additional account information. If this error persists, please contact support.`,
                type: ToastType.ERROR,
              },
            }),
          );
          this.props.context.trackError(err as Error);
        }
      })();
    }
    return this.globalInfoLoadPromise;
  }

  private loadSavedReportsOnce(): Promise<void> {
    if (!this.savedReportsLoadPromise?.then) {
      this.savedReportsLoadPromise = (async () => {
        const start = getNow();
        try {
          await this.props.context.loadSavedReports();
          this.setState({savedReportsLoaded: true});
          this.props.context.track(TrackingEvent.GetSavedReports, {
            latency: Math.round(diffFromNow(start)),
            status: 200,
          });
        } catch (error) {
          this.savedReportsLoadPromise = null;
          this.props.onToast(
            new CustomEvent(`toast`, {
              detail: {
                text: `Failed to load saved reports. If this error persists, please contact support.`,
                type: ToastType.ERROR,
              },
            }),
          );
          this.props.context.track(TrackingEvent.GetSavedReports, {latency: Math.round(diffFromNow(start)), error});
          this.props.context.trackError(error as Error);
        }
      })();
    }
    return this.savedReportsLoadPromise;
  }

  private trackViewedReport(): void {
    const queryParams = new URLSearchParams(this.props.location.search);
    const referrer = queryParams.get(`referrer`) || `N/A`;
    const mpSource = queryParams.get(`mp_source`);

    let properties = {};
    if (mpSource === `webdashdigest`) {
      // ?mp_source=webdashdigest indicates that this URL comes from an email digest
      const digestId = parseInt(queryParams.get(`digest`), 10);
      properties = {
        'arrived from (mp_source)': `web dash digest`,
        'referred by email from digest ID': digestId,
        'used goto action': queryParams.get(`goto_action`) === `1`,
      };
    } else if (mpSource) {
      // handle all other mp_source values (e.g. slack_alert, custom_alert, anomaly_detection_alert)
      properties = {
        'arrived from (mp_source)': mpSource,
      };
    }

    this.props.context.track(TrackingEvent.ViewDashboardsReport, {...properties, referrer});
  }

  // prioritize onboarding, else go to Discover
  private async replaceAndInvokeDefaultRoute(override: string | null = null): Promise<void> {
    const urlData = this.getUrlDataFromState();
    await this.loadGlobalInfoOnce();

    if (!this.mounted) {
      return;
    }

    const dashboardId = (() => {
      if (this.props.context.isFirstTimeUser() && this.getOnboardingDashboardId()) {
        this.props.context.track(TrackingEvent.RedirectedToOnboardingDashboard);
        return this.getOnboardingDashboardId();
      }

      if (this.props.context.isFirstTimeUser() && userDetailsStore.value.myDashboardId) {
        this.props.context.track(TrackingEvent.RedirectedToMyDashboard);
        return userDetailsStore.value.myDashboardId;
      }

      return null;
    })();

    if (dashboardId) {
      const hashParams = serializeDashboardUrlHashParams({
        ...urlData,
        dashboardId,
      });

      this.props.navigate(`#${hashParams}`, {replace: true});
    } else {
      // If we need to show the template modal, can't redirect users to home
      override = getLocation().href.includes(`show-template-selector`) ? DefaultRoutePath.Discover : override;
      const url =
        override === DefaultRoutePath.Discover
          ? `#${BROWSE_URL_PARAM}`
          : getReportUrl(DefaultRoutePath.Home, {
              projectId: this.props.context.projectId,
              workspaceId: this.props.context.workspaceId,
            });
      this.props.navigate(url, {replace: true});
    }
  }

  private handleDashboardControllerChanged(dashboardController: DashboardModelController<DashboardsClient>) {
    // this subscription maybe called while the component is being unmounted
    // this will cause race condition in captureWorkflowTiming because it highly depends on the component `this` context
    if (!this.mounted) return;
    if (!this.perfTracking.has(dashboardController.getModel().id)) {
      captureWorkflowTiming(this, {
        name: `Initial Dashboard Cards`,
        key: `initial-dashboard-cards`,
        isComplete: initialCardsLoaded(dashboardController.getModel().id),
      });
    } else {
      const perfTracking = this.perfTracking.get(dashboardController.getModel().id);
      if (!perfTracking.firstLoad) {
        captureWorkflowTiming(this, {
          name: `Initial Dashboard Cards`,
          key: `initial-dashboard-cards`,
          isComplete: initialCardsLoaded(dashboardController.getModel().id),
        });
      } else {
        cancelWorkflow(this, `initial-dashboard-cards`, `changed to loaded dashboard`);
      }
    }
    // unsubscribe old controller
    if (this.dashboardController) {
      // @ts-ignore
      this.dashboardController.unsubscribeUpdates(this.dashboardListener);
    }

    this.dashboardController = dashboardController;

    // subscribe and save new controller
    if (this.dashboardController) {
      this.dashboardListener = () => {
        this.updateFromDashboardController();
      };
      // @ts-ignore
      this.dashboardController.subscribeUpdates(this.dashboardListener);
      this.updateFromDashboardController();
    }
  }

  reloadChangedCards(previousDashboard: Dashboard, dashboard: Dashboard) {
    // why we expensively check for changed cards:
    // 1. checking for changed queries is hampered by the presence of timestamps in some query params,
    //    so we check for changes in relevant card params as a relatively simple hack/workaround
    // 2. the DashboardModelController updates the whole dashboard.cards array for every individual card modification
    //    so we isolate changed cards to avoid duplicate reloads
    // 3. the DashboardModelController often updates the whole model twice for every load request:
    //    first to return pre-cached offline data (if exists), and second to return fresh network data;
    //    pre-cached offline data is often very similar to fresh network data, so we isolate changed cards to avoid duplicate reloads

    // Note: we are excluding text cards from this specific section because here we are aborting card queries and soft reloading cards
    // whose purpose is to requery the info from the bookmark, which text cards don't have any. we should already be getting the updated
    // text card through the model reload
    const changedCards = this.getParamsChangedCardsFromUpdatedDashboard(previousDashboard, dashboard);
    if (changedCards.length > 0) {
      if (changedCards.length === dashboard.cards.length) {
        // All cards changed, so wipe out ALL cards instead of wiping out cards by id, which can
        // have race-condition issues.
        this.clearCards();
      } else {
        this.clearCards(changedCards);
      }
      for (const card of changedCards) {
        this.loadCard(card, {useQueryApiCache: true});
      }
    }
  }

  private getParamsChangedCardsFromUpdatedDashboard(previousDashboard: Dashboard, updatedDashboard: Dashboard) {
    if (this.state.dashboardLoaded && previousDashboard?.id === updatedDashboard.id) {
      return updatedDashboard.cards.filter((card) => {
        if (!this.state.cards.has(card.id)) {
          return true;
        }
        const existingCard = this.state.cards.get(card.id);
        if (existingCard !== card) {
          if (existingCard.type === ContentType.Media) {
            const existingMediaCard = existingCard as MediaCard;
            const mediaCard = card as MediaCard;
            return existingMediaCard.id !== mediaCard.id;
          } else if (existingCard.type === ContentType.Benchmark) {
            const existingBenchmarkCard = existingCard as BenchmarkCard;
            const benchmarkCard = card as BenchmarkCard;
            return !(
              existingBenchmarkCard.id === benchmarkCard.id &&
              isEqual(existingBenchmarkCard.bookmark.params, benchmarkCard.bookmark.params)
            );
          } else if (existingCard.type === ContentType.Text) {
            const existingTextCard = existingCard as TextCard;
            const textCard = card as TextCard;
            return !(existingTextCard.id === textCard.id && isEqual(existingTextCard.markdown, textCard.markdown));
          } else if (existingCard.type === ContentType.Dashboard) {
            const existingNestedDashboardCard = existingCard as NestedDashboardCard;
            const nestedDashboardCard = card as NestedDashboardCard;
            return !(
              existingNestedDashboardCard.id === nestedDashboardCard.id &&
              isEqual(existingNestedDashboardCard.thumbnail_layout, nestedDashboardCard.thumbnail_layout) &&
              isEqual(existingNestedDashboardCard.thumbnail_contents, nestedDashboardCard.thumbnail_contents)
            );
          } else {
            const existingDashboardCard = existingCard as DashboardCard;
            const dashboardCard = card as DashboardCard;
            return !(
              existingDashboardCard.id === card.id &&
              isEqual(existingDashboardCard.chart_options, dashboardCard.chart_options) &&
              isEqual(existingDashboardCard.bookmark_metadata, dashboardCard.bookmark_metadata) &&
              isEqual(existingDashboardCard.bookmark.id, dashboardCard.bookmark.id) &&
              isEqual(existingDashboardCard.linked_dashboard_card_id, dashboardCard.linked_dashboard_card_id) &&
              isEqual(existingDashboardCard.bookmark.params, dashboardCard.bookmark.params)
            );
          }
        }
        return false;
      });
    }
    return updatedDashboard.cards;
  }

  private updateFromDashboardController() {
    const apiError = this.dashboardController.getError();
    if (apiError) {
      window.dispatchEvent(new Event(`tti:invalid`));
      if (apiError.name === `DashboardDoesNotExistError`) {
        // a 404 not found API error isn't really considered a serious failure, so we ignore it and redirect to an available Dashboard
        // (90% of the time this is caused because URL changed its project/workspace[dataview] ID without changing the URL hash that specifies the dashboard ID)
        this.props.onToast(
          new CustomEvent(`toast`, {
            detail: {
              text: `Board does not exist or has been deleted, you were redirected to Discover.`,
            },
          }),
        );
        this.replaceAndInvokeDefaultRoute(); // async, no need to wait
      } else {
        this.setState({dashboardFailed: true});
        this.props.onToast(
          new CustomEvent(`toast`, {
            detail: {
              text: `Unexpected error: board failed to load.`,
              type: ToastType.ERROR,
            },
          }),
        );
      }
    } else if (this.dashboardController.getLoaded()) {
      const dashboard = this.dashboardController.getModel();
      this.queryApiForegroundPool =
        this.queryApiForegroundPool ??
        new AsyncCountingSemaphorePriorityQueue(getMaxConcurrentQueries(getProjectId(), dashboard));
      this.queryApiBackgroundPool =
        this.queryApiBackgroundPool ??
        new AsyncCountingSemaphorePriorityQueue(getMaxConcurrentBackgroundPoolQueries(getProjectId(), dashboard));
      this.cardsAutoLoadedCounter =
        this.cardsAutoLoadedCounter ?? new AsyncCountingSemaphore(getMaxCardsToLoadImmediately(getProjectId()));

      const {parsedData} = parseDashboardUrlHashParams(this.props.location.hash.slice(1));
      const updateJson = {
        timeFilter: (`timeFilter` in parsedData ? parsedData.timeFilter : dashboard.time_filter) || null,
        filters: getBoardFiltersFromPersisted((`filters` in parsedData ? parsedData.filters : dashboard.filters) || []),
        breakdowns: (`breakdowns` in parsedData ? parsedData.breakdowns : dashboard.breakdowns) || [],
      };

      if (!this.state.dashboardLoaded || this.state.dashboard.id !== dashboard.id) {
        // when loading boards, reset all previously assigned colors
        this.props.themes.resetColorMappings();
        this.props.queryContextManager.updateBoardPermissions(getEditCapability(dashboard).enableEdit);

        this.props.context.track(TrackingEvent.DashboardLoaded, {
          'has a parent': this.dashboardHasParent(dashboard),
          'num board cards': this.getContentTypeCount(dashboard, ContentType.Dashboard),
          'child dashboard count by other creators': this.getChildDashboardCountByOtherCreators(dashboard),
          'dashboard id': dashboard.id,
          description: dashboard.description,
          'description length': dashboard.description ? dashboard.description.length : 0,
          'is onboarding dashboard': dashboard.id === this.getOnboardingDashboardId(),
          title: dashboard.title,
          'title length': dashboard.title ? dashboard.title.length : 0,
          'number of reports': dashboard.card_order ? dashboard.card_order.length : 0,
          'is favorited': dashboard.is_favorited,
          'project id': this.props.context.projectId,
          'workspace id': this.props.context.workspaceId,
          'user id': this.props.context.userId,
          'filters count':
            updateJson.filters.length + (updateJson.timeFilter && !isEmpty(updateJson.timeFilter) ? 1 : 0),
          'breakdowns count': updateJson.breakdowns?.length || 0,
          ...getBoardFilterGroupPropTrackingProps(updateJson.filters, dashboard.cards),
          ...this.myDashboardTrackingProps(dashboard.id),
          ...getCommonDashboardTrackingProps({
            dashboard,
            cards: dashboard.cards,
          }),
        });
        window.dispatchEvent(new Event(`tti:complete`));
      }

      const previousDashboard = this.state.dashboard;

      this.setState(
        {
          ...updateJson,
          dashboard,
          dashboardLoaded: true,
          // cardOrder represent the staged order, while dashboard.card_order is the saved order. We don't want them to be the same object because we compare them,
          // so we need to destructure
          cardOrder: [...dashboard.card_order],
        },
        () => {
          this.reloadChangedCards(previousDashboard, dashboard);

          // TODO go/j/AE-909: properly treat state as immutable.
          // clear temporary cards
          Array.from(this.state.cards.keys())
            .filter((key) => Number(key) < 0)
            .map((tempKey) => {
              this.state.cards.delete(tempKey);
              this.state.foregroundCardResults.delete(tempKey);
            });

          for (const dashboardCard of dashboard.cards) {
            this.state.cards.set(dashboardCard.id, {
              ...dashboardCard,
            });
          }

          /** handles the case when you are editing a report and the underlying dashboard card is deleted */
          if (
            this.state.editorCardId &&
            isStagedCardId(this.state.editorCardId) === false &&
            !this.state.cards.has(this.state.editorCardId)
          ) {
            this.props.onToast(
              new CustomEvent(`toast`, {
                detail: {
                  text: `Report does not exist or has been deleted.`,
                  type: ToastType.ERROR,
                },
              }),
            );

            this.clearEditorCardState();
          }

          this.highlightAndScrollToFocusedCard();
          if (this.isOnStarterBoardWithData()) {
            incrementUserFlag(UserFlag.ViewedStarterBoardWithDataCount);
          }

          if (this.isOnStarterBoard()) {
            trackHotjarEvent(`StarterBoardLoad`);
          }

          // Temporarily enable Hotjar on B2B demo for user research
          if (this.getCurrentProject()?.id === 2195193 /* B2B demo project */) {
            trackHotjarEvent(`B2BDemo`);
          }

          const urlParams = this.getUrlDataFromState();
          this.addReportToRecentsSearchList(dashboard);
          this.setState({
            dashboardModified: `timeFilter` in urlParams || `filters` in urlParams || `breakdowns` in urlParams,
          });
          this.props.navigate(`#${serializeDashboardUrlHashParams(urlParams)}`, {replace: true});
          if (dashboard?.generation_type === DashboardType.StagedTemplate) {
            trackHotjarEvent(`RHS`);
            if (!this.state.blueprintConfig) {
              this.initializeBlueprintConfig();
            }
            this.setBlueprintDashboard(dashboard);
            this.setState({showTemplateRHS: true});
            const chromeHeader = document.querySelector<ChromeHeader>(`mp-chrome-header`);
            chromeHeader?.helpers.dismissOnboardingChecklist(false); // hide onboarding checklist since it's overlapping
          } else {
            this.setState({showTemplateRHS: false});
          }
        },
      );

      const moveInfo = this.movedBoardInfoStorage.getItem({
        userId: this.props.context.userId,
        key: MOVED_BOARD_STORAGE_KEY,
      });
      if (moveInfo) {
        this.movedBoardInfoStorage.removeItem({userId: this.props.context.userId, key: MOVED_BOARD_STORAGE_KEY});
        this.props.onToast(
          new CustomEvent(`toast`, {
            detail: {
              text: `Moved ${dashboard.title || `board`} to ${this.getCurrentProject()?.name || `current project`}`,
              type: ToastType.SUCCESS,
              timeoutMS: 20000,
              undo: moveInfo.undoable ? this.createUndoBoardMoveCallback(dashboard.id, moveInfo) : undefined,
            },
          }),
        );
      }
    }
  }

  private dashboardHasParent(dashboard: Dashboard) {
    const hasParent = !!dashboard.parent_dashboard_id && dashboard.ancestors?.length > 0;
    return hasParent;
  }

  private getContentTypeCount(dashboard: Dashboard, contentType: ContentType) {
    const cardsOfContentType = Object.entries(dashboard.contents[contentType] ?? {});
    return cardsOfContentType.length;
  }

  private getChildDashboardCountByOtherCreators(dashboard: Dashboard) {
    const childDashboardsByOtherCreators = Object.entries(dashboard.contents.dashboard ?? {}).filter(
      (childDash) => childDash[1].creator_id !== dashboard.creator_id,
    );
    return childDashboardsByOtherCreators.length;
  }

  private setBlueprintDashboard(blueprintDashboard: Dashboard) {
    this.setState({blueprintDashboard: cloneDeep(blueprintDashboard)});
  }

  private highlightAndScrollToFocusedCard({smoothScroll = false} = {}) {
    const focusedCardId = this.getFocusedCardId();
    if (this.gridEl && focusedCardId && !this.state.wasCardFocused) {
      let containerElement;
      const card = this.state.cards.get(focusedCardId);
      if (card) {
        const {content_id: contentId, type: contentType} = this.state.cards.get(focusedCardId);
        containerElement = this.gridEl.getItemContainerElement(contentId, contentType);
      }

      if (containerElement) {
        this.setState({wasCardFocused: true});
        window.requestAnimationFrame(() => {
          containerElement.scrollIntoView({
            block: this.state.focusedCard?.isNew ? `nearest` : `start`,
            behavior: smoothScroll ? `smooth` : `auto`,
          });
        });
      }
    }
  }

  isCompanyKPITemplateDashboard = (): boolean => {
    return (
      this.state.dashboard.template_type === TemplateType.SAAS_COMPANY_KPI &&
      this.state.dashboard.generation_type === DashboardType.StagedTemplate
    );
  };

  private async formatKPICardResult(
    card: DashboardCard | BenchmarkCard,
    cardResult: DashboardCardResult,
    dataGroupsPromise: Promise<DataGroup[]>,
  ): Promise<DashboardCardResult> {
    /**
     * Workaround for company KPI tempate cards that can have a mismatched dataGroupId.
     * This broken state will result in a timeout error state. This function formats
     * the card result to display a better broken state.
     */

    const dataGroups = await dataGroupsPromise;
    const dataGroupIds = dataGroups.map((dataGroup) => dataGroup.groupID);
    if (!dataGroupIds.includes(card.bookmark?.params?.sections?.globalDataGroupId)) {
      cardResult = {
        invalidBookmark: false,
        dataGroupNotSupported: false,
        notSupported: false,
        queryLastUpdated: ``,
        queryResponseTimeMs: 0,

        chartLoaded: true,
        chartParams: null,
      };
    }
    return cardResult;
  }

  private async loadCard(card: CardOnDashboard, {useQueryApiCache = true}: {useQueryApiCache?: boolean} = {}) {
    if (!isDashboardCard(card) && !isBenchmarkCard(card)) {
      // no need to query a text card because it's not connected to a bookmark
      // but do scroll to card if focused
      this.highlightAndScrollToFocusedCard();
      return;
    }

    await waitForNecessaryResources({
      metricsResource: this.props.metrics,
      behaviorsResource: this.props.behaviors,
      bookmark: card.bookmark,
    });

    let autoLoadAcquire = false;
    const isNotReportEditor = !this.state.editorCardBookmarkURLSlug && !this.state.editorCardId;
    if (isNotReportEditor) {
      this.cardsAutoLoadedCounter.abortPendingKey(card.id);
      autoLoadAcquire = await this.cardsAutoLoadedCounter.tryAcquire(card.id);
    }

    // Since we are auto-loading cards
    let spinnerStart = this.cardSeenWatcher.wasSeen(card.id) ? getNow() : null;
    let spinnerTime = null;
    const seenLoadAcquirePromise = this.cardSeenWatcher.waitUntilSeen(card.id).then((result) => {
      if (spinnerStart === null && spinnerTime === null) {
        spinnerStart = getNow();
      }
      return result;
    });
    // Load up to the number set in cardsAutoLoadedCounter or wait until the card comes into view.
    if (autoLoadAcquire || (await seenLoadAcquirePromise)) {
      const reportCard = card as DashboardCard | BenchmarkCard;
      // acquiring an already-waiting key in queryApiForegroundPool is not allowed (leads to deadlocks),
      // so abort any pending queryApiForegroundPool slots right before attempting to acquire()
      this.abortCardQueries([card]);
      if (await this.queryApiForegroundPool.acquire(card.id, () => this.getCardDistanceFromViewport(card.id))) {
        let cardResult: DashboardCardResult | null = null;
        try {
          let abortSignal: AbortSignal = null;
          if (AbortController) {
            // check if browser supports AbortController
            const abortController = new AbortController();
            this.foregroundQueryAbortControllers.set(card.id, abortController);
            abortSignal = abortController.signal;
          }

          // When creating a link to a given report, it should reflect current workspace[dataview]
          // ID. Not the workspace where it is originally created.
          const currentWorkspaceCard = this.convertToCurrentWorkspaceCard(card);
          this.markFetchCardTracking(card.id);
          // This grabs the index of a card being loaded as it is laid out in the dashboard, but
          // only relative to other cards that hit query API. This is basically trying to figure
          // out what the load order would be if cards were loaded in the same order they're laid
          // out in.
          const curCardIndex = this.state.dashboard.card_order
            .map((id) => this.state.cards.get(id))
            .filter((filterCard) => [ContentType.Report, ContentType.ReportLink].includes(filterCard?.type))
            // Use the card index from before filtering down to the specific card being processed.
            .map((filterCard, index) => [filterCard, index])
            .filter(([filterCard, _]) => (filterCard as DashboardCard | BenchmarkCard).id === card.id)
            .map(([_, index]) => index)[0];
          const curCardLoadOrderIndex = this.foregroundLoadCardCounter;
          this.foregroundLoadCardCounter += 1;

          const dataGroupsPromise = (async (): Promise<DataGroup[]> => {
            await this.loadGlobalInfoOnce();
            return this.props.context.dataGroups;
          })(); // async, don't wait

          cardResult = getCardResultIfInvalid(currentWorkspaceCard, this.state.filters);
          if (this.isCompanyKPITemplateDashboard) {
            await this.formatKPICardResult(card, cardResult, dataGroupsPromise);
          }
          const currentProject = this.getCurrentProject();
          if (!cardResult) {
            // Removing time filters for benchmark due to x-axis(Day, Week etc) auto adjusting depending on selection
            const timeFilterOverride = isBenchmarkCard(card) ? null : this.state.timeFilter;
            const start = getNow();
            const resultsPromise = queryCanonicalBookmark({
              dashboard: this.state.dashboard,
              currentProject,
              card: currentWorkspaceCard,
              location: this.props.location,
              globalFilters: this.state.filters,
              globalBreakdowns: this.getBreakdowns(),
              timeFilterOverride,
              useQueryCache: useQueryApiCache,
              abortSignal,
              queryOrigin: this.state.tvModeEnabled ? DashboardQueryOrigin.TvMode : DashboardQueryOrigin.Standard,
              metricsResource: this.props.metrics,
              behaviorsResource: this.props.behaviors,
            });

            const bookmarkQueryMetadataPromise = spans.withSpanById(getCardQuerySpanId(card.id), () => {
              return queryCanonicalBookmark({
                dashboard: this.state.dashboard,
                currentProject,
                card: currentWorkspaceCard,
                location: this.props.location,
                globalFilters: this.state.filters,
                globalBreakdowns: this.getBreakdowns(),
                timeFilterOverride,
                useQueryCache: useQueryApiCache,
                abortSignal,
                queryOrigin: this.state.tvModeEnabled ? DashboardQueryOrigin.TvMode : DashboardQueryOrigin.Standard,
                queryMetadata: true,
                metricsResource: this.props.metrics,
                behaviorsResource: this.props.behaviors,
              });
            });

            // TODO go/j/AE-909: replace state mutation with truly immutable state update.
            const foregroundQueryResults = new Map(this.state.foregroundQueryResults.set(card.id, resultsPromise));
            this.setState({foregroundQueryResults});

            const [results, bookmarkQueryMetadata] = await Promise.all([resultsPromise, bookmarkQueryMetadataPromise]);

            const queryResponseTimeMs = Math.round(diffFromNow(start));
            cardResult = createCardResults({
              card: currentWorkspaceCard,
              results,
              queryResponseTimeMs,
            });

            cardResult = {...cardResult, cardEventsInfo: bookmarkQueryMetadata};
          }
          this.foregroundQueryAbortControllers.delete(card.id);
          // TODO go/j/AE-909: replace state mutation with truly immutable state update.
          // Tests currently flake without the state mutation due to other dependencies on combined w/ async patterns.
          const foregroundCardResults = new Map(this.state.foregroundCardResults.set(card.id, cardResult));
          this.setState({foregroundCardResults});

          // highlight the new card if applicable
          this.highlightAndScrollToFocusedCard();

          const reportName = this.getReportName(reportCard?.bookmark?.type);

          if (this.cardSeenWatcher.wasSeen(card.id)) {
            await seenLoadAcquirePromise;
          }
          spinnerTime = spinnerStart !== null ? Math.round(diffFromNow(spinnerStart)) : 0;
          const trackingParams = {
            ...getBookmarkTrackingParams(reportCard.bookmark, reportCard.bookmark_metadata),
            ...getCardTrackingParams({
              card,
              metricsResource: this.props.metrics,
              behaviorsResource: this.props.behaviors,
              currentProject: this.getCurrentProject(),
            }),
            ...getCommonDashboardTrackingProps({
              dashboard: this.state.dashboard,
              cards: this.getAvailableCards(),
            }),
            auto_loaded_card: autoLoadAcquire,
            card_load_order_index: curCardLoadOrderIndex,
            card_order_index: curCardIndex,
            card_seen: this.cardSeenWatcher.wasSeen(card.id),
            isBenchmark: isBenchmarkCard(card),
            latency: cardResult?.queryResponseTimeMs,
            spinnerTime,
            status_code: 200,
            tab: reportName,
            useQueryApiCache,
          };
          this.emitTrackEvent({
            eventName: TrackingEvent.ReportQuery,
            eventPrefix: WEB_DASH_PREFIX,
            props: trackingParams,
          });
        } catch (err) {
          const error: TODO = err;
          await this.handleCardQueryError(card, error);
        } finally {
          this.queryApiForegroundPool.release();
        }

        if (
          !this.state.tvModeEnabled &&
          useQueryApiCache &&
          isDashboardCard(card) &&
          this.resultsOutsideRefreshTtl(
            cardResult?.queryLastUpdated,
            cardResult?.fromDate,
            cardResult?.toDate,
            reportCard?.bookmark?.params?.sections?.time?.[0]?.unit,
          )
        ) {
          // If the results are stale, kick off a background poll to refresh the card.
          this.repollCardInBackground(card);
        }
      }
    }
  }

  /**
   * Queues up a refresh for a stale dashboard card. Assumes card has been loaded in the foreground at least once.
   * @param card Dashboard card to be loaded in the background
   */
  private async queryCardInBackground(card: DashboardCard) {
    // Abort any pending background queries for this card otherwise acquire will fail.
    if (this.backgroundQueryAbortControllers.has(card.id)) {
      this.backgroundQueryAbortControllers
        .get(card.id)
        .abort(new DOMException(`Card background query aborted.`, `AbortError`));
    }
    this.queryApiBackgroundPool.abortPendingKey(card.id);

    if (await this.queryApiBackgroundPool.acquire(card.id, () => this.getCardDistanceFromViewport(card.id))) {
      try {
        let abortSignal: AbortSignal = null;
        if (AbortController) {
          // check if browser supports AbortController
          const abortController = new AbortController();
          this.backgroundQueryAbortControllers.set(card.id, abortController);
          abortSignal = abortController.signal;
        }

        // When creating a link to a given report, it should reflect current workspace[dataview] ID
        // Not the workspace where it is originally created.
        const currentWorkspaceCard = this.convertToCurrentWorkspaceCard(card);

        const dataGroupsPromise = (async (): Promise<DataGroup[]> => {
          await this.loadGlobalInfoOnce();
          return this.props.context.dataGroups;
        })(); // async, don't wait

        let cardResult = getCardResultIfInvalid(currentWorkspaceCard, this.state.filters);
        if (this.isCompanyKPITemplateDashboard) {
          await this.formatKPICardResult(card, cardResult, dataGroupsPromise);
        }
        const currentProject = this.getCurrentProject();
        if (!cardResult) {
          // Removing time filters for benchmark due to x-axis(Day, Week etc) auto adjusting depending on selection
          const timeFilterOverride = isBenchmarkCard(card) ? null : this.state.timeFilter;
          const start = getNow();
          const resultsPromise = queryCanonicalBookmark({
            dashboard: this.state.dashboard,
            currentProject,
            card: currentWorkspaceCard,
            location: this.props.location,
            globalFilters: this.state.filters,
            globalBreakdowns: this.getBreakdowns(),
            timeFilterOverride,
            useQueryCache: true,
            abortSignal,
            queryOrigin: this.state.tvModeEnabled ? DashboardQueryOrigin.TvMode : DashboardQueryOrigin.Standard,
            metricsResource: this.props.metrics,
            behaviorsResource: this.props.behaviors,
            isBackgroundRepoll: true,
          });

          const bookmarkQueryMetadata = await queryCanonicalBookmark({
            dashboard: this.state.dashboard,
            currentProject,
            card: currentWorkspaceCard,
            location: this.props.location,
            globalFilters: this.state.filters,
            globalBreakdowns: this.getBreakdowns(),
            timeFilterOverride,
            useQueryCache: true,
            abortSignal,
            queryOrigin: this.state.tvModeEnabled ? DashboardQueryOrigin.TvMode : DashboardQueryOrigin.Standard,
            queryMetadata: true,
            metricsResource: this.props.metrics,
            behaviorsResource: this.props.behaviors,
            isBackgroundRepoll: true,
          });

          this.backgroundQueryResults.set(card.id, resultsPromise);
          const results = await resultsPromise;

          const queryResponseTimeMs = Math.round(diffFromNow(start));
          cardResult = createCardResults({
            card: currentWorkspaceCard,
            results,
            queryResponseTimeMs,
          });

          cardResult = {...cardResult, cardEventsInfo: bookmarkQueryMetadata};
        }
        this.backgroundQueryAbortControllers.delete(card.id);
        this.backgroundCardResults.set(card.id, cardResult);

        // TODO go/j/AE-904: add card tracking for background polls.
      } catch (_) {
        // Do nothing if a background poll fails. The card will be retried later.
      } finally {
        this.queryApiBackgroundPool.release();
      }
    }
  }

  /**
   * Determine whether to auto-refresh a card. TTLs should be kept roughly in sync with Query API.
   *
   * @param computedAt the date the results were computed at.
   * @param fromDate the fromDate of the report.
   * @param toDate the toDate of the report.
   * @param unit the time granularity unit of the report.
   * @returns true if we should auto-refresh this report, false otherwise.
   */
  resultsOutsideRefreshTtl = (computedAt: string, fromDate?: string, toDate?: string, unit?: string): boolean => {
    if (!computedAt) {
      return false;
    }

    let numHours = null;
    let numDays = null;
    if (fromDate && toDate) {
      numHours = moment(toDate).diff(moment(fromDate), `hours`);
      numDays = moment(toDate).diff(moment(fromDate), `days`);
    }

    // The following should be kept *loosely* in sync with the values in Query API at:
    // api/version_2_0/cache/arb_query_cache.py#_dashboard_refresh_interval
    // It improves the user experience and reduces the amount of unnecessary polling
    // when the values sync up nicely. In Query API if the cached result is over these
    // TTLs then it will issue a background refresh request.
    // However we actually don't want to background refresh automatically TOO much as
    // this will be disruptive to the UX, so these values are larger than what's in
    // Query API.
    let refreshTtl = 2 * SECONDS_PER_HOUR;
    if (numHours !== null && numDays !== null) {
      if (numDays <= 31) {
        refreshTtl = 30 * SECONDS_PER_MINUTE;
      } else if (numDays <= 62) {
        refreshTtl = 2 * SECONDS_PER_HOUR;
      } else if (numDays <= 186) {
        refreshTtl = 6 * SECONDS_PER_HOUR;
      } else if (numDays <= 366) {
        refreshTtl = 6 * SECONDS_PER_HOUR;
      } else {
        refreshTtl = 12 * SECONDS_PER_HOUR;
      }
    }

    if (unit === `minute`) {
      refreshTtl = Math.floor(refreshTtl / 4);
    } else if (unit === `hour`) {
      refreshTtl = Math.floor(refreshTtl / 2);
    }

    const ageOfCachedResult: number = moment().diff(moment(computedAt), `seconds`);
    return ageOfCachedResult >= refreshTtl;
  };

  trackBackgroundRepollStats = (card: DashboardCard, retryCount: number, elapsed: number, repollSucceeded: boolean) => {
    const trackingProps: Record<string, any> = {
      ...getBookmarkTrackingParams(card.bookmark, card.bookmark_metadata),
      ...getCardTrackingParams({
        card,
        metricsResource: this.props.metrics,
        behaviorsResource: this.props.behaviors,
        currentProject: this.getCurrentProject(),
      }),
      ...getCommonDashboardTrackingProps({
        dashboard: this.state.dashboard,
        cards: this.getAvailableCards(),
      }),
      elapsed,
      'repoll succeeded': repollSucceeded,
      'retry count': retryCount,
      tab: this.getReportName(card.bookmark?.type),
      dashboard_card_id: card.id,
      dashboard_id: this.state.dashboard.id,
    };
    if (trackingProps.project_id) {
      trackingProps.distinct_id = trackingProps.project_id;
    }
    this.emitTrackEvent({
      eventName: TrackingEvent.DashboardCardRepolled,
      eventPrefix: PERFORMANCE_EVENT_PREFIX,
      props: trackingProps,
    });
  };

  /**
   * Repolls a card in the background. Retries if the query fails or if the results are stale.
   * @param card
   */
  private async repollCardInBackground(card: DashboardCard) {
    // We only allow one active repoll at a time
    if (this.backgroundRepollAbortControllers.has(card.id)) {
      this.backgroundRepollAbortControllers
        .get(card.id)
        .abort(new DOMException(`Repoll aborted to start a new repoll.`, `AbortError`));
      this.backgroundQueryAbortControllers.delete(card.id);
    }

    let cardBackgroundRepollState = new Map(
      this.state.cardBackgroundRepollState.set(card.id, CardBackgroundRepollState.Pending),
    );
    this.setState({
      cardBackgroundRepollState,
    });

    // Use an abort controller to manage the entire repoll process across multiple background
    // queries.
    let abortSignal: AbortSignal = null;
    if (AbortController) {
      // check if browser supports AbortController
      const abortController = new AbortController();
      this.backgroundRepollAbortControllers.set(card.id, abortController);
      abortSignal = abortController.signal;
    }

    const repollingStart = getNow();
    for (let retryCount = 0; retryCount < MAX_BACKGROUND_POLL_RETRIES; retryCount++) {
      // Sleep for an exponentially increasing amount of time before retrying.
      const sleepResponse = await sleep(
        BASE_RETRY_BACKOFF_MS * Math.pow(EXPONENTIAL_BACKOFF_FACTOR, retryCount),
        abortSignal,
      ).catch((_) => {
        return `sleep aborted`;
      });
      if (sleepResponse === `sleep aborted`) {
        if (abortSignal.reason === `unmounting`) {
          // The component is unmounting. Do not update state and immediately return.
          return;
        } else {
          // Break out of loop to handle the cancelled repoll.
          break;
        }
      }

      await this.queryCardInBackground(card);
      const cardResult = this.backgroundCardResults.get(card.id);
      const foregroundCardResult = this.state.foregroundCardResults.get(card.id);

      if (
        !this.resultsOutsideRefreshTtl(
          cardResult?.queryLastUpdated,
          cardResult?.fromDate,
          cardResult?.toDate,
          card?.bookmark?.params?.sections?.time?.[0]?.unit,
        ) &&
        moment(cardResult?.queryLastUpdated) > moment(foregroundCardResult?.queryLastUpdated)
      ) {
        // Auto-replace the card with the fresher results.
        this.promoteBackgroundCardResultsToForeground(card);
        // Exit early upon success.
        this.trackBackgroundRepollStats(card, retryCount, Math.round(diffFromNow(repollingStart)), true);
        this.backgroundQueryAbortControllers.delete(card.id);
        return;
      }
    }

    // Cancel the repoll since none of the re-queries succeeded at retrieving fresh results.
    this.trackBackgroundRepollStats(card, MAX_BACKGROUND_POLL_RETRIES, Math.round(diffFromNow(repollingStart)), false);
    cardBackgroundRepollState = new Map(
      this.state.cardBackgroundRepollState.set(card.id, CardBackgroundRepollState.None),
    );
    this.setState({
      cardBackgroundRepollState,
    });
    this.backgroundQueryAbortControllers.delete(card.id);
  }

  private promoteBackgroundCardResultsToForeground(card: CardOnDashboard) {
    const cardBackgroundRepollState = new Map(
      this.state.cardBackgroundRepollState.set(card.id, CardBackgroundRepollState.None),
    );
    // `foregroundCardResults` and `foregroundQueryResults` still partially adhere to a legacy
    // format where the state is mutated leading to fragility issues. To avoid a race condition
    // with setState and another consumer of these maps, we directly mutate the maps here.
    // TODO AE-909: replace all state mutations with proper scoped state updates.
    const foregroundCardResults = new Map(
      this.state.foregroundCardResults.set(card.id, this.backgroundCardResults.get(card.id)),
    );
    const foregroundQueryResults = new Map(
      this.state.foregroundQueryResults.set(card.id, this.backgroundQueryResults.get(card.id)),
    );

    // Manually call setState to trigger a refresh for the updated state maps associated with visible UI components.
    this.setState({
      cardBackgroundRepollState,
      foregroundCardResults,
      foregroundQueryResults,
    });
  }

  private async handleCardQueryError(card: CardOnDashboard, error: TODO) {
    const reportCard = card as DashboardCard | BenchmarkCard;
    let reportName = null;
    try {
      reportName = this.getReportName(reportCard?.bookmark?.type);
    } catch (reportNameErr) {
      console.error(reportNameErr);
    }
    if (error.name !== `AbortError`) {
      // no need to log a permission denied or unauthenticated to rollbar
      const isUnauthenticatedError = error?.message && error.message.includes(StatusCode.Unauthenticated.toString());
      if (!isDataClassificationError(error?.message) && !isUnauthenticatedError) {
        this.props.context.trackError(error);
      } else {
        console.error(error);
      }
      let responseBody = null;
      try {
        responseBody = error?.response ? await error.response.json() : null;
      } catch (jsonError) {
        await this.handleCardQueryError(card, jsonError);
        return;
      }
      // TODO go/j/AE-909: replace state mutation with immutable state updates.
      this.state.foregroundQueryResults.delete(card.id);
      this.state.foregroundCardResults.set(card.id, {
        invalidBookmark: false,
        dataGroupNotSupported: false,
        notSupported: false,
        queryLastUpdated: ``,
        queryResponseTimeMs: 0,
        chartLoaded: false,
        chartParams: null,
        chartError: responseBody
          ? {...pick(responseBody, [`error`, `message`, `data`]), status: error.response.status}
          : {error: `Unexpected error`},
        timeoutError: error.response && error.response.status === 504,
      });
    }
    const trackingParams = {
      ...getBookmarkTrackingParams(reportCard.bookmark, reportCard.bookmark_metadata),
      ...getCardTrackingParams({
        card,
        metricsResource: this.props.metrics,
        behaviorsResource: this.props.behaviors,
        currentProject: this.getCurrentProject(),
      }),
      ...getCommonDashboardTrackingProps({
        dashboard: this.state.dashboard,
        cards: this.getAvailableCards(),
      }),
      latency: null,
      status_code: error?.status_code,
      tab: reportName,
      isBenchmark: isBenchmarkCard(card),
    };
    this.emitTrackEvent({
      eventName: TrackingEvent.ReportQuery,
      eventPrefix: WEB_DASH_PREFIX,
      props: trackingParams,
    });
    // TODO go/j/AE-909: replace all forceUpdate calls with proper scoped state updates.
    this.forceUpdate();
  }

  private emitTrackEvent({eventName, eventPrefix, props}: {eventName: TrackingEvent; eventPrefix: string; props: any}) {
    if (!this.containerRef.current) {
      return;
    }
    emitInfraTrackEvent(this.containerRef.current, {
      eventName,
      eventPrefix,
      props,
    });
  }

  private abortAllCardQueries() {
    this.queryApiForegroundPool.abortPending();
    this.queryApiBackgroundPool.abortPending();
    this.cardSeenWatcher.abortAllWait();
    // Abort all pending foreground queries.
    for (const abortController of this.foregroundQueryAbortControllers.values()) {
      abortController.abort(new DOMException(`Card foreground query aborted.`, `AbortError`));
    }

    // Abort all active background repolls.
    for (const abortController of this.backgroundRepollAbortControllers.values()) {
      abortController.abort(new DOMException(`Card background repoll aborted.`, `AbortError`));
    }

    // Abort all pending background queries.
    for (const abortController of this.backgroundQueryAbortControllers.values()) {
      abortController.abort(new DOMException(`Card background query aborted.`, `AbortError`));
    }
  }

  private abortCardQueries(cards: Iterable<CardOnDashboard>) {
    for (const card of cards) {
      // Abort any pending foreground queries for this card.
      if (this.foregroundQueryAbortControllers.has(card.id)) {
        this.foregroundQueryAbortControllers
          .get(card.id)
          .abort(new DOMException(`Card foreground query aborted.`, `AbortError`));
      }
      this.queryApiForegroundPool.abortPendingKey(card.id);
      this.cardSeenWatcher.abortWait(card.id);

      // Abort any active background repolls for this card.
      if (this.backgroundRepollAbortControllers.has(card.id)) {
        this.backgroundRepollAbortControllers
          .get(card.id)
          .abort(new DOMException(`Card background repoll aborted.`, `AbortError`));
      }

      // Abort any pending background queries for this card.
      if (this.backgroundQueryAbortControllers.has(card.id)) {
        this.backgroundQueryAbortControllers
          .get(card.id)
          .abort(new DOMException(`Card background query aborted.`, `AbortError`));
      }
      this.queryApiBackgroundPool.abortPendingKey(card.id);
    }
  }

  /**
   * Aborts pending card queries and deletes the card and query results from the state.
   * @param subsetOfCards If provided, only clear the cards in the subset. Otherwise, clears all cards in an optimized fashion.
   */
  private clearCards(subsetOfCards: Iterable<CardOnDashboard> = null) {
    const cardBackgroundRepollState = new Map(this.state.cardBackgroundRepollState);
    let foregroundCardResults: Map<DashboardCardId, DashboardCardResult>;
    let foregroundQueryResults: Map<DashboardCardId, Promise<BaseQueryResultsType | BaseQueryErrorType>>;

    if (subsetOfCards !== null) {
      this.abortCardQueries(subsetOfCards);
      foregroundCardResults = new Map(this.state.foregroundCardResults);
      foregroundQueryResults = new Map(this.state.foregroundQueryResults);
      for (const card of subsetOfCards) {
        cardBackgroundRepollState.set(card.id, CardBackgroundRepollState.None);
        foregroundCardResults.delete(card.id);
        foregroundQueryResults.delete(card.id);
        this.backgroundCardResults.delete(card.id);
        this.backgroundQueryResults.delete(card.id);
      }
    } else {
      this.abortAllCardQueries();
      cardBackgroundRepollState.clear();
      // Optimization: clearing/recreating maps is more performant than iterating over all cards to delete
      foregroundCardResults = new Map<DashboardCardId, DashboardCardResult>();
      foregroundQueryResults = new Map<DashboardCardId, Promise<BaseQueryResultsType | BaseQueryErrorType>>();
      this.backgroundCardResults.clear();
      this.backgroundQueryResults.clear();
    }

    // Reminder: setState is asynchronous and performs shallow equality (address-based) comparison via `===` operator
    this.setState({cardBackgroundRepollState, foregroundCardResults, foregroundQueryResults});
  }

  private getCardsWithBookmarkId(bookmarkId: number) {
    const cards = Array.from(this.state.cards.values());
    return cards.filter((card) => isDashboardCard(card) && card.bookmark.id === bookmarkId) as DashboardCard[];
  }

  getContentActionTrackingParam(card: CardOnDashboard) {
    let trackingParams = {
      ...getCardTrackingParams({
        card,
        metricsResource: this.props.metrics,
        behaviorsResource: this.props.behaviors,
        currentProject: this.getCurrentProject(),
      }),
      ...getCommonDashboardTrackingProps({
        dashboard: this.state.dashboard,
        cards: this.getAvailableCards(),
      }),
      ...this.myDashboardTrackingProps(),
    };
    if (isDashboardCard(card)) {
      trackingParams = {
        ...trackingParams,
        ...getBookmarkTrackingParams((card as DashboardCard).bookmark, (card as DashboardCard).bookmark_metadata),
      };
    }
    return trackingParams;
  }

  getSparkCardComponentOptions = (cellId: string): SparkCardProps => {
    return {
      location: this.props.location,
      dashboard: this.state.dashboard,
      getReportUrl: (bookmark) => {
        const reportManager = this.getReportManagerFromType(bookmark.type);
        const {projectId, workspaceId} = this.props.context;
        const reportUrlBase = getReportUrl(`boards/`, {projectId, workspaceId});
        const reportUrl = `${reportUrlBase}#${serializeDashboardUrlHashParams(
          this.getUrlDataFromState({
            editorCardEditedBookmark: bookmark,
            editorCardId: generateStagedCardId(reportManager.insightsReportMode || reportManager.bookmarkType),
          }),
        )}`;
        return reportUrl;
      },
      onEditReport: (bookmark) => {
        const reportManager = this.getReportManagerFromType(bookmark.type);

        this.setState({
          editorCardAiCellId: cellId,
          editorCardEditedBookmark: bookmark,
        });

        this.openNewReportInEditor(reportManager);
      },
      onSaveReport: (bookmark) => {
        this.swapSparkCardWithReport(cellId, bookmark);
      },
    };
  };

  /**
   * Replaces a Spark cell with a new report card.
   */
  swapSparkCardWithReport = (cellId: string, bookmark: DisplayBookmark) => {
    // we have to reach into grid layout here because it's the only place that knows
    // the current client state of the board. ai cards are ephemeral so they don't exist upstream.

    // replace the ai card and emit a new report card in its place
    const replaced = this.gridEl?.replaceEphemeralCell(cellId, {
      contentType: ContentType.Report,
      contentParams: {
        bookmark: {
          ...bookmark,
          params: JSON.stringify(bookmark.params),
        },
      },
    });

    if (!replaced) {
      return;
    }

    // add new spark if one isn't already open
    const hasAnySpark = this.gridEl?.hasContentType(ContentType.Spark);

    if (!hasAnySpark) {
      // try to add Spark to the right of the new report, otherwise in a new row
      this.gridEl?.handleContentAdded(
        replaced.rowId,
        {
          actionType: ContentActionType.Create,
          contentType: ContentType.Spark,
        },
        replaced.cellIdx + 1,
        /**
         * We want the card to be half the size of the row.
         * Each card's width is evenly distributed once added.
         * To achieve this, the row should have a maximum of 2 cards on the row after adding the new card.
         * So, there should be only one card before adding, which means 3 empty spaces.
         */
        3,
      );
    }
  };

  // For some reason sometimes the `card` argument gets unsynced of this.state.cards.get(card.id).
  // Use `this.state.cards.get(card.id)` to get the most recent version of the card
  // TODO opslead
  getDashboardCardComponentOptions = (card: CardOnDashboard): CardJsxProps => {
    const {foregroundCardResults, timeFilter, tvModeEnabled} = this.state;
    const hasEditPermissions = this.canEditActiveDashboard();
    let cardResult: DashboardCardResult;
    let trackingParams = {};
    let dashboardCard;
    let cardParams;
    if (!isTextOrMediaCard(card)) {
      dashboardCard = card as DashboardCard | BenchmarkCard;
    }
    trackingParams = this.getContentActionTrackingParam(card);
    let options;
    switch (card.type) {
      case ContentType.Text:
      case ContentType.Media:
      case ContentType.Dashboard:
      case ContentType.Benchmark: {
        options = {
          isPublicMode: false,
          hasEditPermissions,
          isAutofocused: this.getFocusedCardId() === card.id && this.state.focusedCard?.isNew,
          isHighlighted: this.getFocusedCardId() === card.id,
          isTvMode: tvModeEnabled,
        } as GetTextCardParamsOptions;
        if (card.type === ContentType.Text) {
          cardParams = getTextCardParams(card as TextCard, options);
        } else if (card.type === ContentType.Media) {
          cardParams = getMediaCardParams(card as MediaCard, options);
        } else if (card.type === ContentType.Dashboard) {
          const url = getReportUrl(`boards#id=${card.content_id}`, {
            projectId: this.props.context.projectId,
            workspaceId: this.props.context.workspaceId,
          });
          cardParams = getNestedDashboardCardParams(card as NestedDashboardCard, {
            ...options,
            reportUrl: url,
            currentDashboardHierarchyTreeHeight: getDashboardHierarchyTreeHeight(this.state.dashboard),
            descendantDashboardIds: getDashboardDescendants(this.state.dashboard),
            isCurrentDashboardNested: this.state.dashboard.parent_dashboard_id !== null,
          });
        } else if (card.type === ContentType.Benchmark) {
          cardResult = foregroundCardResults.get(card.id) ?? {
            invalidBookmark: false,
            dataGroupNotSupported: false,
            notSupported: false,
            queryLastUpdated: ``,
            queryResponseTimeMs: 0,
            chartLoaded: false,
            chartParams: null,
          };
          cardParams = getBenchmarkCardParams(card as BenchmarkCard, cardResult, options);
          cardParams.projectTimezone = this.props.context.projectTimezone;
          cardParams.utcOffset = this.props.context.utcOffset;
          cardParams.isStagedMode = this.state.dashboard.generation_type === DashboardType.StagedTemplate;
          break;
        }
        break;
      }
      case ContentType.Report:
      case ContentType.ReportLink: {
        // When creating a link to a given report, it should reflect current workspace[dataview] ID
        // Not the workspace where it is originally created.
        const currentWorkspaceCard = this.convertToCurrentWorkspaceCard(card);
        cardResult = foregroundCardResults.get(card.id) ?? {
          invalidBookmark: false,
          dataGroupNotSupported: false,
          notSupported: false,
          queryLastUpdated: ``,
          queryResponseTimeMs: 0,
          chartLoaded: false,
          chartParams: null,
        };
        const chartOptions = getChartOptions({
          bookmark: dashboardCard.bookmark,
          chartOptionsOverride: dashboardCard.chart_options,
          metricsResource: this.props.metrics,
          behaviorsResource: this.props.behaviors,
          currentProject: this.getCurrentProject(),
        });

        trackingParams = {
          ...getBookmarkTrackingParams(dashboardCard.bookmark, dashboardCard.bookmark_metadata),
          ...getCardTrackingParams({
            card,
            metricsResource: this.props.metrics,
            behaviorsResource: this.props.behaviors,
            currentProject: this.getCurrentProject(),
          }),
          ...getCommonDashboardTrackingProps({
            dashboard: this.state.dashboard,
            cards: this.getAvailableCards(),
          }),
          ...this.myDashboardTrackingProps(),
          'card id': card.id,
        };
        options = {
          dashboardMode: AppMode.Standard,
          isAutofocused: false,
          isEmbedMode: this.state.isEmbedMode,
          isHighlighted: this.getFocusedCardId() === card.id,
          isDefaultChartType: !dashboardCard.chart_options,
          isLinked: card.type === ContentType.ReportLink,
          hasEditPermissions,
          chartOptions,
          dataGroups: this.props.context.dataGroups,
          enableCustomAlerts: !!this.getCurrentProject()?.getFeatureGate(FeatureGate.AllowCustomAlerts),
          projectId: this.props.context.projectId,
          projectTimeZone: this.props.context.projectTimezone,
          projectAsOfDate: this.getProjectAsOfDate(),
          reportUrl: getBookmarkUrlPath(currentWorkspaceCard.bookmark, {
            bookmarkMetadata: currentWorkspaceCard.bookmark_metadata,
            chartType: chartOptions?.type,
            originDashboard: this.state.dashboard,
            filters: getActiveBoardFilters(this.state.filters),
            breakdowns: this.getBreakdowns(),
            timeFilter,
          }),
          timeFilter: JSON.stringify(timeFilter),
          isTvMode: tvModeEnabled,
          UTCOffset: this.props.context.utcOffset,
        } satisfies GetDashboardCardParamsOptions;
        const cardRepollState: CardBackgroundRepollState = this.state.cardBackgroundRepollState.get(card.id);
        cardParams = getDashboardCardParams(dashboardCard, cardResult, options, cardRepollState);
        break;
      }
      default:
        break;
    }

    // note that cards still use panel, you see all of the panel-like attr/on/hook here
    return {
      hook: {
        insert: ({elm}) => {
          // no need to watch if the card was already seen
          if (!this.cardSeenWatcher.wasSeen(card.id)) {
            this.cardSeenWatcher.watch(card.id, elm as Element);
          }

          // Start spans when dashboard cards are loaded into view.
          // Only captures first load and not refreshes or changed filters
          if ((elm as any).params?.layoutCardType === `report`) {
            const cardId = (elm as any).params.cardId;
            const [cardSpan] = spans.getOrCreateSpan({id: getCardQuerySpanId(cardId), name: `report-query`});
            cardSpan.setAttributes({
              dashboardCardId: cardId,
            });
          }
        },
        destroy: () => {
          // when moving a card to a new row, a new card instance is rendered from scratch.
          // this prevents the sync issue of having the old card destroyed after the new card is inserted.
          if (!this.state.cards.has(card.id)) {
            this.cardSeenWatcher.unwatch(card.id);
          }
        },
      },
      params: {
        ...cardParams,
        // If we're in fast edit, disallow (mainly) highchart line-chart to re-flow on re-render
        allowChartUpdate: !this.showFastEditReport(),
        card,
        bookmark: dashboardCard?.bookmark,
        dataGroups: this.props.context.dataGroups,
        chartParams: cardResult?.chartParams,
        chartEventsInfo: cardResult?.cardEventsInfo,
        globalBreakdowns: this.getBreakdowns(),
        globalFilters: getActiveBoardFilters(this.state.filters),
        recentDashboardIds: this.getRecentDashboardIds(),
      },
      on: {
        editreport: ({detail: {clickEvent}}) => {
          this.props.context.track(TrackingEvent.ClickedReportLink, trackingParams);
          if (!dashboardCard.bookmark) {
            return;
          }
          const bookmarkType = dashboardCard.bookmark.type;
          const hasReportManager = !!this.getReportManagerFromType(bookmarkType);

          if (hasReportManager) {
            this.setState({isFasteditPageload: false});
            clickEvent.preventDefault();
            clickEvent.stopPropagation();
            this.setState({editorCardId: dashboardCard.id, reportEditorSession: this.state.reportEditorSession + 1});
            this.addCardToRecentsSearchList(dashboardCard.id);
            this.updateUrlFromState(
              {
                editorCardId: dashboardCard.id,
              },
              {replace: false},
            );
          }
        },
        update: async (ev) => {
          const {t} = this.getLocalizationStore();
          const {title, params, requery, saveBoardOverrides} = ev.detail;
          this.props.selectorController.onSaveChangesStart();

          try {
            const stateCard = this.state.cards.get(card.id) as DashboardCard;

            if (saveBoardOverrides) {
              // bulk updates in boards is currently limited to legend color overrides
              // this API will be expanded to include bookmark patches to apply
              // across a list of bookmarks.
              // For instance: selecting a few cards and applying query changes to them
              await this.saveBoardLegendOverrides();
            } else {
              await this.saveReport(
                {
                  dashboardCardId: card.id,
                  editedBookmark: {
                    type: stateCard.bookmark.type,
                    name: title ?? stateCard.title,
                    params: params ?? stateCard.bookmark.params,
                  },
                },
                /** intentionally disable undo/redo for legend bulk updates */
                {requery},
              );
            }
          } catch (error) {
            reporter.error(error);
            let message = t(`boards.board-save-error`);

            if (error.name === `FieldStringTooLongError`) {
              message = t(`boards.board-title-save-error`, {maxLength: error.maxLength});
            }

            addToast({message, type: ToastType.ERROR});
          } finally {
            this.props.selectorController.debouncedStopSavingChanges();
          }
        },
        mouseenter: () => {
          if (card.id === this.getFocusedCardId()) {
            // unhighlight the dashboard card
            this.setState({focusedCard: null});
          }
        },
        refresh: (ev: CustomEvent) => {
          const stateCard = this.state.cards.get(card.id) as DashboardCard;
          this.clearCards([stateCard]);
          this.loadCard(stateCard, {useQueryApiCache: ev.detail?.useQueryApiCache ?? false});
        },
        overflowmenuopen: () => this.props.context.track(TrackingEvent.OpenOverflowMenu, trackingParams),
        edittextcard: (ev) => {
          const {markdown} = ev.detail;
          this.editTextCard(card.id, markdown);
        },
        changechartoptions: (ev) => {
          this.changeCardChartType(card.id, ev.detail.chartOptions);
        },
        charttypemenuopen: () => {
          this.props.context.track(TrackingEvent.OpenChartTypeMenu, trackingParams);
        },
        copylink: async ({detail}) => {
          const {url} = detail;
          let errored = false;
          try {
            await copy(`${getLocation().origin}${url}`);
            this.props.context.track(TrackingEvent.CopyReportUrl, trackingParams);
          } catch (err) {
            reporter.error(err as Error);
            errored = true;
          }
          this.showToast({
            text: errored ? `Could not copy URL` : `Copied report URL to clipboard!`,
            type: errored ? ToastType.ERROR : ToastType.SUCCESS,
          });
        },
        trackcard: ({detail}) => {
          const {eventName, properties} = detail;
          this.props.context.track(eventName, {...properties, ...trackingParams});
        },

        updatemediacard: (ev) => {
          const {mediaType, service, path} = ev.detail;
          this.props.selectorController.onSaveChangesStart();
          const mediaCard = this.state.cards.get(card.id) as MediaCard;
          mediaCard.media_type = mediaType;
          mediaCard.service = service;
          mediaCard.path = path;
          try {
            // Use the common method to save any dashboard changes.
            // If we want to save the media card only, and not the whole layout,
            // follow the model of patchUpdateTextCardFields which calls dashboard_text_card_entry
            const contentAction = {
              actionType: ContentActionType.Update,
              contentId: mediaCard.content_id,
              contentType: mediaCard.type,
              contentParams: {
                media_type: UserMediaToDashboardMediaMap[mediaType],
                service,
                path,
              },
            };
            this.saveChangesOnDashboard({contentAction});
          } finally {
            this.props.selectorController.debouncedStopSavingChanges();
          }
        },
      },
    };
  };

  canUseCohorts() {
    return (
      this.getCurrentProject()?.hasAccessToReport(RestrictedReport.Cohorts) ||
      this.getCurrentProject()?.hasFeatureFlag(ProjectFeatureFlag.Cohorts)
    );
  }

  canUseDataGroups() {
    return (
      this.getCurrentProject()?.hasFeatureFlag(ProjectFeatureFlag.B2BAnalytics) ||
      !!this.getCurrentProject()?.getFeatureGate(FeatureGate.B2BAnalytics)
    );
  }

  getOnboardingDashboardId() {
    return this.props.context.workspaceOnboardingDashboardId || this.getCurrentProject()?.onboardingDashboardID;
  }

  getProjectAsOfDate() {
    return this.getCurrentProject()?.projectAsOfDate ?? null;
  }

  private getReportManagerFromType(bookmarkType: BookmarkType) {
    let insightsReportMode = bookmarkType === BookmarkType.Funnels ? BookmarkType.Funnels : null;
    if (bookmarkType === BookmarkType.Retention) {
      insightsReportMode = BookmarkType.Retention;
    }
    return reportManagerFromType({
      bookmarkType,
      projectId: this.props.context.projectId,
      workspaceId: this.props.context.workspaceId,
      location: this.props.location,
      insightsReportMode,
      dashboardOverrides: this.getEditorBookmarkOverrides(),
    });
  }

  private openNewReportInEditor(reportManager: ReportManager) {
    this.setState(
      {
        editorCardId: generateStagedCardId(reportManager.insightsReportMode || reportManager.bookmarkType),
      },
      () => {
        this.updateUrlFromState();
      },
    );
  }

  private async createDashboard(stagedDashboard: StagedDashboard, globalAccessType?: GlobalAccessType) {
    const fields = {
      ...maybeAddGlobalAccessTypeParam(stagedDashboard.serialize(), globalAccessType, globalAccessType),
    };

    const dashFields = omit(fields, [`cards`, `card_order`]);
    const dashboard = await this.props.selectorController.createDashboard({title: fields.title, fields: dashFields});
    try {
      if (stagedDashboard.isDuplicating()) {
        this.props.context.track(TrackingEvent.DuplicateDashboard, {
          ...stagedDashboard.toTrackingData(),
          ...this.myDashboardTrackingProps(stagedDashboard.duplicate),
        });
      } else {
        this.props.context.track(TrackingEvent.CreateDashboard, {
          ...stagedDashboard.toTrackingData(),
        });
      }
    } catch (err) {
      reporter.error(err, `Failed to track create dashboard event`);
    }

    this.insertDashboardSidenavItem(dashboard, SidenavSectionIndex.Yours, SidenavSectionTrackingName.Yours);
    this.setState({
      hasNoDashboards: false,
      dashboardModified: false,
      editorCardBookmarkURLSlug: null,
      editorCardEditedBookmark: null,
    });
    this.props.navigate(
      `#${serializeDashboardUrlHashParams({
        ...this.getUrlDataFromState(),
        dashboardId: dashboard.id,
      })}`,
    );
    return dashboard;
  }

  private async createRCADashboard(rcaSourceId, rcaSourceData) {
    try {
      const dashboardResponse = await this.props.selectorController.createRCADashboard(rcaSourceId, rcaSourceData);
      this.setState({hasNoDashboards: false, dashboardModified: false});
      this.props.navigate(
        `#${serializeDashboardUrlHashParams({
          ...this.getUrlDataFromState(),
          dashboardId: dashboardResponse.id,
        })}`,
      );
    } catch (error) {
      addToast({message: `Failed to generate the Root Cause Analysis board`, type: ToastType.ERROR});
      console.error(error);
    }
  }

  private async initializeBlueprintBoard(payload: BlueprintsRequest) {
    const dashboardResponse = await this.props.selectorController.createBlueprintDashboard(payload);

    // TODO: this is legacy code. We were checking this even when the types doesn't match
    // according to the ts types, `status` is a number and `statusCode` doesn't exist
    if (`error` in dashboardResponse || dashboardResponse.status === `error` || dashboardResponse.statusCode >= 400) {
      return dashboardResponse;
    }

    this.insertDashboardSidenavItem(
      dashboardResponse.dashboard,
      SidenavSectionIndex.Yours,
      SidenavSectionTrackingName.Yours,
    );
    if (
      this.state.dashboard &&
      !(getLocation().href.includes(`show-template-selector`) || this.state.openCreateFoundationalTemplateModal)
    ) {
      // If a dashboard exists at this point, it is blank and should be deleted
      await this.props.selectorController.deleteDashboard(this.state.dashboard.id);
      this.deleteSidenavItem(this.state.dashboard.id.toString());
    }

    // If a user was directed to the modal using a direct link,
    // we need to remove the query param when user creates a template generated dashboard
    if (getLocation().href.includes(`show-template-selector`)) {
      const uriParams = parseUri(getLocation().href);
      const {anchor} = uriParams;
      this.props.navigate(anchor ? `/dashboards#${anchor}` : `/dashboards`);
    }

    this.props.navigate(
      `#${serializeDashboardUrlHashParams({
        ...this.getUrlDataFromState(),
        dashboardId: dashboardResponse.dashboard.id,
      })}`,
    );

    return dashboardResponse;
  }

  async duplicateDashboard(dashboard: DashboardItemInfo) {
    const fields = {duplicate: dashboard.id};
    let newDashboard = null;

    try {
      newDashboard = await this.props.selectorController.createDashboard({fields});

      this.props.navigate(
        `#${serializeDashboardUrlHashParams({
          dashboardId: newDashboard.id,
        })}`,
      );
      this.props.context.track(TrackingEvent.DuplicateDashboard, {
        ...getCommonDashboardTrackingProps({
          dashboard,
          cards: newDashboard.cards,
          sourceTrackingProps: {crudSource: DashboardCRUDSource.Sidenav},
        }),
        ...this.myDashboardTrackingProps(dashboard.id),
      });
      this.insertDashboardSidenavItem(newDashboard, SidenavSectionIndex.Yours, SidenavSectionTrackingName.Yours);
    } catch (err) {
      reporter.error(err as Error, `Error duplicating board.`);
      this.props.onToast(
        new CustomEvent(`toast`, {
          detail: {
            text: `Error duplicating board.`,
            type: ToastType.ERROR,
            timeoutMS: 20000,
          },
        }),
      );
    }
    this.props.selectorController.debouncedStopSavingChanges();
    return dashboard;
  }

  getDashboardHistoryFields(dashboard: Dashboard, contentAction: ContentAction = null) {
    return {
      dashboard: {
        ...pick(dashboard, [`layout`, `title`, `description`, `time_filter`, `filters`, `breakdowns`]),
        contentAction,
      },
    };
  }

  private async saveChangesOnDashboard({
    dashFilters = null,
    contentAction = null,
    dashboardMetadata = null,
    updateModel = true,
    gridLayoutUpdateParams = this.state.gridLayoutUpdateParams,
  }: {
    dashFilters?: {filters?: BoardFilter[] | null; time_filter?: TimeFilter | null; breakdowns?: Segmentation[]};
    contentAction?: ContentAction;
    dashboardMetadata?: {title?: string; description?: string};
    updateModel?: boolean;
    gridLayoutUpdateParams?: GridLayoutUpdateParams;
  }) {
    this.historyManager?.disableUndoRedo();
    const originalDashboard = this.state.dashboard;
    this.props.selectorController.setIsSavingChanges(true);

    // remove non-persistent state from dash filters
    if (dashFilters?.filters) {
      dashFilters.filters = dashFilters.filters.map((filter) => omit(filter, [`activeValue`]));
    }

    let hasFailed = false;
    const fields = {
      ...(dashFilters ?? {}),
      ...dashboardMetadata,
      ...(contentAction
        ? {
            content: {
              action: contentAction.actionType,
              ...(contentAction.actionType !== ContentActionType.Create ? {content_id: contentAction.contentId} : {}),
              content_type: contentAction.contentType,
              ...(contentAction.contentParams ? {content_params: contentAction.contentParams} : {}),
            },
          }
        : null),
      ...(gridLayoutUpdateParams ? {layout: gridLayoutUpdateParams} : {}),
    };

    let updatedDashboard: Dashboard = this.state.dashboard;
    const updateStartTime = Date.now();
    try {
      updatedDashboard = await this.dashboardController.updateDashboard({fields, updateModel});

      this.historyManager?.enableUndoRedo();

      const isBoardDeletion =
        contentAction?.actionType === ContentActionType.Delete && contentAction.contentType === ContentType.Dashboard;

      const canRecordHistoryAction =
        !this.state.isHistoryActionInProgress &&
        contentAction?.actionType !== ContentActionType.Update &&
        !this.showFastEditReport() &&
        !isBoardDeletion;

      if (canRecordHistoryAction) {
        this.recordHistoryAction({contentAction, updatedDashboard, originalDashboard, dashboardMetadata, dashFilters});
      }
    } catch (error) {
      reporter.error(error as Error, `Error updating board`);
      hasFailed = true;
      if (Object.values(BadLayoutRequestErrors).includes(error.response?.error?.type)) {
        this.props.context.track(
          TrackingEvent.LayoutValidationException,
          getCommonDashboardTrackingProps({
            dashboard: this.state.dashboard,
            cards: this.getAvailableCards(),
          }),
        );
        const message = BadLayoutRequestMessages[error.response?.error?.type];
        addToast({
          message,
          type: ToastType.ERROR,
        });
      } else {
        addToast({message: `Failed to save your board changes.`, type: ToastType.ERROR});
      }
    }

    this.updateDashboardSidenavItem(updatedDashboard);
    this.updateUrlFromState({
      dashboard: updatedDashboard,
    });
    this.props.context.track(TrackingEvent.SaveChanges, {
      ...getCommonDashboardTrackingProps({
        dashboard: updatedDashboard,
        cards: this.getAvailableCards(),
      }),
      ...(contentAction
        ? {
            'Content Action': contentAction.actionType,
            'Content Id': contentAction.contentId,
            'Content Type': contentAction.contentType,
          }
        : {}),
      'Response Time ms': Date.now() - updateStartTime,
      'Has Failed': hasFailed,
      'Has Layout Update': !!gridLayoutUpdateParams,
      'Has Filter Update': !!dashFilters,
      'Has Other Dashboard Fields Update': dashboardMetadata,
    });
    this.setState({gridLayoutUpdateParams: null});
    this.props.selectorController.setIsSavingChanges(false);
    return {updatedDashboard, hasFailed};
  }

  recordHistoryAction({
    contentAction = null,
    updatedDashboard = null,
    originalDashboard = null,
    dashboardMetadata = null,
    dashFilters = null,
  }: {
    dashFilters?: {filters?: Segmentation[] | null; time_filter?: TimeFilter | null};
    contentAction?: ContentAction;
    dashboardMetadata?: {title?: string; description?: string};
    originalDashboard?: Dashboard;
    updatedDashboard?: Dashboard;
  }) {
    if (!this.historyManager.canUseUndoRedo) {
      this.setupUndoRedo();
    }
    let updateContentAction: ContentAction = null;
    let sourceContentAction: ContentAction = null;
    if (contentAction) {
      if (contentAction.actionType === ContentActionType.Move) {
        updateContentAction = contentAction;
        sourceContentAction = {...contentAction, actionType: ContentActionType.UnMove};
      } else {
        updateContentAction =
          contentAction?.actionType === ContentActionType.Delete
            ? {
                contentId: contentAction.contentId,
                contentType: contentAction.contentType,
                actionType: contentAction.actionType,
              }
            : {
                contentId: updatedDashboard[`new_content`]?.id,
                contentType: contentAction.contentType,
                actionType: ContentActionType.Undelete,
              };
        sourceContentAction = {
          ...updateContentAction,
          actionType:
            updateContentAction.actionType === ContentActionType.Delete
              ? ContentActionType.Undelete
              : ContentActionType.Delete,
        };
      }
    }

    // originalDashboardFields + sourceContentAction = updatedDashboard
    // updatedDashboardFeilds + updateContentAction = originalDashboard
    this.historyManager.applyUpdates<TODO>({
      source: this.getDashboardHistoryFields(originalDashboard, sourceContentAction),
      updates: this.getDashboardHistoryFields(updatedDashboard, updateContentAction),
      metadata: {
        isLayoutAction: !!this.state.gridLayoutUpdateParams,
        isDashboardMetadata: !!dashboardMetadata,
        isDashboardFilters: !!dashFilters,
      },
    });
  }

  openExternalDashboard(dashboardId, focusedContent = null, projectId?: number) {
    const focusedContentUrlParam = focusedContent
      ? `&card-id=${getCardIdFromContent(focusedContent.contentId, focusedContent.contentType)}`
      : ``;
    this.props.openInNewTab(
      getReportUrl(`boards/#id=${dashboardId}`, {
        projectId: projectId ?? this.props.context.projectId,
        workspaceId: projectId === undefined ? this.props.context.workspaceId : undefined,
      }) + focusedContentUrlParam,
      `_blank`,
    );
  }

  async duplicateToExternalDashboard(dashboardId, contentAction) {
    const content = this.getDuplicateContentAction(contentAction);
    const fields = {
      content: {
        action: content.actionType,
        content_type: content.contentType,
        content_params: content.contentParams,
      },
    };
    const response = await dashboardsClient.updateDashboardFields({
      projectId: this.props.context.projectId,
      dashboardID: dashboardId,
      fields,
    });

    const ctaCallBack = () => {
      this.openExternalDashboard(dashboardId, {
        contentId: response.new_content?.id,
        contentType: ContentType.ReportLink,
      });
    };
    const undoCallback = () => {
      this.historyManager.undo();
    };
    const toast = {
      cta: `Visit Board`,
      message: `Report has been linked to board "${response.title}."`,
      type: ToastType.SUCCESS,
      undoable: true,
      ctaCallback: ctaCallBack,
      undoCallback,
      undoLabel: `Undo ${generateShortcutLabel(`Z`, {modifiers: [getCommandKey()], joinStr: ` `})}`,
    };
    addToast(toast);
  }

  private showToast({type, text, cta = null, ctaCallback = null}) {
    this.props.onToast(
      new CustomEvent(`toast`, {
        detail: {
          cta,
          ctaCallback,
          text,
          type,
          timeoutMS: 20000,
        },
      }),
    );
  }

  private getReportName(bookmarkType?: BookmarkType): ReportName {
    switch (bookmarkType) {
      case BookmarkType.Segmentation:
      case BookmarkType.Segmentation3:
      case BookmarkType.Insights: {
        return ReportName.Insights;
      }
      case BookmarkType.Flows: {
        return ReportName.Flows;
      }
      case BookmarkType.Funnels: {
        return ReportName.Funnels;
      }
      case BookmarkType.Retention: {
        return ReportName.Retention;
      }
      default: {
        throw new Error(`unknown bookmark type ${bookmarkType}`);
      }
    }
  }

  private getUrlDataFromState({
    routedDashboardId = this.state.routedDashboardId,
    dashboard = this.state.dashboard,
    timeFilter = this.state.timeFilter,
    tvModeEnabled = this.state.tvModeEnabled,
    focusedCard = this.state.focusedCard,
    editorCardId = this.state.editorCardId,
    editorCardBookmarkURLSlug = this.state.editorCardBookmarkURLSlug,
    editorCardEditedBookmark = this.state.editorCardEditedBookmark,
  }: {
    routedDashboardId?: number;
    dashboard?: Dashboard;
    timeFilter?: TimeFilter;
    tvModeEnabled?: boolean;
    focusedCard?: FocusedCard;
    editorCardId?: DashboardCardId;
    editorCardBookmarkURLSlug?: string;
    editorCardEditedBookmark?: DisplayBookmark;
  } = {}): DashboardUrlData {
    const urlData: DashboardUrlData = {};

    const filters = this.state.filters;
    const breakdowns = this.getBreakdowns();
    if (routedDashboardId) {
      urlData.dashboardId = routedDashboardId;
    }
    // We only show the timeFilter param in the URL if it is different to dashboard.time_filter (same for filters)
    // URL '/dashboards#id=1&timeFilter=' could be generated and is valid. It means that dashboard.time_filter=<some_value> but user explicitly selected no filters
    if (dashboard && !isEqual(timeFilter || {}, dashboard.time_filter)) {
      urlData.timeFilter = timeFilter;
    }

    const hasPinnedFilterValues = filters.some((filter) => filter.activeValue);
    if (dashboard && (!isEqualToSavedFilters(dashboard.filters, filters) || hasPinnedFilterValues)) {
      urlData.filters = filters;
    }

    if (dashboard && !isEqualToSavedBreakdowns(dashboard.breakdowns, breakdowns)) {
      urlData.breakdowns = breakdowns;
    }

    if (tvModeEnabled) {
      urlData.tvModeEnabled = tvModeEnabled;
    }

    if (focusedCard && `bookmarkId` in focusedCard) {
      urlData.focusedBookmarkId = focusedCard.bookmarkId;
    }

    if (focusedCard && `cardId` in focusedCard) {
      urlData.focusedCardId = focusedCard.cardId;
    }

    if (editorCardId) {
      if (editorCardBookmarkURLSlug) {
        reporter.warn(`standard-mode: editorCardId and editorCardBookmarkURLSlug shouldn't both be set`, {
          editorCardId,
          editorCardBookmarkURLSlug,
        });
      } else {
        urlData.editorCardId = editorCardId;
      }
    }

    if (editorCardBookmarkURLSlug) {
      urlData.editorCardBookmarkURLSlug = editorCardBookmarkURLSlug;
    } else if (editorCardEditedBookmark) {
      urlData.editorCardEditedBookmark = editorCardEditedBookmark;
    }

    return urlData;
  }

  private myDashboardTrackingProps(dashboardId?: number) {
    const trackedDashboardId = dashboardId || this.state.dashboard?.id;
    return {
      'is my dashboard':
        !!userDetailsStore.value.myDashboardId && trackedDashboardId === userDetailsStore.value.myDashboardId,
    };
  }

  private insertDashboardSidenavItem(
    dashboard: DashboardItemInfo,
    sectionIndex: SidenavSectionIndex,
    trackingName: SidenavSectionTrackingName,
  ) {
    this.props.sidenavContext.insertDashboardSidenavItem(dashboard, sectionIndex, trackingName);
  }

  private updateDashboardSidenavItem(dashboard: DashboardItemInfo) {
    this.props.sidenavContext.updateDashboardSidenavItem({dashboard});
  }

  /**
   * If sectionIndices is null, `value` is removed from all the sections, else it is deleted only from the specified ones
   */
  private deleteSidenavItem(value, sectionIndices?: SidenavSectionIndex[]) {
    this.props.sidenavContext.deleteSidenavItem(value, sectionIndices);
  }

  private moveSidenavItem(dashboard: DashboardItemInfo, parentDashboardId?: number) {
    this.props.sidenavContext.moveSidenavItem(dashboard, null, parentDashboardId, null, null);
  }

  getRecentDashboardIds() {
    const {userId, projectId} = this.props.context;
    const recentlyViewedDashboards = this.recentlyViewedDashboardsStorage.getItem({userId, key: projectId}) || [];
    let dashboards = [];
    if (recentlyViewedDashboards) {
      dashboards = [this.state.dashboard.id, ...recentlyViewedDashboards.map((recentDashboard) => recentDashboard.id)];
    }
    return dashboards;
  }

  async pinDashboard(dashboard: DashboardItemInfo, crudSource: DashboardCRUDSource) {
    // Toggle pinned status. "YYYY-MM-DD" format
    const pinnedDate = dashboard.pinned_date ? null : new Date().toISOString().substring(0, 10);

    try {
      await dashboardsClient.pinDashboard({
        projectId: this.props.context.projectId,
        workspaceId: this.props.context.workspaceId,
        dashboardID: dashboard.id,
        isPinned: Boolean(pinnedDate),
      });
      const message = pinnedDate ? `Board pinned successfully.` : `Board unpinned successfully.`;

      this.props.onToast(
        new CustomEvent(`toast`, {
          detail: {
            text: message,
            type: ToastType.SUCCESS,
            timeoutMS: 20000,
          },
        }),
      );
    } catch (err) {
      const message = pinnedDate ? `Failed to add board to pinned.` : `Failed to remove board from pinned.`;

      reporter.error(err as Error, message);
      this.props.onToast(
        new CustomEvent(`toast`, {
          detail: {
            text: message,
            type: ToastType.ERROR,
            timeoutMS: 20000,
          },
        }),
      );
    }
    this.props.context.track(
      pinnedDate ? TrackingEvent.Pinned : TrackingEvent.Unpinned,
      getCommonDashboardTrackingProps({
        dashboard,
        cards: this.getAvailableCards(),
        sourceTrackingProps: {crudSource},
      }),
    );

    const {userId, projectId} = this.props.context;
    const seenPinnedDashboardIds = this.seenPinnedDashboardIdsStorage.getItem({userId, key: projectId}) || [];
    if (seenPinnedDashboardIds.indexOf(dashboard.id) === -1) {
      this.seenPinnedDashboardIdsStorage.setItem({
        userId,
        key: projectId,
        value: [...seenPinnedDashboardIds, dashboard.id],
      });
    }
    this.updateSidenavSections({...dashboard, pinned_date: pinnedDate});
    if (this.state.dashboard?.id === dashboard.id) {
      this.setState({
        dashboard: {
          ...this.state.dashboard,
          pinned_date: pinnedDate,
        },
      });
    }
  }

  private getFocusedCardId() {
    if (this.state.focusedCard) {
      if (`cardId` in this.state.focusedCard) {
        return this.state.focusedCard.cardId;
      } else if (`bookmarkId` in this.state.focusedCard) {
        const bookmarkId = this.state.focusedCard.bookmarkId;
        return this.getCardsWithBookmarkId(bookmarkId)[0]?.id;
      }
    }
    return null;
  }

  async moveDashboard(dashboard: DashboardItemInfo, source: DashboardMoveSource, parentDashboardId?: number) {
    try {
      let newAncestors;
      if (parentDashboardId) {
        const newParentDashboard = await dashboardsClient.nestDashboard({
          projectId: this.props.context.projectId,
          workspaceId: this.props.context.workspaceId,
          parentDashboardId,
          dashboard,
        });
        newAncestors = [...newParentDashboard.ancestors, {id: newParentDashboard.id, title: newParentDashboard.title}];
      } else {
        // Not passing a parent dashboard id value will move the dashboard to the root level
        await dashboardsClient.unnestDashboard({
          projectId: this.props.context.projectId,
          workspaceId: this.props.context.workspaceId,
          parentDashboardId: dashboard.parent_dashboard_id,
          dashboard,
        });
        newAncestors = [];
      }
      if (this.state.dashboard?.id === dashboard.id) {
        this.setState({
          dashboard: {
            ...this.state.dashboard,
            ancestors: newAncestors,
          },
        });
      }
      const toast = {
        cta: `Visit Board`,
        message: `Board has been moved.`,
        type: ToastType.SUCCESS,
        undoable: false,
        ctaCallback: () =>
          this.openExternalDashboard(parentDashboardId, {contentId: dashboard.id, contentType: ContentType.Dashboard}),
      };
      addToast(toast);

      this.moveSidenavItem(dashboard, parentDashboardId);

      this.props.context.track(TrackingEvent.MoveDashboard, {
        ...getCommonDashboardTrackingProps({
          dashboard,
          cards: this.getAvailableCards(),
        }),
        ...this.myDashboardTrackingProps(dashboard.id),
        Source: source,
      });
    } catch (err) {
      let message = `Error moving board`;

      if (err instanceof ApiResponseError) {
        message = err.message;
      }
      reporter.error(err as Error, `Error moving board.`);
      this.props.onToast(
        new CustomEvent(`toast`, {
          detail: {
            text: message,
            type: ToastType.ERROR,
            timeoutMS: 20000,
          },
        }),
      );
    }
  }

  /**
   * Handles a `copyDemoToProject` event by copying the current board to the given project
   * if it's a demo board.
   */
  handleCopyDemoToProject = (ev: CustomEvent): Promise<void> => {
    if (!this.getCurrentProject().isDemo) {
      throw new Error(`Attempted to perform demo-project copy from outside a demo project`);
    }

    this.props.onToast(
      new CustomEvent(`toast`, {
        detail: {
          text: `Copying...`,
          type: ToastType.INFO,
        },
      }),
    );

    const targetProjectId = ev.detail.targetProjectId;
    const dashboardToCopy = this.state.dashboard;
    if (targetProjectId === undefined || targetProjectId === this.props.context.projectId) {
      // We're being asked to move a board to the project it is already in. Done!
      return Promise.resolve();
    }
    return (
      this.movedBoardInfoStorage.setItem({
        userId: this.props.context.userId,
        key: MOVED_BOARD_STORAGE_KEY,
        value: {
          sourceProjectId: this.props.context.projectId,
          sourceDashboardId: dashboardToCopy.id,
          undoable: false,
        },
      }) as Promise<boolean>
    )
      .then(() => {
        return this.createDashboardInTargetProject(dashboardToCopy, targetProjectId);
      })
      .then((newBoard) => {
        // Ignore the case when newBoard === null because there's nothing to do (error reporting
        // has already happened in createDashboardInTargetProject()).
        if (newBoard !== null) {
          this.navigateToBoard(newBoard.id, targetProjectId);
        }
      });
  };

  executeMoveToYourBoardsWorkflow(
    dashboardToMove: DashboardItemInfo,
    targetProjectId?: number,
    targetParentDashboardId?: number,
  ): Promise<void> {
    if (targetProjectId === undefined || targetProjectId === this.props.context.projectId) {
      // We're being asked to move a board to the project it is already in. Done!
      return Promise.resolve();
    }

    this.props.onToast(
      new CustomEvent(`toast`, {
        detail: {
          text: `Moving...`,
          type: ToastType.INFO,
        },
      }),
    );

    return (
      this.movedBoardInfoStorage.setItem({
        userId: this.props.context.userId,
        key: MOVED_BOARD_STORAGE_KEY,
        value: {
          sourceProjectId: this.props.context.projectId,
          sourceDashboardId: dashboardToMove.id,
          undoable: true,
        },
      }) as Promise<boolean>
    )
      .then(() => {
        return this.moveToYourBoards(dashboardToMove, targetProjectId, targetParentDashboardId);
      })
      .then((newBoardId) => {
        if (newBoardId !== undefined) {
          this.navigateToBoard(newBoardId, targetProjectId);
        }
      });
  }

  private navigateToBoard(dashboardId: number, projectId: number) {
    if (isGlobalSidenavEnabled(this.getCurrentProject())) {
      const wsId =
        getWorkspacesForProject({
          currentProject: this.getCurrentProject(),
          projectsResource: this.props.projects,
          projectId,
        })[0]?.id || undefined;
      const targetUrl = getReportUrl(`boards/#id=${dashboardId}`, {
        projectId,
        workspaceId: wsId,
      });
      this.props.spaNavManager.navigate(targetUrl);
    } else {
      const chromeHeader = document.querySelector<ChromeHeader>(`mp-chrome-header`);
      chromeHeader.helpers.navigateToBoard(dashboardId, projectId);
    }
  }

  private reportErrorOnMovingBoard(err: Error) {
    let message = `Something went wrong moving a board. We've kept it safely in its old location.`;

    if (err instanceof ApiResponseError) {
      message = err.message;
    }

    reporter.error(err, `Error moving board between projects.`);
    this.props.onToast(
      new CustomEvent(`toast`, {
        detail: {
          text: message,
          type: ToastType.ERROR,
          timeoutMS: 20000,
        },
      }),
    );
  }

  private createDashboardInTargetProject(
    dashboard: DashboardItemInfo,
    targetProjectId?: number,
    targetParentDashboardId?: number,
  ): Promise<Dashboard | null> {
    return this.props.selectorController
      .createDashboard({
        fields: {
          duplicate: dashboard.id,
          target_project_id: targetProjectId,
          target_parent_dashboard_id: targetParentDashboardId,
        },
      })
      .catch((err) => {
        this.reportErrorOnMovingBoard(err);
        return null;
      });
  }

  async moveToYourBoards(
    dashboard: DashboardItemInfo,
    targetProjectId?: number,
    targetParentDashboardId?: number,
  ): Promise<number | undefined> {
    if (targetProjectId === undefined) {
      // We're being asked to move a board to the place it already is. Done!
      return undefined;
    }

    const movedBoard = await this.createDashboardInTargetProject(dashboard, targetProjectId, targetParentDashboardId);
    if (movedBoard === null) {
      this.props.selectorController.debouncedStopSavingChanges();
      return undefined;
    }

    try {
      await this.props.selectorController.deleteDashboard(dashboard.id);
      this.deleteSidenavItem(dashboard.id.toString());
      this.props.selectorController.debouncedStopSavingChanges();
      return movedBoard.id;
    } catch (err) {
      // If we fail to delete the board, we should undo the createDashboardInTargetProject(delete the board from the target project).
      this.reportErrorOnMovingBoard(err);
      const targetDashboardController = new DashboardSelectorController({
        projectId: targetProjectId,
        bookmarkID: null,
        dashboardsClient,
        i18n: this.props.i18n,
      });
      await targetDashboardController.deleteDashboard(movedBoard.id);
      this.props.selectorController.debouncedStopSavingChanges();
      return undefined;
    }
  }

  createUndoBoardMoveCallback(movedDashboardId: number, moveInfo: MovedBoardInfo): () => void {
    return debounce(async () => {
      const deletedDashboardController = new DashboardSelectorController({
        projectId: moveInfo.sourceProjectId,
        bookmarkID: null,
        dashboardsClient,
        i18n: this.props.i18n,
      });
      try {
        await Promise.all([
          deletedDashboardController.undeleteDashboard(moveInfo.sourceDashboardId),
          this.props.selectorController
            .deleteDashboard(movedDashboardId)
            .then(() => this.deleteSidenavItem(movedDashboardId.toString())),
        ]);
        this.movedBoardInfoStorage.setItem({
          userId: this.props.context.userId,
          key: MOVED_BOARD_STORAGE_KEY,
          value: {
            sourceProjectId: this.props.context.projectId,
            sourceDashboardId: movedDashboardId,
            undoable: true,
          },
        });
        this.props.selectorController.debouncedStopSavingChanges();

        this.navigateToBoard(moveInfo.sourceDashboardId, moveInfo.sourceProjectId);
      } catch (err) {
        reporter.error(err as Error, `Error undoing board move between projects.`);
        this.props.onToast(
          new CustomEvent(`toast`, {
            detail: {
              text: `Could not successfully undo board move.`,
              type: ToastType.ERROR,
              timeoutMS: 20000,
            },
          }),
        );
      }
    });
  }

  async favoriteDashboard(dashboard: DashboardItemInfo, crudSource: DashboardCRUDSource) {
    // Toggle favorited status
    const isFavorited = !dashboard.is_favorited;
    try {
      await dashboardsClient.favoriteDashboard({
        projectId: this.props.context.projectId,
        workspaceId: this.props.context.workspaceId,
        dashboardID: dashboard.id,
        isFavorited,
      });

      const message = isFavorited ? `Board favorited successfully.` : `Board unfavorited successfully.`;

      this.props.onToast(
        new CustomEvent(`toast`, {
          detail: {
            text: message,
            type: ToastType.SUCCESS,
            timeoutMS: 20000,
          },
        }),
      );
    } catch (err) {
      const message = isFavorited ? `Failed to add board to favorites.` : `Failed to remove board from favorites.`;

      reporter.error(err as Error, message);
      this.props.onToast(
        new CustomEvent(`toast`, {
          detail: {
            text: message,
            type: ToastType.ERROR,
            timeoutMS: 20000,
          },
        }),
      );
    }

    this.props.context.track(
      isFavorited ? TrackingEvent.Favorited : TrackingEvent.Unfavorited,
      getCommonDashboardTrackingProps({
        dashboard,
        cards: this.state.dashboard?.id === dashboard.id ? this.getAvailableCards() : [],
        sourceTrackingProps: {crudSource},
      }),
    );

    this.updateSidenavSections({...dashboard, is_favorited: isFavorited});
    if (this.state.dashboard?.id === dashboard.id) {
      this.setState({
        dashboard: {
          ...this.state.dashboard,
          is_favorited: isFavorited,
        },
      });
    }
  }

  private get bookmarkStore() {
    return this.props.bookmarkStore;
  }

  private getDuplicateContentAction(contentAction) {
    const {contentType, actionType, contentId} = contentAction;
    const content = this.state.dashboard.contents?.[contentType]?.[contentId];
    // Duplicate creates a copy of the `media` and `text` cards, but for report and report links it works different.
    // Duplicate a report or report-link will always result in a report
    // To create a report-link, you have to use the CreateReportLink action

    const cardId = getCardIdFromContent(contentId, contentType);
    const result = {
      ...contentAction,
      actionType: ContentActionType.Create,
    };

    if (contentType === ContentType.Report) {
      const stateCard = this.state.cards.get(cardId) as DashboardCard;
      if (actionType === ContentActionType.CreateReportLink) {
        return {
          ...result,
          contentType: ContentType.ReportLink,
          contentParams: {
            source_bookmark_id: content.id,
            ...(stateCard.chart_options ? {chart_options: stateCard.chart_options} : {}),
          },
        };
      } else if (actionType === ContentActionType.Duplicate) {
        // if the chart options were edited but not saved because the user did not have edit perms, override the chart options
        let bookmarkParams = content.params;
        if (stateCard.chart_options) {
          bookmarkParams = getBookmarkParamsWithOverrides({
            bookmarkParams: content.params,
            bookmarkType: content.type,
            chartTypeOverride: stateCard.chart_options?.type,
            timeFilterOverride: null,
            additionalFilters: [],
            additionalBreakdowns: [],
          });
        }
        return {
          ...result,
          contentType: ContentType.Report,
          contentParams: {
            bookmark: {
              params: JSON.stringify(beforeSavingBookmark({params: bookmarkParams, type: content.type})),
              description: content.description || ``,
              name: `Duplicate of ${content.name}`,
              type: content.type,
            },
          },
        };
      }
    } else if (contentType === ContentType.ReportLink) {
      const stateCard = this.state.cards.get(cardId) as DashboardCard;
      if (actionType === ContentActionType.Duplicate) {
        const bookmarkParams = getBookmarkParamsWithOverrides({
          bookmarkParams: content.bookmark.params,
          bookmarkType: content.bookmark.type,
          chartTypeOverride: stateCard.chart_options?.type,
          timeFilterOverride: null,
          additionalFilters: [],
          additionalBreakdowns: [],
        });
        return {
          ...result,
          contentType: ContentType.Report,
          contentParams: {
            bookmark: {
              params: JSON.stringify(beforeSavingBookmark({params: bookmarkParams, type: content.bookmark.type})),
              description: content.description || ``,
              name: `Duplicate of ${content.title}`,
              type: content.bookmark.type,
            },
          },
        };
      } else if (actionType === ContentActionType.CreateReportLink) {
        return {
          ...result,
          contentType: ContentType.ReportLink,
          contentParams: {
            source_bookmark_id: content.bookmark.id,
            chart_options: stateCard.chart_options,
          },
        };
      }
    } else {
      return contentAction;
    }
  }

  private updateSidenavSections(dashboard: DashboardItemInfo) {
    this.props.sidenavContext.updateSidenavSections({dashboard});
  }

  private convertToCurrentWorkspaceCard(card) {
    if (!this.props.context.workspaceId) {
      return card;
    } else {
      const currentWorkspaceCard = Object.assign({}, card);
      currentWorkspaceCard.bookmark.workspace_id = this.props.context.workspaceId;
      return currentWorkspaceCard;
    }
  }

  public getCurrentProject(): CurrentProject | undefined {
    return this.props.currentProject.get().value;
  }

  public getLocalizationStore(): LocalizationStore | undefined {
    return this.props.i18n;
  }

  private markFetchCardTracking(id: DashboardCardId) {
    const cardIds = this.state.dashboard.cards.map((x) => x.id);
    if (!this.perfTracking.has(this.state.dashboard.id)) {
      this.perfTracking.set(this.state.dashboard.id, {
        firstLoad: false,
        firstLoadTime: 0,
        allLoaded: false,
        fetchedCards: new Set<DashboardCardId>(),
      });
    }
    const dashboardTracking = this.perfTracking.get(this.state.dashboard.id);
    if (!dashboardTracking.fetchedCards.has(id)) {
      dashboardTracking.fetchedCards.add(id);

      this.emitTrackEvent({
        eventName: TrackingEvent.DashboardFirstFetchIssued,
        eventPrefix: PERFORMANCE_EVENT_PREFIX,
        props: {
          ...getCommonDashboardTrackingProps({
            dashboard: this.state.dashboard,
            cards: this.getAvailableCards(),
          }),
          dashboardId: this.state.dashboard.id,
          project_id: this.getCurrentProject()?.id,
          [`Cards Loaded Count`]: dashboardTracking.fetchedCards.size,
          [`Total Card Count`]: this.state.dashboard.cards.length,
        },
      });

      const allFetched = this.cardSeenWatcher
        .getAllSeenKeys()
        .filter((seenKey) => cardIds.includes(seenKey))
        .every((cardId) => dashboardTracking.fetchedCards.has(cardId));
      if (allFetched && !dashboardTracking.firstLoad) {
        dashboardTracking.firstLoad = true;
        dashboardTracking.firstLoadTime = getNow();

        this.emitTrackEvent({
          eventName: TrackingEvent.DashboardInitialCardsFetched,
          eventPrefix: PERFORMANCE_EVENT_PREFIX,
          props: {
            ...getCommonDashboardTrackingProps({
              dashboard: this.state.dashboard,
              cards: this.getAvailableCards(),
            }),
            dashboardId: this.state.dashboard.id,
            project_id: this.getCurrentProject()?.id,
            [`Cards Loaded Count`]: dashboardTracking.fetchedCards.size,
            [`Total Card Count`]: this.state.dashboard.cards.length,
          },
        });
      }
      if (dashboardTracking.fetchedCards.size === this.state.dashboard.cards.length && !dashboardTracking.allLoaded) {
        dashboardTracking.allLoaded = true;

        this.emitTrackEvent({
          eventName: TrackingEvent.DashboardAllCardsFetched,
          eventPrefix: PERFORMANCE_EVENT_PREFIX,
          props: {
            ...getCommonDashboardTrackingProps({
              dashboard: this.state.dashboard,
              cards: this.getAvailableCards(),
            }),
            dashboardId: this.state.dashboard.id,
            project_id: this.getCurrentProject()?.id,
            [`Cards Loaded Count`]: dashboardTracking.fetchedCards.size,
            [`Total Card Count`]: this.state.dashboard.cards.length,
            [`Total Load Latency (s)`]: Math.round(diffFromNow(dashboardTracking.firstLoadTime)),
          },
        });
      }
    }
  }

  get historyManager() {
    return this.props.historyManager;
  }

  updateDashboardFilters({
    timeFilter = null,
    filters = null,
    breakdowns = null,
  }: {
    timeFilter?: TimeFilter;
    filters?: BoardFilter[];
    breakdowns?: Segmentation[];
  }) {
    this.clearCards();
    const filterUpdates = {
      ...(timeFilter ? {timeFilter} : {}),
      ...(filters ? {filters} : {}),
      ...(breakdowns ? {breakdowns} : {}),
    };

    if (!this.state.isHistoryActionInProgress) {
      this.historyManager.applyUpdates<TODO>({
        source: {
          fields: {filters: this.state.filters, timeFilter: this.state.timeFilter, breakdowns: this.state.breakdowns},
        },
        updates: {fields: filterUpdates},
        metadata: {
          isDashboardFiltersUpdate: true,
        },
      });
    }

    const dashboardModified =
      !!timeFilter ||
      !isEqualToSavedFilters(this.state.dashboard?.filters, filters) ||
      !isEqualToSavedBreakdowns(this.state.dashboard?.breakdowns, breakdowns);
    this.setState(
      (prevState) => ({...prevState, ...filterUpdates, dashboardModified}),
      () => {
        const cards = this.state.dashboard.card_order.map((id) => this.state.cards.get(id));
        for (const card of cards) {
          this.loadCard(card);
        }
        this.updateUrlFromState({
          timeFilter: timeFilter || this.state.timeFilter,
        });
      },
    );
  }

  async editTextCard(cardId, markdown) {
    const textCard = this.state.cards.get(cardId) as TextCard;
    const originalMarkdown = textCard.markdown;
    textCard.markdown = markdown;
    this.props.context.track(TrackingEvent.EditTextCard, this.getContentActionTrackingParam(textCard));
    try {
      // Use the common method to save any dashboard changes.
      // If we want to save the text card only, and not the whole layout,
      // use patchUpdateTextCardFields which calls dashboard_text_card_entry
      const contentAction = {
        actionType: ContentActionType.Update,
        contentId: textCard.content_id,
        contentType: textCard.type,
        contentParams: {
          markdown,
        },
      };

      await this.saveChangesOnDashboard({contentAction});
      if (!this.state.isHistoryActionInProgress) {
        this.historyManager.applyUpdates<TODO>({
          source: {fields: {...contentAction, contentParams: {markdown: originalMarkdown}}},
          updates: {fields: contentAction},
          metadata: {
            isContentUpdate: true,
          },
        });
      }
      this.props.selectorController.debouncedStopSavingChanges();
    } catch (error) {
      this.props.selectorController.debouncedStopSavingChanges();
      throw error;
    }
  }

  async changeCardChartType(cardId, newChartOptions) {
    try {
      const stateCard = this.state.cards.get(cardId) as DashboardCard;
      const stateChartOptions = getChartOptions({
        bookmark: stateCard.bookmark,
        chartOptionsOverride: stateCard.chart_options,
        metricsResource: this.props.metrics,
        behaviorsResource: this.props.behaviors,
        currentProject: this.getCurrentProject(),
      });
      const contentAction = {
        actionType: ContentActionType.Update,
        contentId: stateCard.content_id,
        contentType: stateCard.type,
        contentParams: {
          type: newChartOptions.type || null,
        },
      };
      if (stateChartOptions.type !== newChartOptions.type) {
        this.clearCards([stateCard]);
        stateCard.chart_options = newChartOptions;
        if (this.canEditActiveDashboard()) {
          if (stateCard.type === ContentType.ReportLink) {
            this.loadCard(stateCard);
            await this.saveChangesOnDashboard({contentAction});
          } else {
            const bookmarkParams = getBookmarkParamsWithOverrides({
              bookmarkParams: stateCard.bookmark.params,
              bookmarkType: stateCard.bookmark.type,
              chartTypeOverride: newChartOptions.type,
              timeFilterOverride: this.state.timeFilter,
              additionalFilters: getActiveBoardFilters(this.state.filters),
              additionalBreakdowns: this.getBreakdowns(),
            });
            // TODO gonzalo or shayan look into using the saveChangesOndashboard for chart type change
            this.props.selectorController.onSaveChangesStart();
            await this.bookmarkStore.patch({
              id: stateCard.bookmark.id,
              name: stateCard.bookmark.name,
              params: bookmarkParams,
              type: stateCard.bookmark.type,
            });
            // TODO: @gonzalo or @shayan, we are not refreshing the report-links after the main report chart type changes
            this.loadCard(stateCard);
          }
        } else {
          this.setState({dashboardModified: true});
        }

        // TODO go/j/AE-909: de-dupe loadCard calls.
        await this.loadCard(stateCard);
        this.props.context.track(TrackingEvent.ChangeVisualization, {
          ...this.getContentActionTrackingParam(stateCard),
          'new chartType': newChartOptions?.type,
          'old chartType': stateChartOptions.type,
        });
        this.props.selectorController.debouncedStopSavingChanges();
      }

      if (!this.state.isHistoryActionInProgress) {
        this.historyManager.applyUpdates<TODO>({
          source: {fields: {...contentAction, contentParams: stateChartOptions}},
          updates: {fields: contentAction},
          metadata: {
            isContentUpdate: true,
          },
        });
      }
    } catch (err) {
      reporter.error(err as Error);
      this.props.onToast(
        new CustomEvent(`toast`, {
          detail: {
            text: ERROR_MESSAGES[NetworkError.UpdateChartOptions],
            type: ToastType.ERROR,
            timeoutMS: 20000,
          },
        }),
      );
    }
  }

  setupUndoRedo() {
    this.historyManager.setup(async (changes, metadata) => {
      this.setState({isHistoryActionInProgress: true});
      this.props.selectorController.setIsSavingChanges(true);
      const dashboardChange = changes[0].value;
      if (metadata.isDashboardMetadata) {
        const {title, description} = dashboardChange;
        await this.saveChangesOnDashboard({dashboardMetadata: {title, description}});
      } else if (metadata.isDashboardFilters) {
        const currentFilters = {
          filters: this.state.filters,
          timeFilter: this.state.timeFilter,
          breakdowns: this.state.breakdowns,
        };
        await this.saveChangesOnDashboard({
          dashFilters: {
            filters: dashboardChange.filters,
            time_filter: dashboardChange.time_filter,
            breakdowns: dashboardChange.breakdowns,
          },
        });
        this.setState({...currentFilters});
        this.updateUrlFromState();
      } else if (metadata.isDashboardFiltersUpdate) {
        this.updateDashboardFilters({...dashboardChange});
      } else if (metadata.isContentUpdate) {
        await this.handleHistoryActionForContentUpdate(dashboardChange);
      } else {
        let {layout, contentAction} = dashboardChange;

        if (contentAction?.actionType === ContentActionType.UnMove) {
          contentAction = await this.getUnMoveContentActionForHistory(contentAction);
        }
        const layoutParams = getLayoutUpdateParamsForHistoryAction(layout, this.state.dashboard.layout, contentAction);

        this.setState({
          gridLayoutUpdateParams: layoutParams,
        });

        if (contentAction) {
          await this.saveChangesOnDashboard({contentAction, gridLayoutUpdateParams: layoutParams});
        } else {
          await this.saveChangesOnDashboard({gridLayoutUpdateParams: layoutParams});
        }
      }
      this.setState({isHistoryActionInProgress: false});
      this.props.selectorController.setIsSavingChanges(false);
    });
  }

  async getUnMoveContentActionForHistory(contentAction) {
    const targetDashboard = await dashboardsClient.querySingle(
      {
        projectId: this.props.context.projectId,
        workspaceId: this.props.context.workspaceId,
        dashboardID: contentAction.contentParams.target_dashboard_id,
      },
      {clearCache: true},
    ).network;

    const targetDashboardLayoutParam = getLayoutParamWithContentRemoved(
      {contentId: contentAction.contentId, contentType: contentAction.contentType},
      targetDashboard.layout,
    );

    return {
      ...contentAction,
      contentParams: {...contentAction.contentParams, target_dashboard_layout_param: targetDashboardLayoutParam},
    };
  }

  async handleHistoryActionForContentUpdate(contentAction: ContentAction) {
    const cardId = getCardIdFromContent(contentAction.contentId, contentAction.contentType);
    if (contentAction.contentType === ContentType.Text) {
      await this.editTextCard(cardId, contentAction.contentParams.markdown);
    } else if (contentAction.contentParams.type) {
      await this.changeCardChartType(cardId, contentAction.contentParams);
    } else {
      await this.saveChangesOnDashboard({contentAction});
    }
  }

  private updateUrlFromState(
    {
      routedDashboardId = this.state.routedDashboardId,
      dashboard = this.state.dashboard,
      timeFilter = this.state.timeFilter,
      tvModeEnabled = this.state.tvModeEnabled,
      focusedCard = this.state.focusedCard,
      editorCardId = this.state.editorCardId,
      editorCardBookmarkURLSlug = this.state.editorCardBookmarkURLSlug,
      editorCardEditedBookmark = this.state.editorCardEditedBookmark,
    }: {
      routedDashboardId?: number;
      dashboard?: Dashboard;
      timeFilter?: TimeFilter;
      tvModeEnabled?: boolean;
      focusedCard?: FocusedCard;
      editorCardId?: DashboardCardId;
      editorCardBookmarkURLSlug?: string;
      editorCardEditedBookmark?: DisplayBookmark;
    } = {},
    {replace = true} = {},
  ) {
    this.props.navigate(
      `#${serializeDashboardUrlHashParams(
        this.getUrlDataFromState({
          routedDashboardId,
          dashboard,
          timeFilter,
          tvModeEnabled,
          focusedCard,
          editorCardId,
          editorCardBookmarkURLSlug,
          editorCardEditedBookmark,
        }),
      )}`,
      {
        replace,
      },
    );
  }

  private getTrackingContentSubType({
    contentType,
    contentId,
    oldContent,
  }: {
    contentType: ContentType;
    contentId: number;
    oldContent?: CardContent;
  }) {
    // oldContent is provided in case the card has been deleted before this tracking code is called
    const content = this.state.dashboard.contents[contentType]?.[contentId] || oldContent;
    if (!content) {
      return null;
    }

    if (ContentType.Report === contentType) {
      return (content as ReportContent).type;
    } else if (ContentType.ReportLink === contentType) {
      return (content as ReportLinkContent).bookmark.type;
    } else if (ContentType.Media === contentType) {
      return (content as MediaContent).media_type;
    } else {
      return null;
    }
  }

  private getTrackingBookmarkParams({contentType, contentId}: {contentType: ContentType; contentId: number}) {
    const content = this.state.dashboard.contents[contentType][contentId];
    if (!content || ![ContentType.Report, ContentType.ReportLink].includes(contentType)) {
      return {};
    }

    let params;
    if (ContentType.Report === contentType) {
      params = (content as ReportContent).params;
    } else if (ContentType.ReportLink === contentType) {
      params = (content as ReportLinkContent).bookmark.params;
    }

    const displayOptions: DisplayOptions = params?.displayOptions;

    return {
      'Chart Type': displayOptions?.chartType,
      '# of breakdowns': params?.sections?.group?.length,
      '# of metrics': params?.sections?.show?.length,
    };
  }

  private trackContentAction({
    eventName,
    actionType,
    contentType,
    contentId,
    oldContent,
    extraParams = {},
  }: {
    eventName: TrackingEvent;
    actionType: string;
    contentType: ContentType;
    contentId: number;
    oldContent?: CardContent;
    extraParams?: any;
  }) {
    this.props.context.track(eventName, {
      ...getCommonDashboardTrackingProps({
        dashboard: this.state.dashboard,
        cards: this.getAvailableCards(),
      }),
      'Action Type': actionType,
      ...(contentType
        ? {
            'Content Id': contentId,
            'Content Type': contentType,
            'Content Sub Type': this.getTrackingContentSubType({contentId, contentType, oldContent}),
          }
        : {}),
      ...extraParams,
    });
  }

  private isCrossProjectBoardMovesEnabled(): boolean {
    return this.getCurrentProject()?.crossProjectBoardMovingSetting !== BoardMoveSetting.NOT_ALLOWED;
  }

  isNestedDashboardsEnabled(): boolean {
    return isNestedDashboardsEnabled(this.getCurrentProject());
  }

  private getBreakdowns(): Segmentation[] {
    return this.state.breakdowns;
  }

  handleRefreshClicked = () => this.state.dashboardLoaded && this.onRefresh();

  handleCreateDashboardClicked = async (crudSource?: DashboardCRUDSource) => {
    const staged = new StagedDashboard({}, {crudSource});
    const createEntityModalResult = await promptManageDashboardModal(
      `Create New Board`,
      staged.getCreateEntityModalAttrs({projectId: this.props.context.projectId, i18n: this.props.i18n}),
    );

    if (createEntityModalResult.didSave) {
      const {name: title, description, globalAccessType} = createEntityModalResult;
      this.createDashboard(
        staged.updateDashboardAttrs({title, ...(description ? {description} : {})}),
        globalAccessType,
      );
    }
  };

  handleCancelTemplateModal = () => {
    this.setState({
      openCreateFoundationalTemplateModal: false,
    });

    // If a user was directed to the modal using a direct link,
    // we need to remove the query param when user closes modal for no side-effects
    if (getLocation().href.includes(`show-template-selector`)) {
      const url = new URL(getLocation().href);
      const params = new URLSearchParams(url.search);
      params.delete(`show-template-selector`);
      params.delete(`selected-template`);
      url.search = params.toString();
      this.props.navigate(url.toString());
    }
  };

  handleOpenFoundationalTemplateModalClicked = ({entryPoint}: {entryPoint: string}) => {
    this.props.context.track(TrackingEvent.OpenedTemplateSelector, {
      'Show No Data Banner': !this.getCurrentProject()?.hasIntegratedArb,
      'Entry Point': entryPoint,
    });
    this.setState({openCreateFoundationalTemplateModal: true});
  };

  trackFoundationalTemplateEvent = (ev: CustomEvent) => {
    const {eventName, eventProperties} = ev.detail;
    this.props.context.track(eventName, eventProperties);
  };

  isOnStarterBoard = () => {
    return (
      this.getCurrentProject()?.hasFeatureFlag(ProjectFeatureFlag.StarterBoard) &&
      this.state.dashboard?.template_type === TemplateType.STARTER_BOARD &&
      !this.state.browseParams?.isBrowseMode
    );
  };

  isOnStarterBoardWithData = () => {
    return this.isOnStarterBoard() && this.getCurrentProject()?.hasIntegratedArb;
  };

  projectUserCount = () => this.getCurrentProject()?.userCount;

  getDefaultFoundationalTemplatesRecord = (): FoundationalTemplatesStoredRecord => {
    return {
      feedbackRequestCount: 0,
      viewedCount: 1,
      lastInteraction: null,
    };
  };

  isFoundationalTemplateBoard = (): boolean => {
    // This is a little hacky, but using a combination of launch date and feature eligibility is the only way to differentiate between
    // pre and post Foundational Templates KPI boards
    const mFoundationalTemplatesLaunchDate = getCalendarDate(`2023-04-10`);
    const mCreated = getCalendarDate(this.state.dashboard.created);
    return (
      mCreated > mFoundationalTemplatesLaunchDate &&
      this.state.dashboard.generation_type === DashboardType.TemplateGenerated &&
      this.state.dashboard.template_type !== TemplateType.STARTER_BOARD
    );
  };

  private openExportModal = (emailOrSlack: ExportDashboardType) => {
    const {dashboard} = this.state;
    track(DigestTrackingEvent.ExportBoardModalOpened, {
      'dashboard id': dashboard.id,
      'export type': emailOrSlack,
    });
    exportDashboardModalPromise({
      currentProject: this.props.currentProject,
      i18n: this.getLocalizationStore(),
      workspaceId: this.props.context.workspaceId,
      dashboardId: dashboard.id,
      emailOrSlack,
      projectId: this.props.context.projectId,
    });
  };

  handleHeaderActionClick = (ev) => {
    const actionName = ev.detail.name || ev.detail.value;
    switch (actionName) {
      case HeaderAction.Refresh:
        this.onRefresh(true, ev.detail.useQueryApiCache ?? false);
        break;
      case HeaderAction.Delete:
        this.deleteDashboard(this.state.dashboard, DashboardCRUDSource.Header);
        break;
      case CustomHeaderAction.ExportEmail:
        this.openExportModal(ExportDashboardType.Email);
        break;
      case CustomHeaderAction.ExportSlack:
        this.openExportModal(ExportDashboardType.Slack);
        break;
      case CustomHeaderAction.TV:
        this.onTVModeLinkClick();
        break;
      case CustomHeaderAction.PinDashboard:
        this.onPinDashboardClick();
        break;
      case HeaderAction.Duplicate:
        this.duplicateDashboard(this.state.dashboard);
        break;
      case CustomHeaderAction.Move:
        if (ev.detail.additionalInfo.targetProjectId === this.props.context.projectId) {
          this.moveDashboard(this.state.dashboard, DashboardMoveSource.Header, ev.detail.value);
        } else {
          this.executeMoveToYourBoardsWorkflow(
            this.state.dashboard,
            ev.detail.additionalInfo.targetProjectId,
            ev.detail.value,
          );
        }
        break;
      case HeaderAction.Undo:
        this.historyManager.undo();
        break;
      case HeaderAction.Redo:
        this.historyManager.redo();
        break;
      default:
        break;
    }
  };

  handleBoardsSaveFilters = () => {
    trackBoardFilters(BoardFilterEvents.SaveChanges);

    this.saveChangesOnDashboard({
      dashFilters: {filters: this.state.filters, breakdowns: this.state.breakdowns, time_filter: this.state.timeFilter},
    });
  };

  handleBoardsDiscardFilters = () => {
    trackBoardFilters(BoardFilterEvents.DiscardChanges);

    const dashboard = this.dashboardController.getModel();
    if (dashboard) {
      this.updateGlobalTimeFilter(dashboard.time_filter);
      this.updateGlobalFilters(getBoardFiltersFromPersisted(dashboard.filters));
      this.updateGlobalBreakdowns(dashboard.breakdowns ?? []);
    }
  };

  getSetFiltersCount = () => {
    if (!this.state.dashboard) {
      return 0;
    }

    let changedFiltersCount = this.state.filters?.length || 0;
    changedFiltersCount += this.state.breakdowns?.length || 0;

    if (!isEmpty(this.state.timeFilter)) {
      changedFiltersCount += 1;
    }
    return changedFiltersCount;
  };

  areGlobalOptionsChanged = () => {
    if (!this.state.dashboard) {
      return false;
    }
    return (
      !isEqualToSavedFilters(this.state.dashboard.filters, this.state.filters) ||
      !isEqualToSavedBreakdowns(this.state.dashboard.breakdowns, this.state.breakdowns) ||
      !isEqual(this.state.timeFilter || {}, this.state.dashboard.time_filter)
    );
  };

  handleClickExitTVMode = () => {
    this.props.navigate(`#${serializeDashboardUrlHashParams({...this.getUrlDataFromState(), tvModeEnabled: false})}`);
  };

  canEditActiveDashboard = (): boolean => {
    if (this.state.dashboardLoaded) {
      return dashboardUtils.canEditDashboard(this.state.dashboard) && !this.state.isEmbedMode;
    }
    return false;
  };

  getMaxDataHistoryDays = () => this.getCurrentProject()?.getFeatureGate(FeatureGate.MaxDataHistoryDays);

  updateGlobalTimeFilter = (timeFilter: TimeFilter) => {
    this.updateDashboardFilters({timeFilter});
  };

  updateGlobalFilters = (serializedFilters: BoardFilter[]) => {
    this.updateDashboardFilters({filters: serializedFilters});
  };

  updateGlobalBreakdowns = (breakdowns: Segmentation[]) => {
    this.updateDashboardFilters({breakdowns});
  };

  clearTimeFilter = () => {
    this.clearCards();
    this.setState({timeFilter: null, dashboardModified: true}, () => {
      const cards = this.state.dashboard.card_order.map((id) => this.state.cards.get(id));
      for (const card of cards) {
        this.loadCard(card);
      }
      this.updateUrlFromState();
      this.props.context.track(
        TrackingEvent.ResetDateFilter,
        getCommonDashboardTrackingProps({
          dashboard: this.state.dashboard,
          cards: this.getAvailableCards(),
        }),
      );
    });
  };

  handleHeaderSave = (params: QueryReportHeaderSave) => {
    const {name: title, description, saveAsNew, globalAccessType} = params;
    const extraFields = {title, ...(!isNil(description) ? {description} : {})};

    if (saveAsNew) {
      this.createDashboard(this.getActiveStagedDashboard(extraFields), globalAccessType);
    } else {
      this.saveChangesOnDashboard({dashboardMetadata: extraFields});
    }
  };

  handleGlobalFilterBarTrack = (ev) => {
    ev.stopPropagation();
    const {eventPrefix, props} = ev.detail;
    const eventName = ev.detail.eventName.replace(eventPrefix, ``);
    this.props.context.track(eventName, {
      ...props,
      ...this.myDashboardTrackingProps(),
      ...getCommonDashboardTrackingProps({
        dashboard: this.state.dashboard,
        cards: this.getAvailableCards(),
      }),
    });
  };

  getAvailableCards = (): CardOnDashboard[] => {
    if (this.state.dashboardLoaded) {
      const visibleCards = this.state.cardOrder
        .filter((id) => this.state.cards.has(id))
        .map((id) => this.state.cards.get(id));
      return visibleCards;
    }
    return [];
  };

  handleLayoutUpdate = (ev) => {
    const {gridLayoutUpdateParams} = ev.detail;
    this.setState({gridLayoutUpdateParams}, () => {
      this.saveChangesOnDashboard({});
    });
  };

  // @gonzalo Will fix this in a follow up pr
  handleContentAction = async ({contentAction, gridLayoutUpdateParams = null, provideUndoAction = true}) => {
    const {contentType, contentParams, actionType, contentId} = contentAction;

    let contentActionFailed = false;

    this.setState({
      gridLayoutUpdateParams,
    });
    const content = this.state.dashboard.contents?.[contentType]?.[contentId];
    const isDuplicating = [ContentActionType.CreateReportLink, ContentActionType.Duplicate].includes(actionType);
    if (contentType === ContentType.Report && actionType === ContentActionType.Create && !contentParams?.bookmark) {
      const reportManager = this.getReportManagerFromType(contentParams.type);

      if (reportManager) {
        this.openNewReportInEditor(reportManager);
      }
      // we already track the add button, so we don't do any tracking here
    } else if (isDuplicating && contentParams?.target_dashboard_id) {
      this.duplicateToExternalDashboard(contentParams.target_dashboard_id, contentAction);
      this.trackContentAction({
        eventName: TrackingEvent.DuplicateCard,
        contentType,
        contentId,
        actionType,
        extraParams: {
          'Target Dashboard': contentParams.target_dashboard_id,
          'Content Type': contentType,
          ...(contentType === ContentType.Media ? {'Media Type': content.media_type} : {}),
        },
      });
    } else {
      if (isDuplicating) {
        contentAction = this.getDuplicateContentAction(contentAction);
      }
      const {updatedDashboard, hasFailed} = await this.saveChangesOnDashboard({contentAction, gridLayoutUpdateParams});
      contentActionFailed = hasFailed;
      // TODO: hans - find out what this for, the type dosn't match
      // @ts-ignore
      const newContent = updatedDashboard.new_content;
      if (actionType === ContentActionType.Move && !hasFailed) {
        const ctaCallBack = () => {
          this.openExternalDashboard(contentParams.target_dashboard_id, {contentId, contentType});
        };
        const undoCallback = () => {
          this.historyManager.undo();
        };
        const toast = {
          cta: `Visit Board`,
          message: `${contentType === ContentType.Dashboard ? `Board` : `Report`} has been moved.`,
          type: ToastType.SUCCESS,
          undoable: provideUndoAction,
          ctaCallback: ctaCallBack,
          undoCallback,
          undoLabel: `Undo ${generateShortcutLabel(`Z`, {modifiers: [getCommandKey()], joinStr: ` `})}`,
        };
        addToast(toast);
      }
      if (newContent) {
        const cardId = getCardIdFromContent(newContent.id, contentAction.contentType);
        this.setState({focusedCard: {cardId, isNew: true}, wasCardFocused: false});

        //TODO shayan find better fix, when we add new content, if we try to
        //scroll & focus right away the grid-layout hasn't yet been rendered with new content
        setTimeout(() => {
          this.highlightAndScrollToFocusedCard();
        }, 100);
      }
    }

    if (actionType in CONTENT_ACTION_TRACKING_NAME_MAP) {
      const trackingEventName = CONTENT_ACTION_TRACKING_NAME_MAP[actionType];
      if (trackingEventName) {
        this.trackContentAction({
          eventName: trackingEventName,
          contentType,
          contentId,
          oldContent: content,
          actionType,
          extraParams: {
            'Content Type': contentType,
            ...(contentType === ContentType.Media ? {'Media Type': content.media_type} : {}),
            ...(contentType === ContentType.Benchmark
              ? {'Benchmark Type': content.benchmark_type, 'Benchmark Industry': content.industry}
              : {}),
          },
        });
      }
    }

    return contentActionFailed;
  };

  getGridLayout = () => {
    const {layout} = this.dashboardController.getModel();
    return layout;
  };

  isLayoutBeingUpdated = () => !!this.state.gridLayoutUpdateParams;
  getProjectName = () => this.getCurrentProject()?.name;
  handleGridInsert = (elm: GridLayout) => (this.gridEl = elm);

  clearEditorCardState = () =>
    this.setState({
      editorCardId: null,
      editorCardEditedBookmark: null,
      editorCardBookmarkURLSlug: null,
      editorCardAiCellId: null,
    });

  handleReportEditorBackToBoard = ({detail}: BackToBoardEvent) => {
    this.stopEditingReport({
      source: {
        [BackToBoardSource.BackButton]: `Back button`,
        [BackToBoardSource.EscapeKey]: `Escape key`,
      }[detail.source],
    });
  };

  stopEditingReport = (extraTrackingParams: {source: string}) => {
    this.setState({
      focusedCard: null,
      wasCardFocused: false,
    });
    this.clearEditorCardState();
    if (!this.props.selectorController.getIsSavingChanges()) {
      this.setState({gridLayoutUpdateParams: null});
    }
    this.highlightAndScrollToFocusedCard();
    this.updateUrlFromState(
      {
        focusedCard: null,
        editorCardId: null,
        editorCardEditedBookmark: null,
        editorCardBookmarkURLSlug: null,
      },
      {replace: false},
    );

    this.props.context.track(TrackingEvent.CloseReport, {
      ...getCommonDashboardTrackingProps({
        dashboard: this.state.dashboard,
      }),
      ...extraTrackingParams,
    });
  };

  isLoadingEditorBookmark = () => {
    return Boolean(this.state.editorCardId && !isStagedCardId(this.state.editorCardId) && !this.state.dashboardLoaded);
  };

  getEditorCardOriginalBookmark = (): Bookmark | null => {
    const card = this.state.cards.get(this.state.editorCardId) as DashboardCard;
    return card?.bookmark;
  };

  getEditorCard = ({
    editorCardId = this.state.editorCardId,
  }: {editorCardId?: DashboardCardId} = {}): DashboardCard | null => {
    return (this.state.cards.get(editorCardId) as DashboardCard) ?? null;
  };

  getCurrentEditorCardType = (): BookmarkType => {
    return (
      this.getBookmarkTypeFromCardId() ||
      this.state.editorCardEditedBookmark?.type ||
      this.getEditorCard()?.bookmark.type
    );
  };

  showFastEditReport = () => {
    const {editorCardId, editorCardBookmarkURLSlug} = this.state;
    return Boolean(editorCardId || editorCardBookmarkURLSlug) && !this.state.browseParams?.isBrowseMode;
  };

  getBookmarkTypeFromCardId = () => {
    const {editorCardId} = this.state;

    if (!editorCardId) {
      return null;
    }

    if (Number.isInteger(editorCardId) || isStagedCardId(editorCardId) === false) {
      return null;
    }

    const type = findKeyByValue(BookmarkType, editorCardId.toString().substring(STAGED_CARD_PREFIX.length));
    return BookmarkType[type] ?? null;
  };

  getEditorBookmark = () => {
    if (
      // TODO(mack): This can be simplified to isStagedCardId(this.state.editorCardId) after cleaner urls is GA
      !this.state.editorCardEditedBookmark &&
      this.getBookmarkTypeFromCardId() &&
      !this.state.editorCardBookmarkURLSlug
    ) {
      const currentProject = this.getCurrentProject();
      if (!currentProject) {
        return null;
      }

      const type = this.getBookmarkTypeFromCardId();
      const reportManager = this.getReportManagerFromType(type);
      const stagedBookmark = createStagedBookmark(currentProject, reportManager);
      const overriddenParams = getBookmarkParamsWithOverrides({
        bookmarkParams: stagedBookmark.params,
        bookmarkType: stagedBookmark.type,
        chartTypeOverride: null,
        timeFilterOverride: this.state.timeFilter,
        additionalFilters: getActiveBoardFilters(this.state.filters),
        additionalBreakdowns: this.getBreakdowns(),
      });

      return {...stagedBookmark, params: overriddenParams};
    }

    return this.state.editorCardEditedBookmark;
  };

  handleReportEditorDelete = async (ev: DeleteEvent) => {
    if (await confirmDeleteCard()) {
      const cardId = ev.detail.dashboardCardId;
      this.clearEditorCardState();
      this.updateUrlFromState(
        {
          editorCardBookmarkURLSlug: null,
          editorCardId: null,
        },
        {replace: false},
      );
      const card = this.state.cards.get(cardId) as DashboardCard;
      const dashboard = await dashboardsClient.deleteDashboardContent({
        gridLayout: this.getGridLayout(),
        content: {contentType: ContentType.Report, contentId: card.bookmark.id},
        dashboardId: this.state.dashboard.id,
        projectId: this.props.context.projectId,
        workspaceId: this.props.context.workspaceId,
      });
      this.dashboardController.updateModel(dashboard);
    }
  };

  handleReportEditorUpdate = (ev: UpdateEvent) => {
    const {bookmark, bookmarkURLSlug, isReportSwitch, savedBehaviorId} = ev.detail;

    this.setState(
      (prevState) => {
        const stateUpdate: Partial<State> = {
          reportEditorSession: this.state.reportEditorSession,
          editorCardEditedBookmark: bookmark,
          editorCardBookmarkURLSlug: bookmarkURLSlug,
        };

        if (isReportSwitch) {
          stateUpdate.reportEditorSession += 1;
        }

        if (bookmarkURLSlug) {
          stateUpdate.editorCardId = null;
        } else if (isReportSwitch) {
          stateUpdate.editorCardId = generateStagedCardId(bookmark.type);
        }
        return {
          ...prevState,
          ...stateUpdate,
        };
      },
      () => {
        /**
         * @todo(metrics) why is this being done only for behaviors and not other saved entities like cohorts, custom events, formulas, etc..?
         * A better implementation of this would be to have a context build a dependency set of saved entities and subscribe to changes on those resources
         * to trigger a card reload
         */
        if (savedBehaviorId) {
          const cardsToUpdate = this.state.dashboard.cards.filter(
            (card) =>
              isDashboardCard(card) &&
              isInsightsBookmarkParams(card.bookmark.params) &&
              includesSavedBehaviorId({
                params: card.bookmark.params,
                behaviorId: savedBehaviorId,
                metricsResource: this.props.metrics,
                behaviorsResource: this.props.behaviors,
              }),
          );
          this.clearCards(cardsToUpdate);
          for (const card of cardsToUpdate) {
            this.loadCard(card);
          }
        }
        this.updateUrlFromState();
      },
    );
  };

  saveNewReportToDifferentBoard = async (
    detail: SaveEventDetails,
    editedBookmark: DisplayBookmark<any>,
    params: string,
  ) => {
    this.setState({isSavingEditorBookmark: true});
    const bookmark = await dashboardsClient.addNewDashboardBookmark({
      dashboardOrigin: detail.originDashboard,
      dashboardId: detail.dashboard.id,
      bookmarkData: {
        type: editedBookmark.type,
        name: editedBookmark.name,
        params,
        dashboard_id: detail.dashboard.id,
      },
      projectId: this.props.context.projectId,
      workspaceId: this.props.context.workspaceId,
    });

    const editorCardId = getCardIdFromContent(bookmark.id, ContentType.Report);
    this.setState(
      {
        dashboardFailed: false,
        dashboardLoaded: false,
        isSavingEditorBookmark: false,
        routedDashboardId: detail.dashboard.id,
        editorCardId,
        editorCardBookmarkURLSlug: null,
      },
      async () => {
        await this.props.selectorController.changeActiveDashboard(detail.dashboard.id);
        this.props.selectorController.setBookmarkID(bookmark.id);

        this.updateUrlFromState();
        // we are about to navigate, so defer opening the title editor
        this.props.reportEditorActions.openTitleEditor(true);
      },
    );
  };

  handleReportEditorMove = async (ev: CustomEvent) => {
    const contentActionFailed = await this.handleContentAction({
      contentAction: ev.detail,
      gridLayoutUpdateParams: null,
      provideUndoAction: false,
    });

    if (!contentActionFailed) {
      const editorCardId = getCardIdFromContent(ev.detail.contentId, ContentType.Report);
      this.setState(
        {
          dashboardFailed: false,
          dashboardLoaded: false,
          routedDashboardId: ev.detail.contentParams.target_dashboard_id,
          editorCardId,
        },
        async () => {
          await this.props.selectorController.changeActiveDashboard(ev.detail.contentParams.target_dashboard_id);
          this.props.selectorController.setBookmarkID(ev.detail.contentId);

          const targetDashboard = await dashboardsClient.querySingle(
            {
              projectId: this.props.context.projectId,
              workspaceId: this.props.context.workspaceId,
              dashboardID: ev.detail.contentParams.target_dashboard_id,
            },
            {clearCache: true},
          ).network;

          this.setState({dashboard: targetDashboard});

          this.saveChangesOnDashboard({dashboardMetadata: {title: targetDashboard.title}});

          this.updateUrlFromState();
          this.updateSidenavSections(targetDashboard);
        },
      );
    }
  };

  handleReportEditorSave = (ev: SaveEvent) => {
    this.saveReport(ev.detail);
  };

  saveReport = async (
    details: SaveEventDetails,
    {requery = true, disableHistory = false}: {requery?: boolean; disableHistory?: boolean} = {},
  ) => {
    const {
      queryPromise,
      dashboardCardId,
      updateBookmarkUrlOnFasteditSave,
      saveAsNew = false,
      dashboard = this.state.dashboard,
    } = details;

    const editedBookmark = details.hydratedBookmark ?? details.editedBookmark;

    const editedBookmarkParams = JSON.stringify(
      beforeSavingBookmark({params: editedBookmark.params, type: editedBookmark.type}),
    );

    if (saveAsNew && dashboard.id !== this.state.dashboard.id) {
      return this.saveNewReportToDifferentBoard(details, editedBookmark, editedBookmarkParams);
    }

    const editorCard = dashboardCardId ? this.getEditorCard({editorCardId: dashboardCardId}) : null;

    let updatedEditorCard: DashboardCard;
    let cardOrder = this.state.cardOrder;

    const initialSession = this.state.reportEditorSession;

    if (this.state.editorCardAiCellId) {
      this.swapSparkCardWithReport(this.state.editorCardAiCellId, editedBookmark);

      // resetting report editor save state
      updateBookmarkUrlOnFasteditSave({
        originalBookmark: editedBookmark as Bookmark,
        dashboardCardId: null,
      });

      return;
    }

    let cardsToUpdate: DashboardCard[];
    if (saveAsNew) {
      // create a staged card and bookmark so that we can show the card
      // immediately without waiting for the API response
      updatedEditorCard = createStagedCard(editedBookmark.type, {...NULL_BOOKMARK, ...editedBookmark});
      // new cards are appended to the dashboard
      cardOrder = [...cardOrder, updatedEditorCard.id];
      cardsToUpdate = [updatedEditorCard];
    } else {
      updatedEditorCard = {...editorCard, bookmark: {...editorCard.bookmark, ...editedBookmark}};
      // if we're updating the bookmark, need to update any report-linked cards on the dashboard
      cardsToUpdate = this.getCardsWithBookmarkId(updatedEditorCard.bookmark.id).map((card) => ({
        ...card,
        bookmark: updatedEditorCard.bookmark,
      }));
    }

    const updatedBookmarkChartType = getChartOptions({
      bookmark: editedBookmark,
      chartOptionsOverride: null,
      metricsResource: this.props.metrics,
      behaviorsResource: this.props.behaviors,
      currentProject: this.getCurrentProject(),
    }).type;
    this.setState({isSavingEditorBookmark: true});
    for (const card of cardsToUpdate) {
      this.state.cards.set(card.id, card);

      if (requery) {
        // TODO go/j/AE-909: replace state mutation with immutable state updates.
        this.state.foregroundCardResults.delete(card.id);
        this.backgroundCardResults.delete(card.id);

        // if a card has a chart type override different than the one on the edited bookmark,
        // we need to reload to ensure the query/data is correct.
        const isCompatibleChartType =
          updatedBookmarkChartType ===
          getChartOptions({
            bookmark: card.bookmark,
            chartOptionsOverride: card.chart_options,
            metricsResource: this.props.metrics,
            behaviorsResource: this.props.behaviors,
            currentProject: this.getCurrentProject(),
          }).type;
        if (!queryPromise || !isCompatibleChartType) {
          this.loadCard(card);
          continue;
        }

        let cardResult = getCardResultIfInvalid(card, this.state.filters);
        if (!cardResult) {
          // TODO go/j/AE-909: replace state mutation with immutable state updates.
          this.state.foregroundQueryResults.set(card.id, queryPromise);
          try {
            const queryResult = await queryPromise;
            cardResult = createCardResults({
              card,
              results: queryResult as BaseQueryResultsType,
              queryResponseTimeMs: 0,
            });

            const reportCard = card as DashboardCard | BenchmarkCard;
            const reportName = this.getReportName(reportCard?.bookmark?.type);
            const trackingParams = {
              ...getBookmarkTrackingParams(reportCard.bookmark, reportCard.bookmark_metadata),
              ...getCardTrackingParams({
                card,
                metricsResource: this.props.metrics,
                behaviorsResource: this.props.behaviors,
                currentProject: this.getCurrentProject(),
              }),
              ...getCommonDashboardTrackingProps({
                dashboard: this.state.dashboard,
                cards: this.getAvailableCards(),
              }),
              latency: cardResult?.queryResponseTimeMs,
              status_code: 200,
              tab: reportName,
              isBenchmark: isBenchmarkCard(card),
            };
            this.emitTrackEvent({
              eventName: TrackingEvent.ReportQuery,
              eventPrefix: WEB_DASH_PREFIX,
              props: trackingParams,
            });

            // TODO go/j/AE-909: replace state mutation with immutable state updates.
            this.state.foregroundCardResults.set(card.id, cardResult);
          } catch (error) {
            // TODO: Consolidate error states as returned from apiClient
            this.handleCardQueryError(card, error);
          }
        } else {
          // TODO go/j/AE-909: replace state mutation with immutable state updates.
          this.state.foregroundCardResults.set(card.id, cardResult);
        }
      }
    }

    this.setState({cardOrder});

    const {savedReportCardId, updatedDashboard} = await this.saveDashboardReport({
      disableHistory,
      bookmarkData: {
        name: editedBookmark.name,
        description: editedBookmark.description,
        params: editedBookmarkParams,
      },
      // provides the initial card data for undo/redo history
      originalBookmarkData: editorCard
        ? {
            name: editorCard.bookmark.name,
            params: JSON.stringify(editorCard.bookmark.params),
          }
        : null,
      bookmarkType: editedBookmark.type,
      bookmarkId: editorCard?.bookmark?.id,
      cardId: updatedEditorCard.id,
      saveAsNew,
    });
    this.setState({isSavingEditorBookmark: false});

    if (saveAsNew) {
      this.addCardToRecentsSearchList(savedReportCardId);

      // TODO go/j/AE-909: replace state mutation with immutable state updates.
      this.state.foregroundQueryResults.set(
        savedReportCardId,
        this.state.foregroundQueryResults.get(updatedEditorCard.id),
      );
      this.state.foregroundQueryResults.delete(updatedEditorCard.id);

      // TODO go/j/AE-909: replace state mutation with immutable state updates.
      this.state.foregroundCardResults.set(
        savedReportCardId,
        this.state.foregroundCardResults.get(updatedEditorCard.id),
      );
      this.state.foregroundCardResults.delete(updatedEditorCard.id);
    }

    this.dashboardController.updateModel(updatedDashboard);

    // we grab the latest card data from the save response
    // because the 'cards' state is not updated to reflect it yet
    const savedReportCard: DashboardCard = updatedDashboard.cards.find((card) => card.id === savedReportCardId);

    /**
     * If we created a card + bookmark, we should update the editor with the card from API.
     */
    if (this.showFastEditReport() && initialSession === this.state.reportEditorSession) {
      this.updateReportEditorAfterSave(savedReportCardId, savedReportCard, updateBookmarkUrlOnFasteditSave);
      if (saveAsNew && editorCard) {
        this.props.reportEditorActions.openTitleEditor();
      }
    }
  };

  leaveRHSModeConfirmation = async (): Promise<boolean> => {
    const {t, print} = this.getLocalizationStore();

    //TODO(RHS): remove once live
    if (
      this.state.dashboard?.generation_type !== DashboardType.StagedTemplate || // on draft template
      this.state.browseParams?.isBrowseMode // on Discover page
    ) {
      this.setState({showTemplateRHS: false}); // clear out RHS in case of weird state
      return true;
    }

    const confirmation = await confirm({
      title: t(`boards.confirmation.rhs.title`),
      description: t(`boards.confirmation.rhs.description`, {
        templateType: print(TemplateSpecsTranslationKey[this.state.dashboard?.template_type].title),
      }),
      theme: ConfirmTheme.DELETE,
      'confirm-button-label': t(`boards.confirmation.rhs.confirm-btn-label`),
      'cancel-button-label': t(`boards.confirmation.rhs.cancel-btn-label`),
      'description-allow-newline': true,
    });

    this.props.context.track(TrackingEvent.ViewedDiscardPrompt, {
      dashboard_id: this.state.dashboard.id,
      template_type: this.state.dashboard.template_type,
    });

    if (confirmation) {
      await this.deleteDashboard(this.state.dashboard, DashboardCRUDSource.RHS, true);
      this.setState({showTemplateRHS: false});
      this.props.context.track(TrackingEvent.DiscardBlueprint, {
        dashboard_id: this.state.dashboard.id,
        template_type: this.state.dashboard.template_type,
      });
    }
    return confirmation;
  };

  handleHeaderFavoriteDashboard = async () => {
    this.setState({isFavoriteRequestLoading: true});
    await this.favoriteDashboard(this.state.dashboard, DashboardCRUDSource.Header);
    this.setState({isFavoriteRequestLoading: false});
  };

  buildQueryReportHeaderParams = (): QueryReportHeaderParams => {
    const {tvModeEnabled, dashboard, dashboardModified, lastRefreshed} = this.state;
    const isActiveDashboardPinned = this.state.dashboardLoaded && Boolean(dashboard?.pinned_date);
    const isPinDashboardsButtonEnabled = this.props.context.permissions.has(Permission.PinDashboards);
    const canManageEmailDigests = this.props.context.permissions.has(Permission.AccessDashboardEmailDigests);
    const isKpiTemplateDashboard = dashboard?.generation_type === `Template Generated`;
    const isTemplateDraftDashboard = dashboard?.generation_type === DashboardType.StagedTemplate;
    const {t} = this.getLocalizationStore();

    let moveItem;

    if (this.canEditActiveDashboard() && !isTemplateDraftDashboard) {
      const sharedMoveToParams = {
        label: t(`boards.actions.move`),
        icon: `share`,
        name: CustomHeaderAction.Move,
        direction: Directions.Left,
      };

      if (!this.isNestedDashboardsEnabled() && this.isCrossProjectBoardMovesEnabled()) {
        moveItem = getProjectSelectorSectionItem({
          ...sharedMoveToParams,
          placeholder: t(`boards.actions.project-placeholder`),
          quickCreateSaveDashboardCopy: t(`boards.actions.create-new-project`),
        });
      } else if (this.isNestedDashboardsEnabled()) {
        moveItem = getBoardsDashboardSectionItem({
          ...sharedMoveToParams,
          placeholder: t(`boards.actions.board-placeholder`),
          quickCreateSaveDashboardCopy: t(`boards.actions.create-new-board`),
          dashboardController: this.props.selectorController,
          recentDashboardIds: [],
          showProjectSelector: this.isCrossProjectBoardMovesEnabled(),
          showMoveOutButton: this.isNestedDashboardsEnabled() && dashboard?.parent_dashboard_id !== null,
          hideDashboardList: !this.isNestedDashboardsEnabled(),
          excludedDashboards: [
            dashboard?.id,
            ...(dashboard?.parent_dashboard_id !== null ? [dashboard?.parent_dashboard_id] : []),
          ],
          currentDashboardHierarchyTreeHeight: getDashboardHierarchyTreeHeight(dashboard),
          descendantDashboardIds: getDashboardDescendants(dashboard),
          enableNestedDashboardsRestrictions: true,
        });
      }
    }

    const overflowItems = this.isMinimalMode()
      ? []
      : [
          {
            items: [
              ...(moveItem ? [moveItem] : []),
              ...(isPinDashboardsButtonEnabled
                ? [
                    {
                      label: isActiveDashboardPinned ? t(`boards.actions.unpin`) : t(`boards.actions.pin`),
                      value: CustomHeaderAction.PinDashboard,
                      icon: isActiveDashboardPinned ? `filled-pin` : `pin`,
                      highlightIcon: isActiveDashboardPinned,
                    },
                  ]
                : []),
              ...(canManageEmailDigests && dashboard?.id
                ? [
                    {
                      label: t(`boards.actions.export`),
                      icon: `export`,
                      subSections: [
                        {
                          items: [
                            {label: t(`boards.actions.to-email`), value: CustomHeaderAction.ExportEmail},
                            {label: t(`boards.actions.to-slack`), value: CustomHeaderAction.ExportSlack},
                          ],
                        },
                      ],
                    },
                  ]
                : []),
              {
                label: t(`boards.actions.tv-mode-enter`),
                value: CustomHeaderAction.TV,
                icon: `tv-mode`,
              },
              {
                label: t(`boards.actions.manage`),
                href: `${this.props.location.pathname}/subscriptions`,
                icon: `settings`,
              },
            ],
          },
        ];

    // Only use custom controls for tv mode.
    const controlItems = tvModeEnabled ? [{slot: `tvRefresh`}, {slot: `tvExit`}] : [{slot: `favoriteButton`}];
    const hideActions = this.isMinimalMode()
      ? Object.values(HeaderAction)
      : [HeaderAction.SaveNew, HeaderAction.Reset, HeaderAction.Load];

    return {
      ...buildQueryReportHeaderParams({
        projectId: this.props.context.projectId,
        entity: normalizeDashboardEntity(dashboard),
        invalidEntity: !dashboard?.id,
        invalidMessage: `Create a new board to get started`,
        displayContext: SearchDisplayContext.Dashboard,
      }),
      entityType: EntityType.Dashboard,
      trackName: `dashboards`,
      workspaceId: this.props.context.workspaceId,
      userId: this.props.context.userId,
      overflowControlItems: overflowItems,
      hasUnsavedChanges: dashboardModified,
      hideActions,
      controlItems,
      ...(tvModeEnabled ? {computedAt: lastRefreshed.toISOString()} : {}),
      autoRefreshIntervalMs: tvModeEnabled ? TV_MODE_RELOAD_TIME_IN_MS : undefined,
      isPublicDashboard: Boolean(this.state.dashboard) && this.state.dashboard.num_active_public_links > 0,
      isTemplateDashboard: isKpiTemplateDashboard,
      isTemplateDraftDashboard,
      ...(this.state.isEmbedMode ? {canWrite: false, canSave: false} : {}), // disallow editing title
      disableHeaderMargin: this.isMinimalMode(),
      emptyEntityName: t(UntitledReportNameKey),
      dashboardsRoute: getRouteToCurrentDashboard({dashboard: this.state.dashboard}),
      headerActionItemsEl: QueryReportHeaderActionItems({
        dashboardLoaded: this.state.dashboardLoaded,
        isEmbedMode: this.state.isEmbedMode,
        isActiveDashboardFavorited: this.state.dashboardLoaded && this.state.dashboard.is_favorited,
        isFavoriteRequestLoading: this.state.isFavoriteRequestLoading,
        onHeaderFavoriteDashboard: this.handleHeaderFavoriteDashboard,
        onRefresh: this.handleRefreshClicked,
        onExitTvMode: this.handleClickExitTVMode,
        i18n: this.getLocalizationStore(),
      }),
      boardsTitleBadgeEl: QueryReportHeaderTitle({
        isSavingChanges: this.props.selectorController.getIsSavingChanges(),
        canEditActiveDashboard: this.canEditActiveDashboard(),
        i18n: this.getLocalizationStore(),
      }),
    };
  };

  getExtraTrackingParams = (): {[name: string]: Obj} => {
    const cards = this.getAvailableCards();
    return {
      ...getCardTypes(cards),
      'text card styles': getTextCardStyles(cards),
    };
  };

  getEditorBookmarkOverrides = ({
    editorCardId = this.state.editorCardId,
  }: {editorCardId?: DashboardCardId} = {}): DashboardBookmarkOverrides => {
    const editorCard = this.getEditorCard({editorCardId});

    if (
      !editorCard?.chart_options &&
      !this.state.filters.length &&
      !this.state.breakdowns.length &&
      isEmpty(this.state.timeFilter)
    ) {
      return null;
    }

    const chartTypeOverride = editorCard
      ? getChartOptions({
          bookmark: editorCard.bookmark,
          chartOptionsOverride: editorCard.chart_options,
          metricsResource: this.props.metrics,
          behaviorsResource: this.props.behaviors,
          currentProject: this.getCurrentProject(),
        }).type
      : undefined;

    return {
      dateRangeOverride: this.state.timeFilter?.dateRange,
      additionalBreakdowns: this.state.breakdowns,
      additionalFilters: getActiveBoardFilters(this.state.filters),
      chartTypeOverride,
    };
  };

  getDashboardParams = (dashboard, urlEncode: boolean): DashboardOrigin | null => {
    const params = getDashboardParams(dashboard, {urlEncode});
    return isEmpty(params) ? null : (params as DashboardOrigin);
  };

  handleGridTrackEvent = (ev: CustomEvent<GridLayoutTrackEventDetail>) => {
    const {eventName, trackProps, calcTrackPropsParams} = ev.detail;

    let calculatedProps = {};
    if (calcTrackPropsParams && GridCalcTrackingPropsParams.SingleContent in calcTrackPropsParams) {
      const {contentId, contentType} = calcTrackPropsParams[GridCalcTrackingPropsParams.SingleContent];
      calculatedProps[`Content Id`] = contentId;
      calculatedProps[`Content Type`] = contentType;
      calculatedProps[`Content Sub Type`] = this.getTrackingContentSubType({contentType, contentId});
      calculatedProps = {...calculatedProps, ...this.getTrackingBookmarkParams({contentType, contentId})};
    }

    if (calcTrackPropsParams && GridCalcTrackingPropsParams.Contents in calcTrackPropsParams) {
      const contents = calcTrackPropsParams[GridCalcTrackingPropsParams.Contents];
      calculatedProps[`Content Ids`] = contents.map(({contentId}) => contentId);
      calculatedProps[`Content Types`] = contents.map(({contentType}) => contentType);
      calculatedProps[`Content Sub Types`] = contents.map((content) => {
        return this.getTrackingContentSubType({contentType: content.contentType, contentId: content.contentId});
      });
      const trackedBookmarkParams = contents.map((content) =>
        this.getTrackingBookmarkParams({contentType: content.contentType, contentId: content.contentId}),
      );
      calculatedProps[`Chart Types`] = trackedBookmarkParams.map((params) => params[`Chart Type`]);
      calculatedProps[`# of breakdowns`] = trackedBookmarkParams.map((params) => params[`# of breakdowns`]);
      calculatedProps[`# of metrics`] = trackedBookmarkParams.map((params) => params[`# of metrics`]);
    }

    this.props.context.track(eventName, {
      ...getCommonDashboardTrackingProps({
        dashboard: this.state.dashboard,
        cards: this.getAvailableCards(),
      }),
      ...(trackProps || {}),
      ...calculatedProps,
    });
  };

  isDashboardCreator = () => {
    return this.state.dashboard.creator_id === this.props.context.userId;
  };

  private updateReportEditorAfterSave(
    savedReportCardId: any,
    savedReportCard: DashboardCard,
    updateBookmarkUrlOnFasteditSave: ({
      originalBookmark,
      dashboardCardId,
    }: {
      originalBookmark: Bookmark;
      dashboardCardId: DashboardCardId;
    }) => string,
  ) {
    // if the modal was closed before the request completed, make sure it doesn't reopen.
    // if there was a report switch between in between this report save action, don't persist
    // editorCardId when it no longer applies to the active report editor session
    const bookmarkOverrides = this.getEditorBookmarkOverrides({editorCardId: savedReportCardId});
    if (!bookmarkOverrides) {
      this.setState({
        editorCardId: savedReportCardId,
        editorCardEditedBookmark: null,
        editorCardBookmarkURLSlug: null,
        editorCardAiCellId: null,
      });
      this.updateUrlFromState({
        editorCardId: savedReportCardId,
        editorCardEditedBookmark: null,
        editorCardBookmarkURLSlug: null,
      });
    } else {
      // When saving a new report, need to create a new bookmark-url slug that includes the new report's id
      // When updating existing report, a new bookmark-url is not strictly necessary. However, calling
      // `updateBookmarkUrlOnFasteditSave` is needed to update originalBookmark on report-editor.
      const bookmarkUrlSlug = updateBookmarkUrlOnFasteditSave({
        dashboardCardId: savedReportCardId,
        originalBookmark: savedReportCard.bookmark,
      });
      this.setState({
        editorCardId: null,
        editorCardEditedBookmark: null,
        editorCardBookmarkURLSlug: bookmarkUrlSlug,
        editorCardAiCellId: null,
      });
      this.updateUrlFromState({
        editorCardId: null,
        editorCardEditedBookmark: null,
        editorCardBookmarkURLSlug: bookmarkUrlSlug,
      });
    }
  }

  isActiveDashboard(dashboard: DashboardItemInfo) {
    return this.state.dashboard?.id === dashboard.id;
  }

  render() {
    return template.apply(this);
  }

  createBlueprintBoard = async (templateData?: CustomEvent): Promise<void> => {
    const {selectedTemplate} = templateData.detail;
    this.setState({templateModalLoading: true});

    const result = await this.initializeBlueprintBoard({template_type: selectedTemplate});
    const blueprintDashboard = result.dashboard;
    setCardsFromDashboardLayout(blueprintDashboard);
    this.setBlueprintDashboard(blueprintDashboard);
    this.setState({
      hasNoDashboards: false,
      dashboardModified: false,
      templateModalLoading: false,
      showTemplateRHS: true,
      blueprintConfig: result.blueprint_config,
      wasBlueprintUpdated: false,
    });

    if (`error` in result) {
      addToast({message: `Something went wrong, please refresh the page and try again.`, type: ToastType.ERROR});
    } else {
      this.props.context.track(TrackingEvent.CreatedDashboardFromTemplate, {
        'Is Foundational Template': true,
      });
      this.handleCancelTemplateModal();
    }
  };

  shouldDisableGridRowClick = (): boolean => {
    return this.state.showTemplateRHS;
  };

  shouldLowerBoardOpacity = (): boolean => {
    return (
      this.state.dashboard?.generation_type === DashboardType.StagedTemplate &&
      !this.state.browseParams?.isBrowseMode &&
      !this.state.wasBlueprintUpdated
    );
  };

  commitBlueprint = async (_ev: CustomEvent) => {
    this.setState({isCommittingBlueprint: true});
    const cards = [];
    this.state.cards.forEach((card: DashboardCard) => {
      if (card.type === ContentType.Report) {
        cards.push(card.bookmark);
      }
      if (card.type === ContentType.Benchmark) {
        cards.push(card);
      }
      if (card.type === ContentType.Text) {
        cards.push(card);
      }
    });
    const result = await this.props.selectorController.commitBlueprintDashboard({
      dashboardId: this.state.dashboard.id,
      cards,
    });
    this.setState({showTemplateRHS: false, isCommittingBlueprint: false, dashboard: result.dashboard});
    this.props.selectorController.changeActiveDashboard(result.dashboard.id);
  };

  updateBlueprint = async (ev: CustomEvent) => {
    let bpConfig = {...this.state.blueprintConfig};
    const blueprintName = bpConfig.variables[ev.detail.eventName];
    if (!blueprintName) {
      return;
    }

    let realName;
    const {inputType} = ev.detail;

    if (inputType === RHSInputType.Date) {
      realName = ev.detail.eventValue;
    } else if (inputType === RHSInputType.Event) {
      realName = ev.detail.eventValue.entries[0].event.value;
    } else if (inputType === RHSInputType.Property) {
      realName = (ev.detail.eventValue.event as Segmentation).propertyName;
    } else if (inputType === RHSInputType.Text) {
      realName = ev.detail.eventValue;
    } else {
      realName = ev.detail.eventValue.value;
    }

    if (!bpConfig.eventNameMap) {
      bpConfig.eventNameMap = {};
    }
    if (!bpConfig.scdPropEventMap) {
      bpConfig.scdPropEventMap = {};
    }

    bpConfig.eventNameMap[blueprintName] = realName;
    if (isScdPropertyName(realName)) {
      bpConfig.scdPropEventMap[realName] = (ev.detail.eventValue.event as Segmentation).scdEvent;
    }

    if (!this.state.wasBlueprintUpdated) {
      this.setState({wasBlueprintUpdated: true});
    }
    bpConfig = updateBlueprintCohorts(bpConfig, blueprintName, realName, inputType);
    await this.props.selectorController.saveUpdatedBlueprintCohorts(bpConfig.cohorts);

    const updatedCardsById = this.getBlueprintCardsWithRealNames({
      eventNameMap: bpConfig.eventNameMap,
      inputType,
      scdPropEventMap: bpConfig.scdPropEventMap,
    });
    // update state cards
    this.state.cards.forEach((card) => {
      Object.assign(card, updatedCardsById[card.id]);
    });

    this.setState({
      blueprintConfig: bpConfig,
    });
    this.onRefresh(false);

    this.props.context.track(TrackingEvent.UpdateBlueprint, {
      dashboard_id: this.state.dashboard.id,
      template_type: this.state.dashboard.template_type,
    });
  };

  initializeBlueprintConfig = async () => {
    const blueprintConfig = await dashboardsClient.getBlueprintConfig({
      dashboardID: this.state.dashboard.id,
      projectId: this.props.context.projectId,
      workspaceId: this.props.context.workspaceId,
    });

    this.setBlueprintDashboard(this.state.dashboard);
    this.setState({
      blueprintConfig,
      wasBlueprintUpdated: false,
    });
  };

  getBlueprintCardsWithRealNames = ({
    eventNameMap,
    scdPropEventMap,
    inputType,
  }: {
    eventNameMap: {[blueprintName: string]: string};
    scdPropEventMap: {[scdPropName: string]: string};
    inputType: RHSInputType;
  }): {[id: DashboardCardId]: CardOnDashboard} => {
    const cards = cloneDeep(this.state.blueprintDashboard.cards);
    for (const card of cards) {
      // TODO: a very straighforward optimization is to not use this for, and instead pass eventNameMap inside of swapBlueprintVariable. That way, we review the bookmark params only once
      for (const [blueprintName, realName] of Object.entries(eventNameMap)) {
        if (card.type === ContentType.Report) {
          const dashboardCard = card as DashboardCard;
          inputType !== RHSInputType.Date
            ? swapBlueprintVariable(dashboardCard.bookmark.params, blueprintName, realName, scdPropEventMap)
            : swapFeatureLaunchDate(dashboardCard, blueprintName, realName);
          dashboardCard.bookmark.name = dashboardCard.bookmark.name.replace(blueprintName, realName);
        } else if (card.type === ContentType.Text) {
          const textCard = card as TextCard;
          textCard.markdown = textCard.markdown.replace(blueprintName, realName); // for text cards that mention blueprint inputs
        } else if (card.type === ContentType.Benchmark) {
          const benchmarkCard = card as BenchmarkCard;
          const swapBase = inputType === RHSInputType.Event ? benchmarkCard.bookmark.params : card;
          swapBlueprintVariable(swapBase, blueprintName, realName, scdPropEventMap);
        }
      }
    }
    return Object.fromEntries(cards.map((card) => [card.id, card]));
  };

  handleClickSeeAllTemplates = async (ev) => {
    this.setState({isSeeAllTemplatesLoading: true});
    if (Object.keys(ev.detail.fields).length > 0) {
      // user has set fields, prompt
      const leaveRHSConfirm = await this.leaveRHSModeConfirmation();

      if (leaveRHSConfirm) {
        this.setState({openCreateFoundationalTemplateModal: true});
        this.props.context.track(TrackingEvent.WentBackToAllTemplates, {
          dashboard_id: this.state.dashboard.id,
          template_type: this.state.dashboard.template_type,
          'fields set': true,
        });
      }
    } else {
      this.setState({wasBlueprintUpdated: true}); // prevent flicker

      await this.deleteDashboard(this.state.dashboard, DashboardCRUDSource.RHS, true);
      this.setState({showTemplateRHS: false, openCreateFoundationalTemplateModal: true});
      this.props.context.track(TrackingEvent.WentBackToAllTemplates, {
        dashboard_id: this.state.dashboard.id,
        template_type: this.state.dashboard.template_type,
        'fields set': false,
      });
    }
    this.setState({isSeeAllTemplatesLoading: false});
  };

  toggleRHSReminderTooltip = () => {
    if (this.state.showTemplateRHS) {
      // only show the tooltip when RHS showing
      this.setState({showRHSReminderTooltip: !this.state.showRHSReminderTooltip});
    }
  };

  hideRHSReminderTooltip = () => {
    this.setState({showRHSReminderTooltip: false});
  };

  preventDefaultNavigate = () => {
    return this.state.showTemplateRHS;
  };

  async saveBoardLegendOverrides() {
    const legendOverrides = this.props.themes.legendOverrides;
    const bookmarks = [];
    for (const card of this.state.cards.values()) {
      if (`bookmark` in card === false) {
        continue;
      }

      const params: InsightsBookmarkParams = card.bookmark.params;
      params.legend ||= {};
      params.legend.overrides = mergeLegendOverrides(params.legend.overrides ?? {}, legendOverrides);

      bookmarks.push({
        id: card.bookmark.id,
        params,
      });

      // all of Dashboards APIs assume we can only make 1 edit at a time
      // when the bulk update request is done, bookmark params loaded in the UI
      // do not match the updated bookmark params, we prevent a reload of cards
      // by assigning the params directly here
      this.state.cards.set(card.id, {
        ...card,
        bookmark: {
          ...card.bookmark,
          params,
        },
      });
    }

    await bulkUpdateBookmarks(bookmarks);
    this.props.themes.legendOverrides = {};
    this.forceUpdate();
  }

  async setResultsInDashboardCallback(
    dashboardCardId: DashboardCardId,
    results: BaseQueryResultsType,
    useQueryApiCache: boolean = true,
  ) {
    const card = this.state.cards.get(dashboardCardId) as DashboardCard;
    const currentWorkspaceCard = this.convertToCurrentWorkspaceCard(card);
    const timeFilterOverride = isBenchmarkCard(card) ? null : this.state.timeFilter;
    let cardResult = createCardResults({
      card: currentWorkspaceCard,
      results,
      queryResponseTimeMs: null,
    });

    let cardEventsInfo = null;
    if (dashboardCardId in this.state.foregroundCardResults) {
      cardEventsInfo = this.state.foregroundCardResults.get(dashboardCardId);
    } else {
      try {
        cardEventsInfo = await queryCanonicalBookmark({
          abortSignal: null,
          behaviorsResource: this.props.behaviors,
          card: currentWorkspaceCard,
          currentProject: this.getCurrentProject(),
          dashboard: this.state.dashboard,
          globalBreakdowns: this.getBreakdowns(),
          globalFilters: this.state.filters,
          location: this.props.location,
          metricsResource: this.props.metrics,
          queryMetadata: true,
          queryOrigin: DashboardQueryOrigin.Standard,
          timeFilterOverride,
          useQueryCache: useQueryApiCache,
        });
      } catch (err) {
        console.error(`Error when querying bookmark metadata for dashboard card in callback.`, err);
        return;
      }
    }
    cardResult = {...cardResult, cardEventsInfo};

    if (this.state.editorCardId) {
      // TODO go/j/AE-909: replace state mutation with truly immutable state update.
      // Tests currently flake without the state mutation due to other dependencies on combined w/ async patterns.
      const foregroundCardResults = new Map(this.state.foregroundCardResults.set(card.id, cardResult));
      this.setState({foregroundCardResults});
    }
  }

  handleBrowseSearchedTextUpdate = debounce((text: string) => {
    this.props.globalSearchController.setSearchedText(text);
  }, 250);
}

export type {StandardModeClass};

export const StandardMode = (props: Props) => {
  const themes = useThemes();
  const behaviors = useBehaviors();
  const bookmarkStore = useBookmark();
  const currentProject = useCurrentProject();
  const globalSearchController = useGlobalSearchController();
  const historyManager = useHistoryManager();
  const localStorageManager = useLocalStorageManager();
  const location = useLocation();
  const metrics = useMetrics();
  const i18n = useTranslation();
  const navigate = useNavigateWithSearchParams();
  const openInNewTab = useOpenInNewTab();
  const reportEditorActions = useReportEditorActions();
  const selectorController = useDashboardSelectorController();
  const sidenavContext = useDashboardSidenavContext();
  const reportLayoutManager = useReportLayoutManager();
  const queryContextManager = useQueryContextManager();
  const projects = useProjects();
  const spaNavManager = useSpaNavManager();

  return (
    <StandardModeClass
      themes={themes}
      behaviors={behaviors}
      bookmarkStore={bookmarkStore}
      currentProject={currentProject}
      globalSearchController={globalSearchController}
      historyManager={historyManager}
      localStorageManager={localStorageManager}
      location={location}
      metrics={metrics}
      navigate={navigate}
      openInNewTab={openInNewTab}
      reportEditorActions={reportEditorActions}
      selectorController={selectorController}
      sidenavContext={sidenavContext}
      i18n={i18n}
      reportLayoutManager={reportLayoutManager}
      queryContextManager={queryContextManager}
      projects={projects}
      spaNavManager={spaNavManager}
      {...props}
    />
  );
};
